import { create } from 'zustand';
import { toast } from 'sonner';
import type { Conversation, Message, MessageAttachment, Channel, Sender, MessageStatus, ConvChatLabel, ActiveFlowInfo, ConversationOptOut } from '@/types/messaging';
import { useModuleStore } from './module-store';
import { useAppStore } from './app-store';
import { useAuthStore, getEffectiveUid } from './auth-store';

import { useSettingsStore, MAX_PINNED_CHATS } from './settings-store';
import { getLabelPermissionStandalone, labelPermissionAllowsModify, getCanManageLabelsStandalone, getCanDeleteLabelsStandalone } from '@/hooks/use-permissions';
import { doc, getDoc, getDocFromServer, setDoc, updateDoc, collection, getDocs, getDocsFromServer, query, where, orderBy, limit, startAfter, onSnapshot, onFirestoreRefresh } from '@/lib/firebase/firestore';
import { deleteField, FieldPath } from 'firebase/firestore';

import { httpsCallable } from '@/lib/firebase/functions';
import { db, functions } from '@/config/firebase';
import { dbg } from '@/lib/debug-logger';
import { idbCache } from '@/lib/cache/idb-cache';
import { swrFetch, invalidateByPattern } from '@/lib/cache/swr';
import { cacheKeys, TTL } from '@/lib/cache/cache-keys';
import { runWithConcurrency } from '@/lib/utils/concurrency';
import { playReminderChime } from '@/lib/utils/alert-sound';
import { useUIStore } from './ui-store';
import { applyCountryCode, isWaUserId, toContactId, waUserIdToEmail } from '@/lib/utils/phone';
import { OPT_OUT_LIST_PARENT_ID, OPT_OUT_LIST_COLLECTION } from '@/lib/utils/opt-out';

/** Cross-tab broadcast channel for syncing conversation state */
const crossTabChannel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('ulgebra-conv-sync')
  : null;

/** In-flight fetchMessages promise — prevents duplicate concurrent fetches for the
 *  same conversation (React StrictMode double-invokes effects in development). */
let activeFetchMessagesId: string | null = null;
let activeFetchMessagesPromise: Promise<void> | null = null;

/** Monotonic token used to abort in-flight Twilio Firestore-filter enrichment
 *  when a new filter is applied — prevents stale API results from landing
 *  on the wrong conversation list. */
let twilioEnrichmentToken = 0;

/** Generation counter for `fetchConversations`. Every call takes the next
 *  value; a call whose value is no longer current has been superseded by a
 *  newer filter and must not write its results into the store. */
let convFetchToken = 0;

/** Identity of the in-flight `fetchConversations` request, used to drop a
 *  genuinely duplicate call (React StrictMode double-invoke) without
 *  blocking a call for different filters. */
let convFetchKey: string | null = null;

/**
 * Fill in the last-message preview for Firestore-sourced Twilio conversations
 * by asking the Twilio API, applying each result as it lands.
 *
 * Conversations arrive from the Firestore-filtered queries with
 * `enrichmentPending: true` whenever the inbox doc has no usable preview, and
 * the row renders a skeleton shimmer until this fills it in — so every code
 * path that puts such conversations into the store has to run this, including
 * pagination. Callers that start a *new* list bump `twilioEnrichmentToken`
 * first so any in-flight pass aborts; callers appending to the current list
 * (load-more) must not, or they would cancel the pass still working through
 * the earlier page.
 */
function enrichTwilioLatestMessages(
  convs: Conversation[],
  set: (partial: Partial<ConversationStore>) => void,
  get: () => ConversationStore
): void {
  if (convs.length === 0) return;
  const tpaModule = useModuleStore.getState().tpaModule;
  if (!tpaModule || tpaModule.id !== 'twilio') return;

  const twilioModule = tpaModule as unknown as {
    enrichConversationsWithLatestMessages?: (
      convs: Conversation[],
      onEach: (convId: string, update: { lastMessage: string; lastMessageTime: Date; metadata: Record<string, unknown> }) => void,
      shouldAbort?: () => boolean
    ) => Promise<void>;
  };
  if (typeof twilioModule.enrichConversationsWithLatestMessages !== 'function') return;

  const myToken = twilioEnrichmentToken;
  twilioModule.enrichConversationsWithLatestMessages(
    convs,
    (convId, update) => {
      const updated = get().conversations.map((c) =>
        c.id === convId
          ? {
            ...c,
            lastMessage: update.lastMessage,
            lastMessageTime: update.lastMessageTime,
            metadata: { ...c.metadata, ...update.metadata },
          }
          : c
      );
      set({ conversations: updated });
    },
    () => twilioEnrichmentToken !== myToken
  ).catch((e) => console.warn('[ConvStore] Twilio enrichment failed:', e));
}

/** Per-conversation debounce for the "mark as read" cloud function.
 *  Server derives the read index from the conv doc itself, so rapid
 *  clicks / focus-blur bursts on the same conv should collapse to a
 *  single call within this window. */
const _lastReadCallAt = new Map<string, number>();
const READ_DEBOUNCE_MS = 2000;
/** Per-conversation timestamp of the last local "Mark as unread". Mirrors
 *  `_lastReadCallAt`: server refresh paths that would otherwise accept a
 *  doc-derived 0 must hold off until the mark-unread CF has landed on the doc. */
const _lastUnreadMarkAt = new Map<string, number>();
const READ_STATE_SETTLE_MS = 15000;

/** Default system labels for conversations */
const DEFAULT_CHAT_LABELS: Record<string, ConvChatLabel> = {
  unreplied: { display_label: 'Un Replied', value: 'NEED_REPLY', systemClass: 'sys-sts-open', color: '#00b1ff', order_pos: -1 },
  open: { display_label: 'Open', value: 'open', systemClass: 'sys-sts-open', color: '#21c221' },
  hold: { display_label: 'On Hold', value: 'hold', systemClass: 'sys-sts-onhold', color: '#ff8c00' },
  closed: { display_label: 'Closed', value: 'closed', systemClass: 'sys-sts-closed', color: '#6b7280' },
  blocked: { display_label: 'Blocked', value: 'blocked', systemClass: 'sys-sts-blocked', color: '#dc143c' },
};

/** True for a built-in status key (Open/On Hold/Closed/Blocked and the virtual
 *  'unreplied'). Keyed on the key on purpose: an org-stored override of a system
 *  key can lack `systemClass`, so the stored marker can't gate create/delete. */
export function isSystemLabelKey(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(DEFAULT_CHAT_LABELS, key);
}

/** A custom label that was deleted. Chats keep the old key, so Kanban uses this
 *  record to show them in a "Removed labels" column where they can be re-tagged.
 *  Stored per org next to the label list, in `ua_conv_chat_labels_deleted.{key}`. */
export interface DeletedChatLabel {
  display_label: string;
  color?: string;
  deletedAt: number;
}

const DELETED_LABELS_FIELD = 'ua_conv_chat_labels_deleted';
/** Keep only the most recently deleted labels — older records are pruned on delete. */
const MAX_DELETED_LABELS = 20;

/** A single mention document from UA_INBOX_MENTIONS/{email}/MENTIONED_MESSAGES */
export interface MentionDocument {
  id: string;
  conversationId: string;
  noteId: string;
  noteBody: string;
  mentionedBy: string;
  mentionedByName: string;
  createdTime: number;
  read: boolean;
}

/**
 * What a caller supplies to create or reschedule a reminder. The store fills in
 * the rest of the task document (ids, owner, status, eventBody) — see
 * `setScheduledEvent`, which is the only place that shape is built.
 */
export interface ReminderInput {
  subject: string;
  /** Epoch ms the reminder is due. */
  performAt: number;
  /** Present when rescheduling — reuses the existing REMINDERS_TASKS doc. */
  eventId?: string;
}

/** A past (completed/cancelled) reminder fetched from REMINDERS_TASKS */
export interface PastReminder {
  eventId: string;
  subject: string;
  performAt: number;
  createdTime: number;
  conversationId: string;
  contactId: string;
  status: string;
  read: boolean;
}

interface ConversationStore {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  activeSender: Sender | null;
  messages: Message[];
  /** Full mention documents for the current user */
  mentions: MentionDocument[];
  loading: boolean;
  loadingMore: boolean;
  messagesLoading: boolean;
  loadingOlderMessages: boolean;
  messagesHasMore: boolean;
  searchQuery: string;
  filterChannel: Channel | null;
  filterLabel: string | null;
  /** Agent filter: 'all' | 'mine' | 'unassigned' | specific email */
  filterAgent: string;
  /** One-shot "jump to" cutoff — show conversations last updated on or before this date */
  jumpBefore: Date | null;
  error: string | null;
  nextCursor: string | null;
  hasMore: boolean;
  chatLabels: Record<string, ConvChatLabel>;
  /** Recently deleted custom labels (see DeletedChatLabel) — keys not in chatLabels. */
  deletedChatLabels: Record<string, DeletedChatLabel>;
  /** True once the org's label list has arrived from the server (not just the
   *  cached paint). UI that treats "missing from chatLabels" as meaningful must
   *  wait for this, or a still-loading label looks deleted. */
  chatLabelsLoaded: boolean;

  setActiveConversation(c: Conversation | null): void;
  setActiveSender(sender: Sender | null): void;
  setSearchQuery(query: string): void;
  setFilterChannel(channel: Channel | null): void;
  setFilterLabel(label: string | null): void;
  setFilterAgent(agent: string): void;
  /** Jump-load conversations most recent at or before this date (replaces list) */
  jumpToConversationsAtDate(date: Date | null): Promise<void>;
  fetchConversations(): Promise<void>;
  loadMoreConversations(): Promise<void>;
  /** Resolve a full Conversation record for `id` without mutating `conversations`.
   *  Tries: (1) current list, (2) Firestore inbox doc, (3) parse id as
   *  `sender::contact`. Falls back to a minimal stub. Used by the chat dialog
   *  to render the header for conversations not in the current list. */
  resolveConversationById(id: string): Promise<Conversation>;

  /**
   * Pull pinned conversations that aren't in the loaded page into the list by
   * reading their inbox docs by id. Without this a chat pinned long ago only
   * surfaces once the user scrolls far enough to "Load more". No-op when a
   * filter/search/jump-date is active, or when nothing is missing.
   */
  hydratePinnedConversations(): Promise<void>;
  fetchMessages(conversationId: string): Promise<void>;
  /** Read this conversation's inbox notes and add them to the messages already
   *  in the store. For surfaces that get their messages from somewhere other
   *  than `fetchMessages` (the CRM chat view's `searchContactMessages`, which
   *  returns provider messages only) and would otherwise show no notes. */
  mergeNotesIntoMessages(conversationId: string): Promise<void>;
  /** Jump-load messages at or before a specific date (replaces current list) */
  jumpToMessagesAtDate(conversationId: string, date: Date): Promise<void>;
  loadOlderMessages(): Promise<void>;
  refreshMessages(): Promise<void>;
  addMessage(message: Message): void;
  markAsRead(conversationId: string, _fromCrossTab?: boolean): void;
  /** WhatsApp-Web style "Mark as unread": badge of 1 as a come-back-later
   *  reminder, shared by the whole team. Persisted as `__markedUnread` on
   *  the inbox doc; the next open by any agent (markAsRead) clears it. */
  markAsUnread(conversationId: string, _fromCrossTab?: boolean): void;
  updateMessageStatus(messageId: string, newStatus: MessageStatus): void;
  updateConversation(conversationId: string, updates: Partial<Conversation>): void;
  addOrUpdateMessage(message: Message, fromRealtime?: boolean): void;
  /** Set of message IDs added via realtime events (for new-message animation) */
  realtimeMessageIds: Set<string>;
  clearRealtimeMessageIds(): void;
  /** Set of conversation IDs currently being enriched by the CRM phone-lookup
   *  pass. Drives a subtle pulse on the contact display in the conversation
   *  list while the lookup is in flight. */
  enrichingConvIds: Set<string>;
  prependOrUpdateConversation(conversationId: string, lastMessage: string, lastMessageTime: Date, incrementUnread?: boolean, newConvDefaults?: Partial<Conversation>): void;
  togglePin(conversationId: string): void;
  pinMessage(conversationId: string, messageId: string | null): void;
  removeConversation(conversationId: string): void;
  updateConvLabel(conversationId: string, label: string, opts?: { isNewLabel?: boolean }): Promise<void>;
  updateConvAssignee(conversationId: string, assigneeEmail: string): Promise<void>;
  autoAssignIfUnassigned(conversationId: string, assigneeEmail: string): Promise<void>;
  addCustomLabel(key: string, label: ConvChatLabel): Promise<void>;
  /** Delete a CUSTOM chat label from the org list (system keys are protected).
   *  Requires the Delete Labels permission AND modify-level (View & Apply) access
   *  to this label. Chats still tagged keep the value and render as an orphan
   *  badge until re-tagged — no bulk reassignment. */
  deleteCustomLabel(key: string): Promise<void>;
  /** Ensure chat labels (defaults + custom) are loaded; safe to call multiple times */
  ensureChatLabelsLoaded(): Promise<void>;
  /** Add an internal note to the active conversation (stored in Firestore, not sent to messaging API).
   *  `attachment` carries an already-uploaded file — notes never reach the provider, so the
   *  caller uploads to a permanent path (`uploadNoteAttachment`) and passes the URL here. */
  addNote(conversationId: string, body: string, from: string, to: string, channel: Channel, mentionedEmails?: string[], attachment?: MessageAttachment): Promise<void>;
  /** Lazy-fetch inbox details (label, assignee, active-flow marker) for the active conversation from Firestore */
  fetchActiveConvDetails(): Promise<void>;
  /** Cancel a scheduled message via the TPA API */
  cancelScheduledMessage(messageId: string): Promise<{ success: boolean; error?: string }>;
  /** Search conversations by a specific phone number via the API */
  searchByPhone(phone: string): Promise<void>;
  /** Whether a phone number API search is active (results are from API, not local filter) */
  phoneSearchActive: boolean;
  /** Resolve media attachments for a single message (called when message enters viewport) */
  resolveMessageMedia(messageId: string): void;

  /** Stop the Chat Automation flow currently driving a conversation (unlocks the composer). */
  stopActiveFlow(conversationId: string): Promise<void>;

  /** Per-conversation draft messages — keyed by conversation ID */
  drafts: Record<string, string>;
  /** Save a draft message for a conversation (debounced Firestore write) */
  saveDraft(conversationId: string, text: string): void;
  /** Clear draft for a conversation (also clears from Firestore) */
  clearDraft(conversationId: string): void;
  /** Get the draft text for a conversation */
  getDraft(conversationId: string): string;

  // ── UA_INBOX_UTILITY methods (ported from old app) ──────────────────

  /** Mark conversation as read on the server (calls cloud function).
   *  The server reads the current `messagesCount` from the conv doc and
   *  writes `__userLastReadCount_{uid}` to that value — client doesn't
   *  pass the index anymore. Client-side is debounced per-conv to avoid
   *  storming the function on rapid clicks / focus-blur cycles. */
  markMessageAsReadInConversation(conversationId: string, contactId: string): Promise<void>;
  /** Update conversation subject/chat name */
  updateConvSubject(conversationId: string, subject: string): Promise<void>;
  /** Pin/unpin a message via the server API (persists to Firestore) */
  setPinnedMessage(conversationId: string, contactId: string, pinnedMessage: Record<string, unknown> | null): Promise<void>;
  /** Star/unstar a message via the server API */
  starMessage(conversationId: string, contactId: string, messageId: string, isStarred: boolean): Promise<void>;
  /** Delete a conversation or message via the server API */
  deleteConvOrMessage(type: 'conversation' | 'message', conversationId: string, messageId?: string | null): Promise<void>;
  /** Set (or reschedule) a follow-up reminder. Resolves false when the write
   *  failed — the caller must not close its form as if it had worked. */
  setScheduledEvent(conversationId: string, contactId: string, reminder: ReminderInput): Promise<boolean>;
  /** Cancel or complete a reminder — updates status on server and clears local
   *  state. Resolves false when nothing was written. */
  cancelOrCompleteReminder(conversationId: string, contactId: string, status: 'completed' | 'cancelled', eventId?: string): Promise<boolean>;
  /** React to a server-pushed `schedule-task` RTDB event — a reminder fired,
   *  or was set/cancelled from another device. Shows the toast + chime on fire. */
  handleReminderEvent(event: { b?: unknown; z?: Record<string, unknown> }): void;
  /** Tell the server a fired reminder was seen (UA_UPDATE_REMINDER_WITH_READ) */
  markReminderAsRead(scheduledEvent: Record<string, unknown>): Promise<void>;
  /** Fetch past (completed) reminders from Firestore */
  fetchPastReminders(): Promise<void>;
  /** Past reminders list */
  pastReminders: PastReminder[];
  /** Whether past reminders are loading */
  pastRemindersLoading: boolean;
  /** More closed reminders exist beyond the page we hold. */
  pastRemindersHasMore: boolean;
  pastRemindersLoadingMore: boolean;
  /** Append the next page of closed reminders. */
  loadMorePastReminders(): Promise<void>;
  /** ALL of my scheduled reminders, straight from REMINDERS_TASKS. The
   *  conversation badge shows only the one "next" pointer per chat — this is
   *  the full drawer, including multiple per chat and chats not currently
   *  loaded in the inbox list. */
  activeReminders: PastReminder[];
  activeRemindersLoading: boolean;
  /** The last drawer read failed (e.g. Firestore rules). An empty list then
   *  means "unknown", not "no reminders" — consumers must not conclude a chat
   *  is reminder-free from it. */
  activeRemindersFailed: boolean;
  /** Reminders on the conversation that is open, fetched for that chat alone.
   *  The panel and the menu label read THIS, not the global list: asking about
   *  one chat is a handful of documents, where filtering a paged global list
   *  gets the answer wrong as soon as the chat's reminder falls outside the page. */
  chatReminderConvId: string | null;
  chatReminderRows: PastReminder[];
  chatRemindersLoading: boolean;
  chatRemindersFailed: boolean;
  fetchChatReminders(conversationId: string): Promise<void>;
  /** More scheduled reminders exist beyond the page we hold. */
  activeRemindersHasMore: boolean;
  activeRemindersLoadingMore: boolean;
  fetchActiveReminders(): Promise<void>;
  /** Append the next page of scheduled reminders (legacy's "load more"). */
  loadMoreActiveReminders(): Promise<void>;
  /** Remove the "need reply" label (mark conversation as replied) */
  removeNeedReplyLabel(conversationId: string): Promise<void>;
  /** Update general message details/metadata */
  updateMessageDetails(conversationId: string, contactId: string, messageId: string, updateData: Record<string, unknown>): Promise<void>;
  /**
   * Store per-message meta for a freshly-sent outbound message.
   * Writes the current user's source object + resolved text to the
   * Firestore messages sub-collection via UA_INBOX_UPDATE_MESSAGE.
   * Used after a successful send so shared users see author attribution
   * and lazy message meta can surface the sender avatar.
   */
  storeSentMessageMeta(conversationId: string, contactId: string, messageId: string, resolvedText: string): Promise<void>;
  /**
   * Request the author/source of a specific message. Called from ChatMessage
   * when it enters the viewport. Debounced + batched — multiple requests in
   * quick succession are merged into a single Firestore query.
   * Results are cached for the session. Returns immediately from cache if hit.
   */
  requestMessageAuthor(messageId: string): void;
  /** Update WABA media download URL when initially missed */
  updateWABAMediaUrl(conversationId: string, contactId: string, messageId: string, mediaFileID: string): Promise<void>;
  /** Update inbox details along the assignee path */
  updateInboxDetailsInAssigneePath(conversationId: string, contactId: string | null, updateData: Record<string, unknown>): Promise<void>;
  /** Map service entity relationships for TPA */
  setServiceEntityMapsForTPA(orgId: string, saasUserId: string, msgId: string, historyData: Record<string, unknown>): Promise<void>;
  /** Update linked SaaS record info in conversation */
  updateExistingRecordInConversation(conversationId: string, module: string, recordId: string, recordName?: string): Promise<void>;
  /** Update the contact-shaped CRM record used for inbox-row display.
   *  Separate from saas_record so we can stamp a Contact without
   *  overwriting a user-linked Ticket/Account in saas_record. */
  updateExistingContactInConversation(conversationId: string, module: string, recordId: string, recordName?: string): Promise<void>;
  /** Drop the local enrichment IDB cache and force-refresh CRM record
   *  matches for every conversation in the current list. Clears
   *  in-memory `crmRecord` so the candidate filter accepts them, then
   *  re-runs the inbox-detail read + enrichment pass. */
  resyncConversationEnrichment(): Promise<void>;
  /** Fetch mentions from Firestore — call lazily when the mentions panel is opened */
  fetchMentions(): Promise<void>;
  /** Dismiss a mention by ID — marks as read locally and on server (matches old app behavior) */
  dismissMention(mentionId: string): void;
  /** Starred message IDs — keyed by message ID */
  starredMessages: Record<string, boolean>;
}

/**
 * Get the Firebase doc path for a conversation's inbox details.
 * Path: ulgebraUsers/{uid}/extensions/{tpaCode}forpage/UA_INBOX_CONVERSATIONS/{convId}
 */
export function getInboxConvDocPath(convId: string): string | null {
  const uid = getEffectiveUid(); // read path — uses effective UID
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const appCodeTPA = appStore.appCodeTPA;

  if (!uid || !extensionName || !appCodeTPA) return null;

  // extensionName base: e.g. "twilioforzohocrm" → "twilio", use "twilioforpage"
  const tpaBase = extensionName.includes('for')
    ? extensionName.split('for')[0]
    : extensionName;

  return `ulgebraUsers/${uid}/extensions/${tpaBase}forpage/UA_INBOX_CONVERSATIONS/${convId}`;
}

/**
 * KAN-155: the opt-out list of one sender — every customer who opted out of it,
 * in one document, written by the server on each STOP / START.
 * Path: …/UA_INBOX_CONVERSATIONS/UA_OPT_OUT_LISTS/senders/{senderKey}
 * It sits under UA_INBOX_CONVERSATIONS because that is the folder the whole
 * team may read (Firestore rules). No conversation exists under the id
 * "UA_OPT_OUT_LISTS", so conversation queries never see it.
 */
export function getInboxOptOutListDocPath(senderKey: string): string | null {
  const parentPath = getInboxConvDocPath(OPT_OUT_LIST_PARENT_ID);
  return parentPath && senderKey ? `${parentPath}/${OPT_OUT_LIST_COLLECTION}/${senderKey}` : null;
}

/**
 * Collection holding this user's reminder tasks. Reminders are per-agent: the
 * inbox owner (or impersonated admin) owns the extension node, but the tasks
 * live under the signed-in user's own uid.
 *   ulgebraUsers/{ownerUid}/extensions/{tpa}forpage/UA_INBOX_TASKS/{userUid}/REMINDERS_TASKS
 */
export function getRemindersTasksPath(): string | null {
  const ownerUid = getEffectiveUid();
  const userUid = useAuthStore.getState().user?.uid || ownerUid;
  const extensionName = useAppStore.getState().extensionName;
  if (!ownerUid || !userUid || !extensionName) return null;
  const tpaBase = extensionName.includes('for') ? extensionName.split('for')[0] : extensionName;
  return `ulgebraUsers/${ownerUid}/extensions/${tpaBase}forpage/UA_INBOX_TASKS/${userUid}/REMINDERS_TASKS`;
}

/**
 * schedule-task pushes we already reacted to, keyed `${eventId}:${status}`.
 * Both RTDB alert nodes (org-level and per-user) can replay the same push,
 * and the hook-level dedup only covers a 2-second window.
 *
 * A Map with timestamps, not a Set: keys have to expire. A dashboard left open
 * all day would otherwise accumulate one entry per reminder event forever.
 * Same pruning shape as `recentEvents` in use-realtime-listeners.ts, just with
 * a much longer window. (Sign-out and impersonation changes reload the page,
 * which clears this along with all other module state.)
 */
const _handledReminderEvents = new Map<string, number>();
const HANDLED_REMINDER_TTL_MS = 6 * 60 * 60 * 1000;

function _rememberReminderEvent(key: string): boolean {
  const now = Date.now();
  for (const [k, t] of _handledReminderEvents) {
    if (now - t > HANDLED_REMINDER_TTL_MS) _handledReminderEvents.delete(k);
  }
  if (_handledReminderEvents.has(key)) return false;
  _handledReminderEvents.set(key, now);
  return true;
}


/**
 * Run an ordered query, falling back to the unordered one when Firestore has no
 * composite index for it. Ordering matters because every reminder query is
 * capped: unordered, Firestore returns by document id, and ids embed the
 * creation time — so the cap kept the OLDEST reminders and hid the newest.
 * The index may not exist in every project, and a missing one throws
 * `failed-precondition`, so the unordered read stays as the safety net.
 */
type _Queryable = Parameters<typeof getDocsFromServer>[0];
async function _orderedOrPlain(ordered: _Queryable, plain: _Queryable) {
  try {
    return { snapshot: await getDocsFromServer(ordered), ordered: true };
  } catch (err) {
    const code = (err as { code?: string })?.code;
    if (code !== 'failed-precondition') throw err;
    console.warn('[ConvStore] No composite index for the ordered reminder query — falling back');
    return { snapshot: await getDocsFromServer(plain), ordered: false };
  }
}

/**
 * One page of reminders, ordered when the index allows it. Both lists page the
 * way the legacy client did (firebase-things.js:18199 — limit + orderBy +
 * startAfter): a hard cap alone either hides reminders or forces us to read the
 * whole collection.
 */
// Both lists are lists, nothing more — the per-chat query answers "does this
// chat have a reminder?" now, so neither has to be big enough to cover every
// conversation. Same page size legacy used (firebase-things.js:18199), with
// "Load more" for the rest.
const REMINDERS_PAGE_SIZE = 10;
const PAST_REMINDERS_PAGE_SIZE = 10;
/** A single chat's PENDING reminders — filtered server-side, so this is a stop,
 *  not a page: it would take fifty unclosed reminders on one conversation. */
const CHAT_REMINDERS_LIMIT = 50;
/**
 * Recently-read chats, so clicking back and forth does not re-query. Two reads
 * per chat open is small but not free, and an inbox is browsed by clicking
 * around. Writes keep this in step through `_syncChatRow`, so a cached list is
 * never staler than the live one; the refresh bus and pushes update it too.
 */
const CHAT_REMINDERS_TTL_MS = 30_000;
const CHAT_REMINDERS_CACHE_MAX = 20;
const _chatRowsCache = new Map<string, { rows: PastReminder[]; at: number }>();

function _cacheChatRows(conversationId: string, rows: PastReminder[]): void {
  _chatRowsCache.set(conversationId, { rows, at: Date.now() });
  // Bounded: the oldest insertion goes when the map outgrows the cap.
  while (_chatRowsCache.size > CHAT_REMINDERS_CACHE_MAX) {
    const oldest = _chatRowsCache.keys().next().value;
    if (oldest === undefined) break;
    _chatRowsCache.delete(oldest);
  }
}
/** Firestore cursors for the next page. Snapshots, so they live outside the store. */
let _activeRemindersCursor: unknown = null;
let _pastRemindersCursor: unknown = null;
/** Which ordering each list is paging in. A cursor captured under `orderBy` is
 *  meaningless to an unordered query (Firestore would read it against `__name__`),
 *  so if the mode flips mid-list — an index appearing, or a transient failure —
 *  the cursor is dropped rather than used to skip an arbitrary range. */
let _activeRemindersOrdered: boolean | null = null;
let _pastRemindersOrdered: boolean | null = null;

type _RemindersPage = { rows: PastReminder[]; cursor: unknown; hasMore: boolean; ordered: boolean };

async function _fetchReminderPage(
  tasksPath: string,
  kind: 'active' | 'past',
  cursor: unknown
): Promise<_RemindersPage> {
  const tasksRef = collection(db, tasksPath);
  const size = kind === 'active' ? REMINDERS_PAGE_SIZE : PAST_REMINDERS_PAGE_SIZE;
  const filter = kind === 'active'
    ? where('status', '==', 'scheduled')
    : where('status', 'in', ['completed', 'cancelled']);
  const direction = kind === 'active' ? 'asc' : 'desc';
  type Constraint = Parameters<typeof query>[1];
  const build = (ordered: boolean) => {
    const parts: Constraint[] = [filter];
    if (ordered) parts.push(orderBy('performAt', direction));
    if (cursor) parts.push(startAfter(cursor));
    parts.push(limit(size));
    return query(tasksRef, ...parts);
  };
  const { snapshot, ordered } = await _orderedOrPlain(build(true), build(false));

  const rows: PastReminder[] = [];
  let last: unknown = null;
  snapshot.forEach((d) => {
    const data = d.data() as Record<string, unknown>;
    const eventBody = data.eventBody as Record<string, unknown> | undefined;
    rows.push({
      eventId: d.id,
      subject: (data.subject as string) || (data.text as string) || 'Reminder',
      performAt: (data.performAt as number) || 0,
      createdTime: (data.createdTime as number) || 0,
      conversationId: (eventBody?.conversationId as string) || (data.conversationId as string) || '',
      contactId: (eventBody?.contactId as string) || (data.contactId as string) || '',
      status: data.status as string,
      read: (data.read as boolean) ?? true,
    });
    last = d;
  });
  return { rows, cursor: last, hasMore: rows.length === size, ordered };
}

/**
 * Everything we already know about a reminder, wherever it is held. The open
 * chat's rows come FIRST: since the panel switched to its own per-chat read, the
 * global `activeReminders` is only populated once the bell dialog has been
 * opened, so anything that consulted it alone was reading an empty list in
 * normal use.
 */
function _knownReminder(eventId: string): PastReminder | undefined {
  const st = useConversationStore.getState();
  return (
    st.chatReminderRows.find((r) => r.eventId === eventId) ||
    st.activeReminders.find((r) => r.eventId === eventId) ||
    st.pastReminders.find((r) => r.eventId === eventId)
  );
}

/**
 * Reflect a `scheduled` push: the chat badge, the drawer, and the open chat's
 * rows. Shared by the two paths in `handleReminderEvent` — a push for a reminder
 * we already know, and one confirmed against the stored task first.
 */
function _applyScheduledPush(
  eventId: string,
  task: Record<string, unknown>,
  conversationId: string,
  applyMeta: (patch: Record<string, unknown>) => void
): void {
  applyMeta({
    hasReminder: true,
    reminderEventId: eventId,
    reminderTime: task.performAt,
    reminderSubject: task.subject || 'Reminder',
    reminderCreatedTime: task.createdTime,
  });
  // Only `completed` and `cancelled` used to maintain the lists, so a reminder
  // set on another tab or device stayed invisible: the menu kept saying "Set
  // reminder", the panel showed the empty picker, and the bell omitted it.
  // Filtering by id first makes this an upsert, which is what a reschedule needs.
  const eventBody = task.eventBody as Record<string, unknown> | undefined;
  const row: PastReminder = {
    eventId,
    subject: (task.subject as string) || (task.text as string) || 'Reminder',
    performAt: (task.performAt as number) || 0,
    createdTime: (task.createdTime as number) || 0,
    conversationId,
    contactId: (eventBody?.contactId as string) || (task.contactId as string) || '',
    status: 'scheduled',
    read: (task.read as boolean) ?? false,
  };
  const store = useConversationStore.getState();
  useConversationStore.setState({
    activeReminders: [...store.activeReminders.filter((r) => r.eventId !== eventId), row]
      .sort((a, b) => a.performAt - b.performAt),
  });
  _syncChatRow(row, eventId);
}

/** Upsert / drop a row in the open chat's list, so the panel never lags a write. */
function _syncChatRow(row: PastReminder | null, eventId: string, remove = false): void {
  const st = useConversationStore.getState();
  if (!st.chatReminderConvId) return;
  const conv = row?.conversationId || st.chatReminderRows.find((r) => r.eventId === eventId)?.conversationId;
  // Removing tolerates an unknown conversation (it is a no-op when absent), but
  // INSERTING must be certain: a pushed task with no conversation id used to be
  // accepted here and shown as if it belonged to whatever chat was open.
  if (conv ? conv !== st.chatReminderConvId : !remove) return;
  const without = st.chatReminderRows.filter((r) => r.eventId !== eventId);
  const next = remove || !row ? without : [...without, row].sort((a, b) => a.performAt - b.performAt);
  useConversationStore.setState({ chatReminderRows: next });
  // The cache must not outlive the truth it was built from.
  _cacheChatRows(st.chatReminderConvId, next);
}

/**
 * Reminders closed during this session. The Past list is paged, and without the
 * composite index the page comes back in document order — so the reminder a user
 * just cancelled is usually NOT on page one, and the fetch would replace the row
 * we had put at the top with a page that omits it. Merging these back keeps
 * "close it, look at Past, see it" true whether or not the index exists.
 */
const _closedThisSession = new Map<string, PastReminder>();
const CLOSED_SESSION_MAX = 50;

function _rememberClosed(row: PastReminder): void {
  _closedThisSession.set(row.eventId, row);
  while (_closedThisSession.size > CLOSED_SESSION_MAX) {
    const oldest = _closedThisSession.keys().next().value;
    if (oldest === undefined) break;
    _closedThisSession.delete(oldest);
  }
}

/** A fetched page plus anything closed here that the page did not include. */
function _withClosedThisSession(page: PastReminder[]): PastReminder[] {
  const byId = new Map(page.map((r) => [r.eventId, r] as const));
  for (const [id, row] of _closedThisSession) if (!byId.has(id)) byId.set(id, row);
  return [...byId.values()].sort((a, b) => b.performAt - a.performAt);
}

/** Reminder-drawer fetch coordination: one read at a time, and not more often
 *  than the TTL. */
let _activeRemindersInFlight: Promise<void> | null = null;
let _activeRemindersFetchedAt = 0;
/** A drawer read this recent is reused instead of refetched. */
const ACTIVE_REMINDERS_TTL_MS = 30_000;

/**
 * Close a reminder out locally: drop it from the Active drawer and put it at the
 * top of Past, built from the pushed task when the drawer never held it.
 */
function _moveReminderToPast(
  eventId: string,
  task: Record<string, unknown>,
  status: 'completed' | 'cancelled'
): void {
  _syncChatRow(null, eventId, true);
  const store = useConversationStore.getState();
  const eventBody = task.eventBody as Record<string, unknown> | undefined;
  const existing = store.activeReminders.find((r) => r.eventId === eventId);
  const row: PastReminder = existing
    ? { ...existing, status, read: true }
    : {
        eventId,
        subject: (task.subject as string) || (task.text as string) || 'Reminder',
        performAt: (task.performAt as number) || 0,
        createdTime: (task.createdTime as number) || 0,
        conversationId:
          (eventBody?.conversationId as string) || (task.conversationId as string) || '',
        contactId: (eventBody?.contactId as string) || (task.contactId as string) || '',
        status,
        read: true,
      };
  _rememberClosed(row);
  useConversationStore.setState({
    activeReminders: store.activeReminders.filter((r) => r.eventId !== eventId),
    // Sorted, not prepended: Past is newest-due first, and a reminder closed now
    // can easily be due EARLIER than the rows already there — cancelling an
    // overdue one would otherwise pin it above reminders due next week.
    pastReminders: [row, ...store.pastReminders.filter((r) => r.eventId !== eventId)]
      .sort((a, b) => b.performAt - a.performAt),
  });
}

/**
 * Derive the per-user unread count from an inbox conversation doc.
 * Server tracks `messagesCount` on the doc and stamps
 * `__userLastReadCount_{uid}` when UA_INBOX_UPDATE_CONVERSATION_READ runs —
 * unread is the delta (same formula as the WhatsApp converter, see
 * whatsapp-message-converter.ts convertConversationItem). Returns undefined
 * when the doc doesn't track message counts, so callers can leave the
 * in-memory value untouched.
 */
export function computeInboxDocUnreadCount(
  data: Record<string, unknown>,
  userId: string | undefined
): number | undefined {
  const real = computeInboxDocRealUnreadCount(data, userId);
  // Manual "Mark as unread" (UA_INBOX_MARK_CONVERSATION_UNREAD): a badge no
  // matter what the counts say — even when the last message was ours.
  if (data.__markedUnread === true) return Math.max(real ?? 0, 1);
  return real;
}

/** Genuine per-user unread — messages that arrived after this user's read
 *  watermark. Ignores the manual "Mark as unread" flag. */
function computeInboxDocRealUnreadCount(
  data: Record<string, unknown>,
  userId: string | undefined
): number | undefined {
  const messagesCount = typeof data.messagesCount === 'number' ? data.messagesCount : undefined;
  if (messagesCount === undefined) return undefined;
  if (messagesCount <= 0) return 0;

  const lastRead = userId ? data[`__userLastReadCount_${userId}`] : undefined;
  let unread = typeof lastRead === 'number' ? messagesCount - lastRead : messagesCount;

  if (unread > 0) {
    const outgoing = data.messagesCountOutgoing;
    if (typeof outgoing === 'number' && outgoing === messagesCount) unread = 0;
    if (data.lastMessageIsIncoming === false) unread = 0;
  }
  return Math.max(0, unread);
}

/** True when the row's badge is ONLY the manual "Mark as unread" reminder:
 *  some agent flagged it, nobody opened it since (the read CF resets the
 *  shared flag to false), and no real message arrived for this user on top
 *  of it. The row then shows a dot instead of a count. The flag itself is
 *  deliberately NOT per user — unlike `__userLastReadCount_{uid}`, the
 *  reminder is for the whole team. */
export function isInboxDocMarkedUnread(
  data: Record<string, unknown>,
  userId: string | undefined
): boolean {
  if (data.__markedUnread !== true) return false;
  return !computeInboxDocRealUnreadCount(data, userId);
}

/**
 * Parse the `_activeFlow` marker the Chat Automation engine writes onto a
 * conversation doc while a flow is paused mid-run. Returns undefined when the
 * marker is absent/cleared (null) so callers can treat "no flow" uniformly.
 */
export function parseActiveFlow(raw: unknown): ActiveFlowInfo | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (!r.flowId && !r.flowName) return undefined;
  return {
    flowId: typeof r.flowId === 'string' ? r.flowId : '',
    flowName: typeof r.flowName === 'string' ? r.flowName : 'Flow',
    nodeId: typeof r.nodeId === 'string' ? r.nodeId : undefined,
    stage: typeof r.stage === 'string' ? r.stage : undefined,
    since: typeof r.since === 'number' ? r.since : undefined,
  };
}

/**
 * Parse the KAN-155 `optOut` field the server writes onto a conversation doc
 * when the customer sends a STOP / START keyword. Undefined when absent.
 */
export function parseOptOut(raw: unknown): ConversationOptOut | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  return {
    active: r.active === true,
    keyword: typeof r.keyword === 'string' ? r.keyword : undefined,
    time: typeof r.time === 'number' ? r.time : undefined,
  };
}

/**
 * Conversation ids circulate in both `a::b` and `a--b` spellings depending on
 * the surface (see base-tpa-module.ts:715). Compare them through this so a pin
 * saved under one spelling still matches a list entry carrying the other.
 */
export function normalizeConvId(id: string): string {
  return id.replaceAll('--', '::');
}

/**
 * Sortable epoch ms for a conversation's last message, 0 when unknown. An
 * `Invalid Date` is still `instanceof Date` and its `getTime()` is NaN, and a
 * comparator returning NaN leaves the array in an arbitrary order.
 */
function conversationTimeMs(c: Conversation): number {
  const raw = c.lastMessageTime;
  const ms = raw instanceof Date ? raw.getTime() : Number(raw);
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Map a raw UA_INBOX_CONVERSATIONS doc onto a Conversation for the pinned-chat
 * hydration path only.
 *
 * Deliberately NOT shared with `resolveConversationById`, even though the field
 * probing overlaps: rows injected into the inbox list must carry the sender half
 * of the id in `metadata`, because conversation-list.tsx hides conversations
 * whose sender the user lacks permission for by reading
 * `metadata.channelId || metadata.from` and treats an empty value as "allowed".
 * `resolveConversationById` feeds the chat dialog and deep links, which must
 * keep returning `metadata: {}` — populating it there would let the inbox's
 * permission filter hide a deep-linked conversation that used to render.
 */
function buildPinnedConversation(id: string, data: Record<string, unknown>): Conversation {
  let senderPhone = '';
  let contactPhone = '';
  if (id.includes('::')) {
    const [sender, contact] = id.split('::');
    if (sender) senderPhone = sender.replace(/^whatsapp:/i, '');
    if (contact) contactPhone = contact.replace(/^whatsapp:/i, '');
  }

  const channelFromDoc = typeof data.channel === 'string' ? data.channel.toUpperCase() : undefined;
  const validChannels: Channel[] = ['SMS', 'MMS', 'WHATSAPP', 'VOICE', 'EMAIL', 'INTERCOM', 'INSTAGRAM', 'TELEGRAM'];
  const channel: Channel =
    channelFromDoc && (validChannels as string[]).includes(channelFromDoc)
      ? channelFromDoc as Channel
      // Same fallback the Twilio converter uses (twilio.module.ts
      // convertFirestoreInboxDoc): the `whatsapp:` prefix in the id.
      : id.toLowerCase().includes('whatsapp') ? 'WHATSAPP' : 'SMS';

  const subject = typeof data.subject === 'string' ? data.subject : undefined;
  const contactPhoneFromDoc =
    (typeof data.contactPhone === 'string' ? data.contactPhone : undefined)
    || (typeof data.to === 'string' ? data.to : undefined)
    || (typeof data.contactNumber === 'string' ? data.contactNumber : undefined);

  // `lastMessage` is a plain string for Twilio but an object for WhatsApp.
  const lastMessageObj = data.lastMessage as Record<string, unknown> | undefined;
  const lastMessage =
    (typeof data.text === 'string' ? data.text : undefined)
    || (typeof data.body === 'string' ? data.body : undefined)
    || (typeof data.lastMessage === 'string' ? data.lastMessage : undefined)
    || (lastMessageObj && typeof lastMessageObj === 'object'
      ? (typeof lastMessageObj.body === 'string' ? lastMessageObj.body : undefined)
        || (typeof lastMessageObj.text === 'string' ? lastMessageObj.text : undefined)
      : undefined)
    || '';

  const rawTime = data.lastUpdatedTime ?? data.time ?? data.createdTime ?? data.lastMessageTime;
  const lastMessageTime =
    typeof rawTime === 'number' || typeof rawTime === 'string' ? new Date(rawTime) : new Date();

  return {
    id,
    contactName: subject
      || (typeof data.latestExistingRecordName === 'string' ? data.latestExistingRecordName : undefined)
      || contactPhoneFromDoc
      || contactPhone
      || id,
    contactPhone: contactPhoneFromDoc || contactPhone,
    lastMessage,
    lastMessageTime,
    unreadCount: typeof data.unreadCount === 'number' ? data.unreadCount : 0,
    channel,
    label: typeof data.label === 'string' ? data.label : undefined,
    assigneeEmail: typeof data.assigneeEmail === 'string' ? data.assigneeEmail : undefined,
    optOut: parseOptOut(data.optOut),
    metadata: senderPhone ? { from: senderPhone, channelId: senderPhone } : {},
  };
}

/**
 * Read pinned conversations straight from their inbox docs, skipping ids with no
 * doc so a deleted chat can't render a ghost row. Ports the legacy
 * `getAndLoadConversationInConvListByConvId` (public/legacy/js/tpa/*-things.js).
 *
 * Prefers the WhatsApp converter when the active TPA ships one — it derives
 * unreadCount, channelId and the last-message preview that the generic mapping
 * above can only approximate.
 */
async function fetchPinnedConversationsByIds(ids: string[]): Promise<Conversation[]> {
  const tpaModule = useModuleStore.getState().tpaModule;

  let waConvert: ((data: Record<string, unknown>) => Conversation) | null = null;
  if (tpaModule && 'buildConvertContext' in tpaModule) {
    try {
      const { convertConversationItem } = await import('@/modules/tpa/whatsapp/whatsapp-message-converter');
      type WaDoc = Parameters<typeof convertConversationItem>[0];
      type WaCtx = Parameters<typeof convertConversationItem>[2];
      const ctx = (tpaModule as unknown as { buildConvertContext(): WaCtx }).buildConvertContext();
      const uid = useAuthStore.getState().user?.uid || '';
      waConvert = (data) => convertConversationItem(data as unknown as WaDoc, uid, ctx);
    } catch { /* fall back to the generic mapping */ }
  }

  const results = await Promise.all(ids.map(async (id) => {
    // Ids circulate as both `a::b` and `a--b` (base-tpa-module.ts:715) — probe
    // each spelling, since the pin may be saved under either.
    const variants = id.includes('::') ? [id, id.replaceAll('::', '--')]
      : id.includes('--') ? [id, id.replaceAll('--', '::')]
        : [id];

    for (const variant of variants) {
      const path = getInboxConvDocPath(variant);
      if (!path) continue;
      try {
        const snap = await getDoc(doc(db, path));
        if (!snap.exists()) continue;
        const data = snap.data() as Record<string, unknown>;
        // Keep the caller's id so the row still matches `pinnedConversationIds`.
        if (waConvert) {
          try {
            return waConvert({ ...data, id });
          } catch { /* fall through to the generic mapping */ }
        }
        return buildPinnedConversation(id, data);
      } catch { /* try the next spelling */ }
    }
    return null;
  }));

  return results.filter((c): c is Conversation => c !== null);
}

/**
 * Get the Firebase collection path for inbox mentions.
 * Path: ulgebraUsers/{uid}/extensions/{tpaCode}forpage/UA_INBOX_MENTIONS
 */
function getInboxMentionsCollectionPath(): string | null {
  const uid = getEffectiveUid();
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const appCodeTPA = appStore.appCodeTPA;

  if (!uid || !extensionName || !appCodeTPA) return null;

  const tpaBase = extensionName.includes('for')
    ? extensionName.split('for')[0]
    : extensionName;

  return `ulgebraUsers/${uid}/extensions/${tpaBase}forpage/UA_INBOX_MENTIONS`;
}

/**
 * How many message docs the notes read scans. Wider than the TPA page size (50)
 * because notes and provider message docs share this window — a conversation
 * with a run of recent messages would otherwise surface no notes at all.
 */
const NOTES_FETCH_LIMIT = 100;

/**
 * True for docs UA writes itself — notes, pinned notes and system messages
 * (assignee/label/subject/reminder updates). These carry no provider-specific
 * shape, so they can be parsed without a TPA converter, unlike provider message
 * docs which differ per TPA (and for REST TPAs are only partial stubs).
 */
export function isUaAuthoredMessageDoc(data: Record<string, unknown>): boolean {
  const type = typeof data.type === 'string' ? data.type : '';
  return type.includes('--note')
    || data.status === 'note-added'
    || data._uaOriginService === 'ua-msg-system';
}

/**
 * Convert one UA-authored inbox message doc into a `Message`, or null when the
 * doc isn't one (regular provider message, unknown system type).
 *
 * Shared by the Firestore read below and the `onFirestoreRefresh` handler, so
 * both agree on what a note is and how it renders.
 */
export function parseInboxNoteDoc(
  docId: string,
  // Untyped Firestore doc bag — the parse reads a dozen optional legacy-shaped
  // fields, so it stays loose here rather than at every access.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>,
  convId: string,
): Message | null {
  // System messages (assignee-update, label-update, subject-update, optout-update)
  if (data._uaOriginService === 'ua-msg-system') {
    const sysType = data.type as string | undefined;
    if (sysType === 'assignee-update' || sysType === 'label-update' || sysType === 'subject-update' || sysType === 'chat-reminder' || sysType === 'optout-update') {
      return {
        id: data.id || docId,
        conversationId: convId,
        from: data.performer || '',
        to: data.assigneeEmail || data.label || '',
        body: data.text || data.body || '',
        channel: (data.channel as Channel) || 'SMS',
        direction: 'outbound',
        status: 'sent',
        timestamp: data.time ? new Date(data.time) : (data.createdTime ? new Date(data.createdTime) : new Date()),
        isNote: true,
        noteType: sysType,
        isSystemMessage: true,
        metadata: {
          performer: data.performer,
          assigneeEmail: data.assigneeEmail,
          label: data.label,
          subject: data.subject,
          ...(sysType === 'chat-reminder' && {
            reminderSubject: data.subject || data.text || '',
            reminderPerformAt: data.performAt || data.reminderTime || undefined,
            reminderCreatedTime: data.createdTime || data.time || undefined,
            // scheduled | cancelled | completed — drives the timeline verb.
            reminderStatus: data.status || undefined,
            reminderIsRescheduled: data.isRescheduled === true,
            // Automation-set reminders: the flow's name (author) and who the
            // reminder is FOR — lets the timeline say "New Flow set a
            // reminder for dev" instead of crediting the admin.
            reminderAuthorName: data.author?.name || undefined,
            reminderFor: data.remindFor || undefined,
          }),
          // KAN-155: true = the customer opted out, false = opted back in.
          ...(sysType === 'optout-update' && {
            optOut: data.optOut === true,
            optOutKeyword: typeof data.keyword === 'string' ? data.keyword : undefined,
          }),
        },
      };
    }
    return null;
  }

  // Only include notes (type contains "--note")
  const isNote = data.type?.includes('--note') || data.status === 'note-added';
  if (!isNote) return null;

  // Extract author info (old app stores it in source or author or _uaUser)
  const authorObj = data.source?.itsMsgSource ? data.source : (data.author || data._uaUser);
  const noteAuthor = authorObj
    ? {
        name: authorObj.name || authorObj.email || '',
        email: authorObj.email,
        pic: authorObj.pic,
        uid: authorObj.uaAppSaaSUserId || authorObj.uid,
      }
    : undefined;

  // For pinned notes, get the pinned-by author
  const isPinnedNote = data.type?.includes('--note--pinned');
  if (isPinnedNote && data.pinned?.createdBy_UA) {
    const pinnedBy = data.pinned.createdBy_UA;
    if (pinnedBy.name || pinnedBy.email) {
      Object.assign(noteAuthor || {}, {
        name: pinnedBy.name || pinnedBy.email || '',
        email: pinnedBy.email,
        pic: pinnedBy.pic,
        uid: pinnedBy.uaAppSaaSUserId || pinnedBy.uid,
      });
    }
  }

  // Media notes carry `attachments: [{ type, url }]` (legacy shape — the old app
  // wrote the same array for `image--note` docs). Without this the note renders
  // as an empty bubble because the file lives only in the doc.
  const rawAttachments = Array.isArray(data.attachments) ? data.attachments : [];
  const attachments: MessageAttachment[] = rawAttachments
    .filter((a: Record<string, unknown>) => a && typeof a.url === 'string' && a.url)
    .map((a: Record<string, unknown>) => ({
      url: a.url as string,
      type: (a.type as MessageAttachment['type']) || 'file',
      ...(typeof a.contentType === 'string' && { contentType: a.contentType }),
      ...(typeof a.name === 'string' && { name: a.name }),
      ...(typeof a.width === 'number' && { width: a.width }),
      ...(typeof a.height === 'number' && { height: a.height }),
      ...(typeof a.size === 'number' && { size: a.size }),
    }));

  return {
    id: data.id || docId,
    conversationId: convId,
    from: data.from || '',
    to: data.to || '',
    body: data.text || data.body || '',
    channel: (data.channel as Channel) || 'SMS',
    direction: data.isIncoming ? 'inbound' : 'outbound',
    status: 'sent',
    timestamp: data.createdTime ? new Date(data.createdTime) : new Date(),
    isNote: true,
    noteType: data.type || 'text--note',
    noteAuthor,
    ...(attachments.length > 0 && { attachments }),
  };
}

/**
 * Fetch notes (internal messages) from the Firestore messages subcollection
 * for a given conversation. Notes are stored with type "text--note" or similar.
 * Returns them as Message[] sorted by timestamp.
 */
/** Conversations whose legacy `default::` notes doc has already been probed
 *  this session — one extra read per conversation is the ceiling. */
const legacyNotesProbed = new Set<string>();

async function fetchNotesForConversation(
  convId: string,
  window?: {
    /** Only docs at or after this epoch — the oldest loaded TPA message. */
    since?: number;
    /** Only docs strictly before this epoch. Used when paging older messages in,
     *  so the slice already on screen isn't read twice. */
    until?: number;
  },
): Promise<Message[]> {
  const convDocPath = getInboxConvDocPath(convId);
  if (!convDocPath) {
    dbg.notesDocsRead(null, 0);
    return [];
  }

  try {
    const msgsRef = collection(db, convDocPath, 'messages');
    // Recency-ordered, newest first. A bare `limit()` is served in DOCUMENT-ID
    // order: `ua-note-…` (lowercase) sorts after every provider SID (`MM…`,
    // `SM…`), and note ids embed their epoch, so the window kept the OLDEST
    // notes and truncated the newest — a busy conversation showed only ancient
    // notes, or none once the time-window filter dropped them.
    // `_uaCreatedTime` is stamped by the CF on every UA-written doc and is the
    // same field the WhatsApp/MessageBird modules page this collection on.
    //
    // The range narrows the read to the slice of history actually on screen —
    // notes outside it are dropped by the caller anyway, so fetching them was
    // pure cost (one account read 100 docs to keep 3). There is deliberately no
    // upper bound by default: a note is almost always newer than the last
    // message, so capping at it would hide the note just added.
    // Inequality + orderBy are on the same field, so this runs on the automatic
    // single-field index — no composite index to deploy.
    const constraints = [];
    if (window?.since) constraints.push(where('_uaCreatedTime', '>=', window.since));
    if (window?.until) constraints.push(where('_uaCreatedTime', '<', window.until));
    constraints.push(orderBy('_uaCreatedTime', 'desc'), limit(NOTES_FETCH_LIMIT));

    let snap = await getDocs(query(msgsRef, ...constraints));
    if (snap.empty && !window?.since && !window?.until) {
      // Firestore skips docs that lack the sort field, so a conversation whose
      // docs predate `_uaCreatedTime` would read as empty — fall back to the
      // unordered read for those. Only when unbounded: an empty *range* result
      // legitimately means "no notes in this window", and re-reading the whole
      // window there would undo the saving.
      snap = await getDocs(query(msgsRef, limit(NOTES_FETCH_LIMIT)));
    }
    const parseSnapshot = (
      s: Awaited<ReturnType<typeof getDocs>>,
      readPath: string
    ): Message[] => {
      dbg.firestoreRead(`${readPath}/messages`, s.size);
      dbg.notesDocsRead(`${readPath}/messages`, s.size);

      const parsedNotes: Message[] = [];
      // Docs the parser rejected — logged with the fields the gate looks at, so a
      // note that never renders can be traced to its `type` / `status` / origin.
      const skipped: Array<Record<string, unknown>> = [];
      s.forEach((docSnap) => {
        const data = docSnap.data();
        const parsed = parseInboxNoteDoc(docSnap.id, data, convId);
        if (parsed) {
          parsedNotes.push(parsed);
          return;
        }

        // Not a note — so it's a provider message doc, and the only thing UA keeps
        // in it is the author of an outbound send. That's exactly what the
        // viewport-triggered author lookup re-reads by id later, so harvest it
        // here and the second read never happens.
        // Only positive results are cached: a doc scanned in the gap before
        // storeSentMessageMeta writes its `source` has no author yet, and caching
        // that as "none" would suppress the name for the rest of the session.
        // The id-keyed path still caches negatives, deliberately.
        const author = extractMessageAuthor(data);
        if (author && !messageAuthorCache.has(docSnap.id)) {
          messageAuthorCache.set(docSnap.id, author);
        }

        skipped.push({
          id: docSnap.id,
          type: data.type,
          status: data.status,
          origin: data._uaOriginService,
        });
      });
      dbg.notesParsed(parsedNotes.length, skipped);
      return parsedNotes;
    };

    const notes = parseSnapshot(snap, convDocPath);

    // The fallback turns on "no notes came back", not "the subcollection is
    // empty". The app writes a provider message doc of its own on the first
    // outbound send (storeSentMessageMeta), which makes the modern path
    // non-empty while still yielding zero notes — gating on emptiness retired
    // the legacy read the moment that happened, so notes visible on the first
    // open vanished afterwards and never came back.
    //
    // Windowed reads probe too. They are the live path — the CRM chat view
    // passes `{ since: oldest }` whenever the thread already holds provider
    // messages, and for Route Mobile those come from their API, not Firestore,
    // so the modern subcollection is routinely empty while the window is set.
    // Restricting the probe to unbounded reads meant a migrated conversation
    // opened from a CRM record never reached the legacy notes at all.
    //
    // Keyed by the resolved doc path, not the bare convId: the path embeds the
    // effective uid, and on the silent admin-restore flow the same convId can
    // be fetched under the agent's own uid before hydration and the admin's
    // after — a bare-convId key would let the first (wrong-uid) probe suppress
    // the real one for the rest of the session.
    // Deliberately NOT `notes.length > 0`: the two paths are merged, not chosen
    // between. Adding a single note to a migrated conversation writes it under
    // the modern id, and short-circuiting on that would retire the legacy read
    // and wipe every pre-migration note from the thread — the same disappearing
    // act, one step further along.
    if (legacyNotesProbed.has(convDocPath)) {
      return notes;
    }

    // Route Mobile WA minted conversation ids with a literal 'default'
    // sender half (`credentials.senderId ?? 'default'`) until it began
    // resolving the real account number, so notes saved back then live under
    // `default::<contact>`. When the modern id yields no notes, look there once
    // before concluding the conversation has none. Scoped to that one TPA and
    // probed at most once per conversation per session — an empty query still
    // bills a read, and every other provider's empty conversations would
    // otherwise pay it on each open and scroll.
    const [senderHalf, contactHalf] = convId.split('::');
    if (
      !contactHalf || !senderHalf || senderHalf === 'default' ||
      useAppStore.getState().appCodeTPA !== 'routemobilewa'
    ) {
      return notes;
    }

    const legacyPath = getInboxConvDocPath(`default::${contactHalf}`);
    if (!legacyPath) return notes;

    const legacyRef = collection(db, legacyPath, 'messages');
    // Sorted, for the reason spelled out above the modern read: this
    // subcollection is not notes-only. storeSentMessageMeta wrote one doc per
    // outbound send here for as long as ids were minted as `default::`, those
    // ids are provider request ids, and `ua-note-…` sorts after all of them —
    // so an unordered page of 100 on a busy thread is all meta docs and no
    // notes. Both kinds carry `_uaCreatedTime` (the CF stamps it on every doc
    // it writes); the bare read stays as a fallback for anything that lacks it.
    let legacySnap = await getDocs(
      query(legacyRef, orderBy('_uaCreatedTime', 'desc'), limit(NOTES_FETCH_LIMIT))
    );
    if (legacySnap.empty) {
      legacySnap = await getDocs(query(legacyRef, limit(NOTES_FETCH_LIMIT)));
    }
    const legacyNotes = parseSnapshot(legacySnap, legacyPath);

    // Spend the probe only on a conversation that genuinely has nothing there.
    // Marking it after a successful read would hand the notes back once and
    // hide them on every later open — the same disappearing act this whole
    // block exists to stop. A conversation that does have legacy notes pays a
    // second read each time, which is what returning them costs anyway.
    //
    // A full page is not evidence of absence either: sorting makes truncation
    // rare, not impossible, and caching a truncated zero would retire the probe
    // for the session. Only a short page proves the conversation is empty.
    if (legacyNotes.length === 0 && legacySnap.size < NOTES_FETCH_LIMIT) {
      legacyNotesProbed.add(convDocPath);
    }

    // The legacy query carries no date bound — applying the caller's window to
    // a collection whose ids and clock predate the modern path is more trouble
    // than filtering the handful of parsed notes here. Callers that asked for a
    // slice must not be handed notes from outside it: mergeNotesIntoMessages
    // dedupes by id but never checks the time.
    const inWindow = legacyNotes.filter((n) => {
      const t = n.timestamp.getTime();
      if (window?.since && t < window.since) return false;
      if (window?.until && t >= window.until) return false;
      return true;
    });

    const seen = new Set(notes.map((n) => n.id));
    return [...notes, ...inWindow.filter((n) => !seen.has(n.id))];
  } catch (err) {
    console.warn('[Notes] read failed:', err);
    return [];
  }
}

/**
 * Lazy-fetch inbox details (label, assignee) for a list of conversations from Firebase,
 * then update the store with the resolved data.
 */
// ── Lazy message author fetching (viewport-triggered) ──────────────────
//
// ChatMessage components call `requestMessageAuthor(messageId)` when they
// enter the viewport via IntersectionObserver. Requests are cached and
// batched: we wait 100ms to collect more IDs, then fire a single
// `where(__name__, 'in', [...30])` query per batch.
//
// Results are cached indefinitely in the session — the same message won't
// be re-fetched when the user scrolls away and back. Fires at most once
// per unique message ID.

interface MessageAuthor {
  name: string;
  email?: string;
  pic?: string;
  uid?: string;
  /** Raw source type, e.g. 'uaapp' (an agent) or 'uaapp-workflow' (automation).
   *  Kept so the bubble can render a Bot avatar for machine-sent messages. */
  type?: string;
}

// Cache: null = confirmed no author (don't retry), undefined = not fetched
const messageAuthorCache = new Map<string, MessageAuthor | null>();

/**
 * Pull the UA author off a provider message doc. The old app stored it under
 * `source` (flagged with itsMsgSource), `author`, or `_uaUser` depending on the
 * writer, so all three are checked. Returns null for docs that carry none —
 * inbound messages, or outbound ones whose `source` hasn't been written yet.
 */
function extractMessageAuthor(data: Record<string, any>): MessageAuthor | null { // eslint-disable-line @typescript-eslint/no-explicit-any
  if (data._uaOriginService === 'ua-msg-system') return null;
  if (data.type?.includes('--note')) return null;

  const authorObj = data.source?.itsMsgSource ? data.source : (data.author || data._uaUser);
  if (!authorObj) return null;
  return {
    name: authorObj.name || authorObj.email || '',
    email: authorObj.email,
    pic: authorObj.pic,
    uid: authorObj.uaAppSaaSUserId || authorObj.uid,
    type: authorObj.type,
  };
}

/**
 * Stamp authors already known from `messageAuthorCache` onto a message list, so
 * they render with the first paint instead of after the viewport-triggered
 * lookup. Leaves messages that already carry an author untouched.
 */
function applyCachedAuthors(messages: Message[]): Message[] {
  if (messageAuthorCache.size === 0) return messages;
  return messages.map((m) => {
    if ((m as Message & { author?: MessageAuthor }).author) return m;
    const cached = messageAuthorCache.get(m.id);
    return cached ? ({ ...m, author: cached } as Message) : m;
  });
}
// Per-conversation pending queue + timer
const pendingAuthorFetches = new Map<
  string /* convDocPath */,
  { ids: Set<string>; timer: ReturnType<typeof setTimeout> | null }
>();

async function flushAuthorFetches(
  convDocPath: string,
  set: (partial: Partial<ConversationStore>) => void,
  get: () => ConversationStore
): Promise<void> {
  const pending = pendingAuthorFetches.get(convDocPath);
  if (!pending || pending.ids.size === 0) return;

  const idsToFetch = Array.from(pending.ids).filter((id) => !messageAuthorCache.has(id));
  pending.ids.clear();
  pending.timer = null;
  if (idsToFetch.length === 0) return;

  try {
    const msgsRef = collection(db, convDocPath, 'messages');
    // Firestore 'in' operator caps at 30 values — batch in chunks of 30
    const batches: string[][] = [];
    for (let i = 0; i < idsToFetch.length; i += 30) {
      batches.push(idsToFetch.slice(i, i + 30));
    }

    const snapshots = await Promise.all(
      batches.map(async (batchIds) => {
        const q = query(msgsRef, where('__name__', 'in', batchIds));
        return getDocs(q);
      })
    );

    const fetchedAuthors = new Map<string, MessageAuthor>();
    for (const snap of snapshots) {
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        if (data._uaOriginService === 'ua-msg-system') return;
        if (data.type?.includes('--note')) return;

        const author = extractMessageAuthor(data);
        if (author) fetchedAuthors.set(docSnap.id, author);
      });
    }

    // Cache all requested IDs — even ones with no author result, to prevent retries
    for (const id of idsToFetch) {
      messageAuthorCache.set(id, fetchedAuthors.get(id) ?? null);
    }

    if (fetchedAuthors.size === 0) return;

    // Apply to currently rendered messages
    const currentMessages = get().messages;
    if (currentMessages.length === 0) return;

    let changed = false;
    const updated = currentMessages.map((m) => {
      const author = fetchedAuthors.get(m.id);
      if (!author) return m;
      const mWithAuthor = m as Message & { author?: MessageAuthor };
      if (mWithAuthor.author) return m;
      changed = true;
      return { ...m, author } as Message;
    });

    if (changed) {
      set({ messages: updated });
      console.log(`[ConvStore] Lazy author fetch: ${fetchedAuthors.size}/${idsToFetch.length} matched`);
    }
  } catch (err) {
    console.warn('[ConvStore] Lazy author fetch failed:', err);
    // Don't cache failures — allow retry on next scroll
  }
}

async function fetchInboxDetailsForConversations(
  conversations: Conversation[],
  set: (partial: Partial<ConversationStore>) => void,
  get: () => ConversationStore
): Promise<void> {
  // The reminder field on the inbox doc is dynamic: __userNextReminder_{uid}
  const currentUserUid = useAuthStore.getState().user?.uid;
  const effectiveUid = getEffectiveUid();
  const reminderFieldKey = `__userNextReminder_${currentUserUid || effectiveUid}`;
  const draftFieldKey = `messageDraft_${currentUserUid || effectiveUid}`;

  const updates: Array<{ id: string; label?: string; assigneeEmail?: string; pinnedMessageId?: string; pinnedMessageData?: Record<string, unknown>; hasReminder?: boolean; reminderEventId?: string; reminderTime?: number; reminderSubject?: string; reminderCreatedTime?: number; crmRecordName?: string; crmRecord?: { appCode: string; entityId: string; module: string; recordName?: string }; callPermission?: Record<string, unknown>; lastMessageType?: string; noteBody?: string; subject?: string; activeFlow?: ActiveFlowInfo | null; optOut?: ConversationOptOut | null; unreadCount?: number; markedUnread?: boolean }> = [];
  const draftUpdates: Record<string, string> = {};

  // Build the base path for reminder tasks:
  // ulgebraUsers/{uid}/extensions/{tpaBase}forpage/UA_INBOX_TASKS/{userUid}/REMINDERS_TASKS
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const tpaBase = extensionName?.includes('for') ? extensionName.split('for')[0] : extensionName;
  const reminderUserUid = currentUserUid || effectiveUid;

  // Resolve the current adapter's enrichment-allowed module set once
  // per call. Used to decide whether a legacy `saas_record` stamp can
  // be salvaged for display without triggering a fresh enrichment write.
  const _saasModule = useModuleStore.getState().saasModule;
  const _userPins = useSettingsStore.getState().preferences.inboxRecordEnrichmentModules;
  const currentEnrichmentModules: string[] | null = _saasModule
    ? getCurrentEnrichmentModules(_saasModule, _userPins)
    : null;

  // Extract inbox details (label, assignee, reminder) for each conversation.
  // If the TPA module stashed raw Firestore data on metadata._rawInboxDoc
  // (WhatsApp does this), use it directly to avoid re-fetching each doc.
  await Promise.allSettled(
    conversations.map(async (conv) => {
      // Use cached raw data if available (saves 1 getDoc per conversation)
      const rawCached = conv.metadata?._rawInboxDoc as Record<string, unknown> | undefined;
      if (rawCached) {
        delete conv.metadata!._rawInboxDoc; // clean up — no longer needed
      }

      let data: Record<string, unknown> | null = rawCached ?? null;

      if (!data) {
        const path = getInboxConvDocPath(conv.id);
        if (!path) return;
        try {
          const snap = await getDoc(doc(db, path));
          data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
        } catch {
          return; // Skip failed fetches silently
        }

        // No inbox doc at all. Every Firestore-backed filter queries this
        // collection, so such a conversation can never appear under Un
        // Replied or Send Failed however it actually looks — the unfiltered
        // list renders it correctly straight from the Twilio API, then the
        // filter drops it, which reads as the filter being broken.
        // Reconciliation below can't help: it only runs once a doc exists.
        // Seed the filterable fields from API truth so the conversation
        // becomes reachable. Only for conversations that would genuinely
        // match a filter right now — seeding every docless conversation
        // would mean a write per row just to browse the inbox.
        if (!data && useAppStore.getState().appCodeTPA === 'twilio') {
          const apiDir = conv.metadata?.lastMessageDirection as string | undefined;
          const apiStatus = conv.metadata?.lastMessageStatus as string | undefined;
          const isInbound = apiDir === 'inbound';
          const isFailedSend = apiDir === 'outbound' && apiStatus === 'failed';
          if (isInbound || isFailedSend) {
            const ts = conv.lastMessageTime instanceof Date
              ? conv.lastMessageTime.getTime()
              : Number(conv.lastMessageTime) || Date.now();
            setDoc(
              doc(db, path),
              {
                lastMessageIsIncoming: isInbound,
                lastFailedMsgCount: isFailedSend ? 1 : 0,
                lastUpdatedTime: ts,
              },
              { merge: true }
            ).catch(() => {
              // Best-effort — retried on the next inbox load
            });
          }
        }
      }

      try {
        if (data) {
          // Reminder: __userNextReminder_{uid} = { id, time } when active, '' when cleared
          const reminderData = data[reminderFieldKey] as Record<string, unknown> | undefined;
          let hasReminder = !!(reminderData && typeof reminderData === 'object' && reminderData.time);
          let reminderSubject: string | undefined;
          let reminderCreatedTime: number | undefined;

          // Fetch reminder subject + createdTime from REMINDERS_TASKS/{eventId}
          if (hasReminder && reminderData && reminderData.id && effectiveUid && tpaBase && reminderUserUid) {
            try {
              const taskPath = `ulgebraUsers/${effectiveUid}/extensions/${tpaBase}forpage/UA_INBOX_TASKS/${reminderUserUid}/REMINDERS_TASKS/${reminderData.id}`;
              const taskSnap = await getDoc(doc(db, taskPath));
              if (taskSnap.exists()) {
                const taskData = taskSnap.data();
                // The pointer outlives the reminder: the server clears
                // __userNextReminder_{uid} on mark-as-read and on cancel/complete,
                // but NOT when a reminder fires. A fired-and-forgotten task leaves
                // the pointer behind, which used to read as "still has a reminder" —
                // the menu offered "View reminder" and the panel then had nothing
                // active to show. Trust the task's own status instead.
                if (taskData.status && taskData.status !== 'scheduled') {
                  hasReminder = false;
                } else {
                  reminderSubject = taskData.subject || taskData.text || undefined;
                  reminderCreatedTime = taskData.createdTime || undefined;
                }
              }
              // Doc missing is NOT treated as stale: the Firestore wrapper is
              // cache-first, so a never-seen task reads as absent on first open
              // and arrives later on the refresh bus.
            } catch {
              // Reminder task fetch failed — subject stays undefined
            }
          }

          // Read pinned message from inbox doc
          const pinnedMessage = data.pinnedMessage as Record<string, unknown> | undefined;
          let pinnedMessageId: string | undefined;
          let pinnedMessageData: Record<string, unknown> | undefined;
          if (pinnedMessage && typeof pinnedMessage === 'object' && Object.keys(pinnedMessage).length > 0) {
            // The pinned message ID can be in pinned.originalMsgId or the message's own sid/id
            const pinMeta = pinnedMessage.pinned as Record<string, unknown> | undefined;
            pinnedMessageId = (pinMeta?.originalMsgId as string) || (pinnedMessage.sid as string) || (pinnedMessage.id as string) || undefined;
            pinnedMessageData = pinnedMessage;
          }

          // Read draft message for this user
          const draftText = data[draftFieldKey];
          if (draftText && typeof draftText === 'string' && draftText.trim()) {
            draftUpdates[conv.id] = draftText;
          }

          // ── Read CRM record info for inbox-row name resolution ─────
          // Display priority: subject (user-set chat name) > saas_contact
          // > saas_record / saas_record_{ext} *iff its module is in the
          // currently configured enrichment module set*. The third path
          // salvages legacy stamps that happen to point at a contact-shaped
          // module without triggering a fresh enrichment write — when the
          // module doesn't match (e.g. Ticket / Account / Order) we ignore
          // it and let the enrichment loop resolve a proper contact.
          let crmRecordName: string | undefined;
          let crmRecord: { appCode: string; entityId: string; module: string; recordName?: string } | undefined;

          const saasContact = data.saas_contact;
          if (saasContact && typeof saasContact === 'object') {
            const sc = saasContact as Record<string, unknown>;
            // Only `entityId` is required. `appCode` is allowed to be
            // missing or different from the current extension — fall
            // back to the active extensionName so downstream "open in
            // CRM" deep links still work.
            if (sc.entityId) {
              crmRecord = {
                appCode: (typeof sc.appCode === 'string' && sc.appCode) ? sc.appCode : (extensionName || ''),
                entityId: sc.entityId as string,
                module: (sc.module as string) || '',
                recordName: sc.recordName as string | undefined,
              };
              if (sc.recordName && typeof sc.recordName === 'string') {
                crmRecordName = sc.recordName;
              }
            }
          }

          // Salvage `saas_record` only when its module matches the
          // current enrichment-allowed set. Read-only — no Firestore
          // write fired here, so we never promote a Ticket/Account
          // stamp into saas_contact.
          if (!crmRecord) {
            const sr = (extensionName ? data[`saas_record_${extensionName}`] : undefined) || data.saas_record;
            if (sr && typeof sr === 'object') {
              const srObj = sr as Record<string, unknown>;
              const srModule = typeof srObj.module === 'string' ? srObj.module : '';
              if (srObj.entityId && srModule && currentEnrichmentModules && moduleInList(srModule, currentEnrichmentModules)) {
                crmRecord = {
                  appCode: (typeof srObj.appCode === 'string' && srObj.appCode) ? srObj.appCode : (extensionName || ''),
                  entityId: srObj.entityId as string,
                  module: srModule,
                  recordName: srObj.recordName as string | undefined,
                };
                if (typeof srObj.recordName === 'string') {
                  crmRecordName = srObj.recordName;
                }
              }
            }
          }

          // Filter out CRM names that are just phone numbers or system names
          if (crmRecordName) {
            const cleaned = crmRecordName.replace(/[\s\-+()]/g, '');
            if (/^\d+$/.test(cleaned)) {
              crmRecordName = undefined; // It's just a phone number, not a real name
            }
          }

          // Read call permission status (written by server on call_permission_reply webhook)
          const callPermission = data.callPermission as Record<string, unknown> | undefined;

          // Read message type from inbox doc (e.g. "text--note", "text", "template")
          const lastMessageType = (data.type as string) || undefined;

          // If last message is a note and conv.lastMessage is empty, use the note body
          const noteBody = lastMessageType?.includes('--note')
            ? ((data.text as string) || (data.body as string) || undefined)
            : lastMessageType === 'reaction'
            ? `Reacted '${(data.reaction as Record<string, unknown>)?.emoji || '😀'}'`
            : undefined;

          // Read subject (chat name set by user)
          const subject = data.subject && typeof data.subject === 'string' ? data.subject : undefined;

          // Relative freshness of the inbox doc vs the conversation row —
          // both unread derivation and reconciliation below must only
          // trust whichever side is at least as new as the other. 60s
          // tolerance absorbs webhook-write vs message-timestamp jitter.
          const docUpdatedMs = typeof data.lastUpdatedTime === 'number' ? data.lastUpdatedTime : 0;
          const convMsgMs = conv.lastMessageTime instanceof Date
            ? conv.lastMessageTime.getTime()
            : Number(conv.lastMessageTime) || 0;
          const FRESHNESS_TOLERANCE_MS = 60_000;

          // ── Unread count from the inbox doc ────────────────────────
          // REST TPAs (Twilio & co.) have no unread info in their API —
          // the inbox doc carries `messagesCount` plus the per-user read
          // watermark `__userLastReadCount_{uid}`. Mirror the WhatsApp
          // converter's math. Guards:
          // - WhatsApp is skipped entirely: its converter + onSnapshot
          //   refresh already maintain live counts, and on the resync
          //   path `rawCached` is a load-time snapshot that would
          //   overwrite them with stale values.
          // - The doc must be at least as fresh as the conversation row —
          //   a doc lagging behind (webhook CF still processing the
          //   message that created this row) would zero a just-set badge.
          // - Skip the currently-open conversation so a just-clicked chat
          //   doesn't get its locally-zeroed badge back before the
          //   markAsRead CF lands on the doc.
          let unreadCount: number | undefined;
          const messagesCount = typeof data.messagesCount === 'number' ? data.messagesCount : 0;
          if (
            // Twilio only, like the two blocks below. This read `!== 'whatsapp'`,
            // which let it run for all ~35 other providers and overwrite the
            // unread count their own modules supply. Intercom and RingCentral
            // report genuine provider-side read state, and RingCentral is the
            // worst case: its read watermark is deliberately never written
            // (markMessageAsReadInConversation returns early for it), so
            // `lastRead` is always undefined and the fallback below reported
            // the entire thread as unread.
            useAppStore.getState().appCodeTPA === 'twilio' &&
            !rawCached &&
            messagesCount > 0 &&
            docUpdatedMs >= convMsgMs - FRESHNESS_TOLERANCE_MS &&
            get().activeConversation?.id !== conv.id
          ) {
            // Same math as the WhatsApp converter, plus the manual
            // "Mark as unread" flag (see computeInboxDocUnreadCount).
            unreadCount = computeInboxDocUnreadCount(data, currentUserUid || effectiveUid || undefined);
          }

          // ── Reconcile filterable fields against API truth (Twilio) ──
          // metadata.lastMessageDirection / lastMessageStatus come from
          // the Twilio API conversion, so they are ground truth — but
          // only when the row is at least as fresh as the doc. The
          // unfiltered list can be served from the SWR cache (7-day TTL),
          // and stale metadata must never clobber newer webhook writes
          // (e.g. an inbound that set lastMessageIsIncoming: true after
          // the cache entry was written). Older inbox docs predate the
          // webhook writers and lack `lastMessageIsIncoming` /
          // `lastFailedMsgCount`, so the Firestore-backed Un Replied and
          // Send Failed filters miss them. Patch the doc whenever it
          // disagrees — idempotent 0/1 writes.
          if (
            useAppStore.getState().appCodeTPA === 'twilio' &&
            convMsgMs >= docUpdatedMs - FRESHNESS_TOLERANCE_MS
          ) {
            const apiDir = conv.metadata?.lastMessageDirection as string | undefined;
            const apiStatus = conv.metadata?.lastMessageStatus as string | undefined;
            const patch: Record<string, unknown> = {};
            if (apiDir === 'inbound' && data.lastMessageIsIncoming !== true) {
              patch.lastMessageIsIncoming = true;
            } else if (apiDir === 'outbound') {
              if (data.lastMessageIsIncoming !== false) patch.lastMessageIsIncoming = false;
              const docFailed = Number(data.lastFailedMsgCount) > 0;
              if (apiStatus === 'failed' && !docFailed) patch.lastFailedMsgCount = 1;
              if (apiStatus && apiStatus !== 'failed' && docFailed) patch.lastFailedMsgCount = 0;
            }
            // Backfill a missing `lastUpdatedTime`. Every filter query ends
            // with orderBy('lastUpdatedTime'), and Firestore silently drops
            // documents that lack the orderBy field — so a legacy doc can
            // hold a perfectly correct `lastMessageIsIncoming: true` and
            // still never appear under Un Replied. Seed it from the
            // conversation's own last-message time (API truth).
            if (docUpdatedMs === 0 && convMsgMs > 0) {
              patch.lastUpdatedTime = convMsgMs;
            }
            if (Object.keys(patch).length > 0) {
              const docPath = getInboxConvDocPath(conv.id);
              if (docPath) {
                setDoc(doc(db, docPath), patch, { merge: true }).catch(() => {
                  // Best-effort — filter heals on a later pass
                });
              }
            }
          }

          // ── Migration: ensure assigneeEmail and label fields exist (remove after 2026-05-04) ──
          const MIGRATION_EXPIRY = new Date('2026-05-04').getTime();
          if (Date.now() < MIGRATION_EXPIRY) {
            const needsMigration: Record<string, null> = {};
            if (!('assigneeEmail' in data)) needsMigration.assigneeEmail = null;
            if (!('label' in data)) needsMigration.label = null;
            if (Object.keys(needsMigration).length > 0) {
              const docPath = getInboxConvDocPath(conv.id);
              if (docPath) {
                updateDoc(doc(db, docPath), needsMigration).catch(() => {
                  // Silent — migration is best-effort
                });
              }
            }
          }

          updates.push({
            id: conv.id,
            label: (data.label as string) || undefined,
            assigneeEmail: (data.assigneeEmail as string) || undefined,
            // REST TPAs (Twilio & co.) return conversations with unreadCount 0
            // — the API has no read state. The inbox doc is the source of
            // truth, so derive it here or the badge vanishes on page refresh
            // (KAN-70).
            //
            // Skip when the row came with `_rawInboxDoc`: its converter
            // already derived both fields from that very doc, and the doc is
            // a cache-first load-time snapshot. Re-applying it here — after
            // the awaits above — lands AFTER the server refresh that just
            // corrected the row (e.g. the chat was opened elsewhere, or the
            // "Mark as unread" flag was cleared), resurrecting the stale
            // badge/dot until the next reload.
            ...(!rawCached && {
              unreadCount: computeInboxDocUnreadCount(data, currentUserUid || effectiveUid || undefined),
              // Display hint only — the badge itself is still gated on
              // unreadCount, so syncing this from the doc is always safe.
              markedUnread: isInboxDocMarkedUnread(data, currentUserUid || effectiveUid || undefined),
            }),
            pinnedMessageId,
            pinnedMessageData,
            hasReminder,
            reminderEventId: hasReminder ? (reminderData?.id as string) : undefined,
            reminderTime: hasReminder ? (reminderData?.time as number) : undefined,
            reminderSubject,
            reminderCreatedTime,
            crmRecordName,
            crmRecord,
            callPermission,
            lastMessageType,
            noteBody,
            subject,
            activeFlow: parseActiveFlow(data._activeFlow) ?? null,
            optOut: parseOptOut(data.optOut) ?? null,
          });
        }
      } catch {
        // Skip failed fetches silently
      }
    })
  );

  // Apply updates to conversations in the store
  const current = get().conversations;
  const activeConvId = get().activeConversation?.id;
  const nowMs = Date.now();
  const updatedConvs = current.map((c) => {
    const update = updates.find((u) => u.id === c.id);
    if (update) {
      // Doc-derived unread only ever raises the badge here: zeroing stays
      // with markAsRead/realtime sync. Skip the open conversation and any
      // conv whose read-CF fired within the debounce window — the server
      // may not have stamped __userLastReadCount_{uid} yet, and applying
      // the stale doc delta would resurrect a badge the user just cleared.
      const recentlyMarkedRead = nowMs - (_lastReadCallAt.get(c.id) ?? 0) < 15000;
      // Mirror for "Mark as unread": the local badge of 1 is ahead of the
      // doc until that CF lands, so a lower doc-derived count is stale.
      const recentlyMarkedUnread = nowMs - (_lastUnreadMarkAt.get(c.id) ?? 0) < READ_STATE_SETTLE_MS;
      const holdLocalUnread =
        recentlyMarkedUnread &&
        update.unreadCount !== undefined &&
        update.unreadCount < (c.unreadCount || 0);
      // One gate for both doc-derived read-state fields. The open
      // conversation and anything read within the settle window keep their
      // local state — the doc this pass read is cache-first and may predate
      // the read CF, so applying it would put the badge/dot back on a chat
      // the user just opened.
      const applyDocReadState =
        c.id !== activeConvId && !recentlyMarkedRead && !holdLocalUnread;
      return {
        ...c,
        ...(applyDocReadState && update.unreadCount !== undefined && { unreadCount: update.unreadCount }),
        // A doc flagged unread turns the row into a dot (a fresh refetch row
        // has no local flag, and the badge above may already be 1 from the
        // same doc). Clearing is markAsRead's job — never set false here.
        ...(applyDocReadState && update.markedUnread === true && { markedUnread: true }),
        label: update.label,
        assigneeEmail: update.assigneeEmail,
        activeFlow: update.activeFlow ?? undefined,
        optOut: update.optOut ?? undefined,
        ...(update.subject && { contactName: update.subject }),
        ...(update.crmRecordName && { crmRecordName: update.crmRecordName }),
        ...(update.crmRecord && { crmRecord: update.crmRecord }),
        ...(update.pinnedMessageId && { pinnedMessageId: update.pinnedMessageId }),
        ...(update.noteBody && (!c.lastMessage || update.lastMessageType === 'reaction') && { lastMessage: update.noteBody }),
        metadata: {
          ...c.metadata,
          ...(update.hasReminder && { hasReminder: true, reminderEventId: update.reminderEventId, reminderTime: update.reminderTime, reminderSubject: update.reminderSubject, reminderCreatedTime: update.reminderCreatedTime }),
          ...(update.pinnedMessageData && { pinnedMessageData: update.pinnedMessageData }),
          ...(update.callPermission && { callPermission: update.callPermission }),
          ...(update.lastMessageType && { lastMessageType: update.lastMessageType }),
        },
      };
    }
    return c;
  });
  set({ conversations: updatedConvs });

  // Keep the open conversation's flow-lock + opt-out lock in sync (banner + composer lock).
  const _activeConv = get().activeConversation;
  if (_activeConv) {
    const _au = updates.find((u) => u.id === _activeConv.id);
    if (_au) set({ activeConversation: { ..._activeConv, activeFlow: _au.activeFlow ?? undefined, optOut: _au.optOut ?? undefined } });
  }

  // Apply fetched drafts to the drafts map (merge with any local changes)
  if (Object.keys(draftUpdates).length > 0) {
    const existingDrafts = get().drafts;
    set({ drafts: { ...draftUpdates, ...existingDrafts } });
  }

  // ── Background CRM record enrichment ────────────────────────────────
  // For conversations that came back without a linked CRM record (no
  // saas_record / latestExistingRecordName in the inbox doc), kick off a
  // phone-based lookup against the connected SaaS module and persist the
  // first match via UA_INBOX_UPDATE_CONV_EXISTING_RECORD. The next time
  // the inbox loads, the record name will be cached on the doc and this
  // pass becomes a no-op for that conversation.
  //
  // Both WhatsApp (eager Firestore inbox load) and Twilio / other REST
  // TPAs (lazy detail fetch) funnel through this function, so a single
  // hook here covers all surfaces.
  //
  // Label / assignee filter changes (setFilterLabel, setFilterAgent) and
  // pagination (loadMoreConversations) also re-enter through
  // fetchConversations → here, so enrichment runs naturally on those
  // paths without a dedicated call site.
  const candidatesForEnrichment = updatedConvs.filter(
    (c) => !c.crmRecord && c.contactPhone
  );
  if (candidatesForEnrichment.length > 0) {
    void enrichConversationsWithCRMRecord(candidatesForEnrichment, set, get);
  }
}

/**
 * Modules that the conversation-list enrichment should NEVER search by
 * default — Tickets are noisy (every customer-support interaction creates
 * one) and Accounts / Companies / Organizations are entity-level rather
 * than person-level, so the matched name is rarely the right human-facing
 * label for a 1:1 conversation. Users can still pin these via the inbox
 * setting if they really want to.
 *
 * Lower-cased for case-insensitive comparison against module values that
 * different SaaS adapters use (e.g. HubSpot "Companies" vs Bitrix24
 * "company" vs Zoho "Accounts").
 */
export const ENRICHMENT_DEFAULT_EXCLUDED_MODULES = new Set([
  'ticket',
  'tickets',
  'account',
  'accounts',
  'company',
  'companies',
  'organization',
  'organizations',
]);

/**
 * In-flight enrichment lookups, keyed by `{saasCode}:{orgId}:{phoneDigits}:{modulesKey}`.
 * Concurrent calls for the same phone (e.g. a label-filter change firing
 * while the initial fetchConversations enrichment is still in flight) share
 * one promise instead of double-fetching the CRM and racing the
 * UA_INBOX_UPDATE_CONV_EXISTING_RECORD CF.
 */
const inFlightEnrichments = new Map<
  string,
  Promise<{ module: string; entityId: string; displayName: string } | null>
>();

/** Modules-list cap for the conversation-list enrichment. */
export const ENRICHMENT_MAX_MODULES = 2;

/**
 * Per-adapter cap on how many modules conversation enrichment can
 * search. Monday's `search.items` accepts an arbitrary `board_ids: []`
 * array in a single call, so the user can pin any number of boards
 * without paying per-board API cost. Every other adapter pays one
 * call per module, so the 2-cap stays.
 */
export function getEnrichmentModulesCap(saasModule: { id: string } | null | undefined): number {
  if (saasModule?.id === 'monday') return Number.POSITIVE_INFINITY;
  return ENRICHMENT_MAX_MODULES;
}

/**
 * Per-SaaS-adapter default module list for the conversation-list enrichment.
 * Keys are `SaaSModule.id`. When the user has not pinned modules via
 * `preferences.inboxRecordEnrichmentModules`, this map is consulted first;
 * adapters not listed here fall through to the historic exclude-list logic
 * in {@link resolveDefaultEnrichmentModules}.
 *
 * Choices follow the product-intent map ("conversation enrichment should
 * resolve to a person-shaped record by default, not a ticket / account /
 * org"). Override per-user via Inbox View Settings.
 *
 * An empty array opts the adapter out of enrichment entirely (the resolver
 * returns `null`, mirroring Monday's "needs user pin" behavior).
 */
export const ENRICHMENT_DEFAULT_MODULES_BY_ID: Record<string, string[]> = {
  hubspotcrm:     ['Contacts'],
  bitrix24:       ['contacts', 'leads'],
  freshdesk:      ['contacts'],
  pipedrive:      ['persons'],
  salesforce:     ['Contact', 'Lead'],
  zohocrm:        ['Contacts', 'Leads'],
  zohodesk:       ['contacts'],
  zohobigin:      ['Contacts'],
  zohorecruit:    ['Contacts', 'Candidates'],
  freshworkscrm:  ['contact', 'lead'],
  activecampaign: ['contact', 'account'],
  dynamics:       ['Contacts', 'Leads'],
  leadsquared:    ['Leads'],
  woocommerce:    ['Customers'],
  shopify:        ['Customers'],
  airtable:       ['Records'],
  odoo:           ['Contacts', 'Leads'],
  zendesksupport: ['Users'],
  zohobooks:      ['contact'],
  zohocampaigns:  ['contacts'],
  // Explicit opt-outs: no sensible person-shaped record to enrich with.
  googlesheets:   [],
  spreadsheet:    [],
  uaweb:          [],
};

/**
 * Resolve the modules that the conversation-list enrichment WOULD use
 * by default for a given SaaS module — i.e. the same logic
 * `enrichConversationsWithCRMRecord` uses when no `inboxRecordEnrichmentModules`
 * is pinned. Returns `null` for Monday (no sensible default — boards are
 * user-specific). Exported so the Inbox View Settings UI can preview
 * the defaults in the slot dropdowns.
 */
/**
 * Resolve the effective enrichment module list for the current session —
 * either the user-pinned set (capped at ENRICHMENT_MAX_MODULES) or the
 * per-adapter default. Returns `null` when the adapter requires an
 * explicit user pin (Monday) and the user has not pinned anything.
 *
 * Used by both `enrichConversationsWithCRMRecord` (to fan out the API
 * call) and the inbox-doc reader (to salvage an existing `saas_record`
 * into in-memory `crmRecord` when its module is in the same allowed set,
 * without triggering a Firestore write).
 */
export function getCurrentEnrichmentModules(
  saasModule: { id: string; getSupportedModules: () => Array<{ value: string; label: string }> },
  userModules: string[] | undefined,
): string[] | null {
  if (userModules && userModules.length > 0) {
    const cap = getEnrichmentModulesCap(saasModule);
    return Number.isFinite(cap) ? userModules.slice(0, cap) : [...userModules];
  }
  return resolveDefaultEnrichmentModules(saasModule);
}

/**
 * Case- and plural-insensitive membership check for a module value
 * against a list. SaaS adapters report modules in inconsistent shapes
 * (e.g. HubSpot "Contacts", Bitrix24 "contacts", Salesforce "Contact"),
 * so direct equality misses obvious matches.
 */
function moduleInList(module: string, allowed: string[]): boolean {
  if (!module) return false;
  const target = module.toLowerCase();
  const targetSingular = target.endsWith('s') ? target.slice(0, -1) : target;
  for (const a of allowed) {
    const lower = a.toLowerCase();
    const singular = lower.endsWith('s') ? lower.slice(0, -1) : lower;
    if (lower === target || singular === targetSingular) return true;
  }
  return false;
}

export function resolveDefaultEnrichmentModules(
  saasModule: { id: string; getSupportedModules: () => Array<{ value: string; label: string }> }
): string[] | null {
  if (saasModule.id === 'monday') return null;

  // Per-adapter explicit defaults take priority. An empty array is an
  // explicit opt-out (treat the same as Monday's "needs user pin").
  const explicit = ENRICHMENT_DEFAULT_MODULES_BY_ID[saasModule.id];
  if (explicit !== undefined) {
    return explicit.length === 0 ? null : explicit.slice(0, ENRICHMENT_MAX_MODULES);
  }

  // Fallback for adapters not enumerated above — historic behavior: take
  // every supported module minus tickets / accounts / companies / orgs.
  return saasModule
    .getSupportedModules()
    .map((m) => m.value)
    .filter((value) => {
      const normalized = value.toLowerCase();
      const singular = normalized.endsWith('s') ? normalized.slice(0, -1) : normalized;
      return !ENRICHMENT_DEFAULT_EXCLUDED_MODULES.has(normalized)
        && !ENRICHMENT_DEFAULT_EXCLUDED_MODULES.has(singular);
    })
    .slice(0, ENRICHMENT_MAX_MODULES);
}

/**
 * For each conversation missing a linked CRM record, search the connected
 * SaaS module by phone number and persist the first match via the
 * UA_INBOX_UPDATE_CONV_EXISTING_RECORD cloud function.
 *
 * Module-resolution priority:
 *   1. User-configured `inboxRecordEnrichmentModules` (capped at 2) —
 *      applies to every SaaS, including Monday (where values are board
 *      IDs).
 *   2. Default fallback — first 2 supported modules excluding Tickets /
 *      Accounts / Companies / Organizations. Monday is skipped entirely
 *      in this path (boards are user-specific so a sensible default
 *      doesn't exist).
 *
 * Local store is updated eagerly so the conversation list reflects the
 * resolved record name immediately, without waiting for the CF round-trip
 * or a subsequent inbox refresh.
 */
async function enrichConversationsWithCRMRecord(
  conversations: Conversation[],
  set: (partial: Partial<ConversationStore>) => void,
  get: () => ConversationStore
): Promise<void> {
  console.log(`[ConvEnrich] called with ${conversations.length} candidate convs`);

  // Skip in standalone TPA mode (no CRM connected) — searchAcrossModules
  // would have nothing to query against.
  const appStore = useAppStore.getState();
  const appCodeSAAS = appStore.appCodeSAAS;
  if (!appCodeSAAS || appCodeSAAS === 'page') {
    console.log('[ConvEnrich] skipped: no CRM connected (appCodeSAAS missing or "page")');
    return;
  }

  const { saasModule } = useModuleStore.getState();
  if (!saasModule) {
    console.log('[ConvEnrich] skipped: saasModule not yet initialized');
    return;
  }

  // ── Resolve modules-to-search (priority chain) ─────────────────────
  const userModules = useSettingsStore.getState().preferences.inboxRecordEnrichmentModules;
  const modulesToSearch = getCurrentEnrichmentModules(saasModule, userModules);
  if (modulesToSearch === null) {
    console.log('[ConvEnrich] skipped: Monday requires user to pin at least one board in Inbox View Settings');
    return;
  }
  if (modulesToSearch.length === 0) {
    console.log(`[ConvEnrich] skipped: no modules resolved for SaaS=${saasModule.id} (userModules=${JSON.stringify(userModules)})`);
    return;
  }
  console.log(`[ConvEnrich] modules=[${modulesToSearch.join(', ')}] (source=${userModules && userModules.length > 0 ? 'user-pinned' : 'default-fallback'})`);

  // Filter out conversations that already have a linked record or no
  // usable phone. Per-conversation deduplication is no longer needed
  // because the CRM search is now cached by phone (see below) — two
  // refreshes of the same list reuse the cached result, and two
  // conversations sharing a phone (e.g. SMS + WhatsApp from the same
  // person) only hit the CRM once.
  let rejectedHasRecord = 0;
  let rejectedNoPhone = 0;
  let rejectedShortDigits = 0;
  const candidates = conversations.filter((c) => {
    if (c.crmRecord) { rejectedHasRecord++; return false; }
    if (!c.contactPhone) { rejectedNoPhone++; return false; }
    // WhatsApp username chats are keyed by a Meta user ID ("US.1349…"), not a
    // phone — they are looked up by their dummy CRM email instead (processPhoneGroup).
    if (isWaUserId(c.contactPhone)) return true;
    // Reject obvious non-phone identifiers (e.g. "support@example.com" left
    // in contactPhone by some channels). 6+ digits — the shortest real
    // phone numbers (US short codes are 5-6 digits, international mobiles
    // are at least 7 incl. country code) clear this floor.
    const digits = c.contactPhone.replace(/\D/g, '');
    if (digits.length < 6) { rejectedShortDigits++; return false; }
    return true;
  });

  if (candidates.length === 0) {
    console.log(`[ConvEnrich] no eligible candidates — rejected: hasRecord=${rejectedHasRecord}, noPhone=${rejectedNoPhone}, shortDigits=${rejectedShortDigits}`);
    return;
  }
  console.log(`[ConvEnrich] ${candidates.length} eligible / ${conversations.length} total (rejected: hasRecord=${rejectedHasRecord}, noPhone=${rejectedNoPhone}, shortDigits=${rejectedShortDigits})`);

  const orgId = appStore.appConfig?.orgId || '';
  const modulesKey = modulesToSearch.join(',');

  const enrichments: Array<{
    id: string;
    module: string;
    entityId: string;
    displayName: string;
    /** True when the match came from a fresh CRM API call. Cache hits
     *  already persisted the saas_record to Firestore on a previous run,
     *  so we must not re-fire the CF for them — that would explain the
     *  UA_INBOX_UPDATE_CONV_EXISTING_RECORD spam every refresh. */
    fromFreshFetch: boolean;
  }> = [];

  // Group candidates by normalized phone so two conversations sharing a
  // number don't trigger two cache lookups / API calls.
  const byPhone = new Map<string, Conversation[]>();
  for (const conv of candidates) {
    const digits = toContactId(conv.contactPhone);
    const list = byPhone.get(digits) ?? [];
    list.push(conv);
    byPhone.set(digits, list);
  }
  const phoneGroups = Array.from(byPhone.entries());

  // Mark all candidate conversations as "enriching" so the conv-list row
  // can pulse the contact display while the lookup runs. Cleared per
  // phone-group as each finishes.
  {
    const current = new Set(get().enrichingConvIds);
    let mutated = false;
    for (const conv of candidates) {
      if (!current.has(conv.id)) {
        current.add(conv.id);
        mutated = true;
      }
    }
    if (mutated) set({ enrichingConvIds: current });
  }
  const clearEnrichingFor = (ids: string[]) => {
    const current = new Set(get().enrichingConvIds);
    let mutated = false;
    for (const id of ids) {
      if (current.delete(id)) mutated = true;
    }
    if (mutated) set({ enrichingConvIds: current });
  };

  // Throttle to 3 concurrent CRM searches — Zoho / HubSpot / Pipedrive all
  // have per-user rate limits and several connections in flight at once is
  // already enough to saturate any single page-load.
  const tallies = { cacheHit: 0, freshFetch: 0, sharedInFlight: 0, cachedEmpty: 0, freshEmpty: 0, errored: 0 };
  const t0 = Date.now();
  console.log(`[ConvEnrich] starting lookups: ${phoneGroups.length} unique phone(s), modules=[${modulesToSearch.join(', ')}], concurrency=3`);
  await runWithConcurrency(phoneGroups, 3, async ([phoneDigits, convsForPhone]) => {
    const convIds = convsForPhone.map((c) => c.id);
    try {
      await processPhoneGroup(phoneDigits, convsForPhone, tallies);
    } finally {
      clearEnrichingFor(convIds);
    }
  });
  console.log(`[ConvEnrich] done in ${Date.now() - t0}ms — cacheHit=${tallies.cacheHit}, freshFetch=${tallies.freshFetch}, sharedInFlight=${tallies.sharedInFlight}, cachedEmpty=${tallies.cachedEmpty}, freshEmpty=${tallies.freshEmpty}, errored=${tallies.errored}, matched=${enrichments.length}`);

  async function processPhoneGroup(
    phoneDigits: string,
    convsForPhone: Conversation[],
    tallies: { cacheHit: number; freshFetch: number; sharedInFlight: number; cachedEmpty: number; freshEmpty: number; errored: number }
  ): Promise<void> {
    const cacheKey = cacheKeys.convEnrichment(appCodeSAAS, orgId, phoneDigits, modulesKey);

    // ── IDB cache: lean entry per phone-lookup attempt ──────────────
    // Stored fields:
    //   conversationId — the originating conv that triggered the first
    //                    lookup (informational; matches user request)
    //   phone          — normalized digits the lookup was keyed on
    //   recordId/Module/Name — the matched record, or null fields when
    //                    nothing matched
    //   fetchedAt      — ms timestamp of the API call
    //
    // Hits are kept in IDB for 7 days. Empty results (no record matched)
    // are honored only for 24h via the fetchedAt check below — after
    // that the entry counts as stale and we re-query in case the user
    // has since added the record to CRM.
    type Cached = {
      conversationId: string;
      phone: string;
      recordId: string | null;
      recordModule: string | null;
      recordName: string | null;
      fetchedAt: number;
    };
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    const SEVEN_DAYS_MS = 7 * ONE_DAY_MS;

    // ── Authoritative pre-check: read the inbox doc directly ──────────
    // The in-memory `c.crmRecord` is built from `saas_contact` (and
    // optionally a salvageable `saas_record`), but multiple race windows
    // (initial paint before inbox-detail fetch lands; realtime push
    // arriving stale; cache layers serving pre-stamp data) can leave it
    // undefined even when Firestore actually has `saas_contact` set.
    // Cost is one tiny doc read per phone-group — much cheaper than
    // firing a CRM API call we'll just discard.
    try {
      const docPath = getInboxConvDocPath(convsForPhone[0].id);
      if (docPath) {
        const snap = await getDoc(doc(db, docPath));
        if (snap.exists()) {
          const d = snap.data() as Record<string, unknown>;
          const sc = d.saas_contact as Record<string, unknown> | undefined;
          if (sc && typeof sc === 'object' && sc.entityId) {
            const scMatch = {
              module: typeof sc.module === 'string' ? sc.module : '',
              entityId: sc.entityId as string,
              displayName: typeof sc.recordName === 'string' ? sc.recordName : '',
            };
            // Apply in-memory to every conv in the group (skip the
            // fresh-fetch CF call — saas_contact is already stored).
            for (const conv of convsForPhone) {
              enrichments.push({
                id: conv.id,
                module: scMatch.module,
                entityId: scMatch.entityId,
                displayName: scMatch.displayName,
                fromFreshFetch: false,
              });
            }
            // Refresh IDB cache so future passes don't re-read the doc.
            const entry: Cached = {
              conversationId: convsForPhone[0].id,
              phone: phoneDigits,
              recordId: scMatch.entityId,
              recordModule: scMatch.module,
              recordName: scMatch.displayName,
              fetchedAt: Date.now(),
            };
            await idbCache.set('org_data', cacheKey, entry, SEVEN_DAYS_MS).catch(() => {});
            tallies.cacheHit++;
            console.log(`[ConvEnrich] inbox-doc saas_contact found for phone=${phoneDigits} — skipping CRM search`);
            return;
          }
        }
      }
    } catch (e) {
      // Non-fatal — fall through to the normal cache/search path.
      console.warn('[ConvEnrich] inbox-doc pre-check failed:', e);
    }

    const cached = await idbCache.get<Cached>('org_data', cacheKey);

    let match: { module: string; entityId: string; displayName: string } | null = null;
    let useCached = false;
    if (cached) {
      if (cached.recordId && cached.recordModule && cached.recordName) {
        // Cached hit — reuse without re-querying.
        match = {
          module: cached.recordModule,
          entityId: cached.recordId,
          displayName: cached.recordName,
        };
        useCached = true;
        tallies.cacheHit++;
      } else if (Date.now() - cached.fetchedAt < ONE_DAY_MS) {
        // Cached "no match" within the last 24h — skip until tomorrow.
        tallies.cachedEmpty++;
        return;
      }
      // Otherwise: cached entry is a stale empty — fall through to refetch.
    }

    let isInFlightOwner = false;
    if (!useCached) {
      // In-flight dedupe: if another enrichConversationsWithCRMRecord pass
      // (e.g. filter change firing while initial fetch is mid-flight) is
      // already searching this phone+modules combo, await its promise
      // instead of double-querying the CRM. Only the original caller fires
      // the CF below — piggybackers treat the result like a cache hit.
      const existing = inFlightEnrichments.get(cacheKey);
      if (existing) {
        match = await existing;
        tallies.sharedInFlight++;
      } else {
        isInFlightOwner = true;
        const fetchPromise = (async () => {
          try {
            // WhatsApp username chat: no phone — search by the dummy CRM email
            // the server syncs for that user ID. Everyone else: by phone.
            const waUserEmail = waUserIdToEmail(convsForPhone[0].contactPhone);
            const results = await saasModule.searchAcrossModules(
              // Pass the user-typed contact phone (not just digits) — some
              // SaaS phone search endpoints expect "+" / formatted numbers.
              waUserEmail || convsForPhone[0].contactPhone,
              waUserEmail ? 'email' : 'phone',
              { modules: modulesToSearch }
            );
            const first = results[0];
            const m = first && first.displayName
              ? { module: first.module, entityId: first.id, displayName: first.displayName }
              : null;
            // Cache the outcome (hit or empty). 7d IDB TTL keeps hits fresh
            // for a week; the 24h fetchedAt check above re-queries empties
            // sooner.
            const entry: Cached = {
              conversationId: convsForPhone[0].id,
              phone: phoneDigits,
              recordId: m?.entityId ?? null,
              recordModule: m?.module ?? null,
              recordName: m?.displayName ?? null,
              fetchedAt: Date.now(),
            };
            await idbCache.set('org_data', cacheKey, entry, SEVEN_DAYS_MS).catch(() => {});
            if (m) tallies.freshFetch++; else tallies.freshEmpty++;
            return m;
          } catch (err) {
            // Don't cache hard failures (auth / network) — they may
            // resolve on next attempt and we don't want to suppress retries
            // for a full day.
            tallies.errored++;
            console.warn(`[ConvEnrich] CRM search failed for phone=${phoneDigits}:`, err);
            return null;
          }
        })();
        inFlightEnrichments.set(cacheKey, fetchPromise);
        try {
          match = await fetchPromise;
        } finally {
          inFlightEnrichments.delete(cacheKey);
        }
      }
    }

    if (!match) return;
    // Apply the same match to every conversation that shares this phone.
    // `fromFreshFetch` distinguishes new API matches (need CF persist)
    // from cache hits (already persisted on a previous run).
    for (const conv of convsForPhone) {
      enrichments.push({
        id: conv.id,
        module: match.module,
        entityId: match.entityId,
        displayName: match.displayName,
        fromFreshFetch: !useCached && isInFlightOwner,
      });
    }
  }

  if (enrichments.length === 0) return;

  // Apply local store update immediately so the UI shows resolved names
  // without waiting for the CF round-trip + Firestore listener.
  const extensionName = useAppStore.getState().extensionName || '';
  const current = get().conversations;
  const updated = current.map((c) => {
    const enr = enrichments.find((e) => e.id === c.id);
    if (!enr) return c;
    return {
      ...c,
      crmRecordName: enr.displayName,
      crmRecord: {
        appCode: extensionName,
        entityId: enr.entityId,
        module: enr.module,
        recordName: enr.displayName,
      },
    };
  });
  set({ conversations: updated });

  // Persist matches to Firestore via UA_INBOX_UPDATE_CONV_SAAS_CONTACT
  // (writes to `saas_contact`, a dedicated field for the contact-shaped
  // record used in inbox-row display). We deliberately do NOT touch
  // `saas_record` here — that field may hold a user-linked Ticket /
  // Account / Order which must survive. Only the contact-shaped match
  // from auto-enrichment lives in `saas_contact`. If the per-adapter
  // enrichment defaults change later, all that's needed is a one-line
  // bump to `saas_contact` (or a Firestore wipe of that field) — the
  // user's manual saas_record linkages stay intact.
  //
  // Only fresh fetches persist; cache hits already wrote on their
  // original run. Fire-and-forget per record.
  const updateFn = get().updateExistingContactInConversation;
  for (const enr of enrichments) {
    if (!enr.fromFreshFetch) continue;
    updateFn(enr.id, enr.module, enr.entityId, enr.displayName).catch(() => {});
  }
}

/**
 * Fetch chat labels list from org settings.
 */
/** Standing live subscription to the org's chat-label doc, and the doc path it
 *  watches. The label SET (custom labels) is shared by every session in the org,
 *  so it must sync live — a label added by any admin/user should appear in
 *  everyone's dropdown, not only after the 24h IDB cache expires or a reload. */
let unsubscribeChatLabels: (() => void) | null = null;
let subscribedLabelsPath: string | null = null;

/** Merge the org's custom labels with the system defaults (defaults always
 *  present; a custom label that duplicates a system display name is dropped). */
function mergeChatLabels(
  customLabels: Record<string, ConvChatLabel> | undefined
): Record<string, ConvChatLabel> {
  const merged: Record<string, ConvChatLabel> = { ...DEFAULT_CHAT_LABELS };
  if (!customLabels) return merged;
  const systemDisplayNames = new Set(
    Object.values(DEFAULT_CHAT_LABELS).map((l) => l.display_label.toLowerCase())
  );
  Object.keys(customLabels).forEach((key) => {
    if (key in DEFAULT_CHAT_LABELS) { merged[key] = customLabels[key]; return; }
    if (systemDisplayNames.has(customLabels[key].display_label?.toLowerCase())) return;
    merged[key] = customLabels[key];
  });
  return merged;
}

/** (Re)point a live subscription at the org's chat-label doc so label additions
 *  by ANY session sync live into everyone's dropdown/settings. Idempotent while
 *  already watching the correct doc. */
function subscribeChatLabels(set: (partial: Partial<ConversationStore>) => void): void {
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;
  if (!extensionName || !orgId || !saasCode) return; // app context not ready yet
  const orgPath = `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}`;
  if (unsubscribeChatLabels && subscribedLabelsPath === orgPath) return;
  if (unsubscribeChatLabels) { unsubscribeChatLabels(); unsubscribeChatLabels = null; }
  subscribedLabelsPath = orgPath;
  set({ chatLabelsLoaded: false }); // new target doc — not loaded until its first snapshot
  const idbKey = `chat_labels_${extensionName}_${orgId}`;

  unsubscribeChatLabels = onSnapshot(
    doc(db, orgPath),
    (snap) => {
      // Skip our own not-yet-committed optimistic write echoing back.
      if (snap.metadata.hasPendingWrites) return;
      const data = snap.exists() ? snap.data() : {};
      const merged = mergeChatLabels(data.ua_conv_chat_labels_list as Record<string, ConvChatLabel> | undefined);
      // Deleted-label records, minus any key that is live again (re-created).
      const deletedChatLabels: Record<string, DeletedChatLabel> = {};
      for (const [k, v] of Object.entries((data[DELETED_LABELS_FIELD] || {}) as Record<string, DeletedChatLabel>)) {
        if (v && typeof v === 'object' && !Object.prototype.hasOwnProperty.call(merged, k)) deletedChatLabels[k] = v;
      }
      set({ chatLabels: merged, deletedChatLabels, chatLabelsLoaded: true });
      // Keep the IDB cache fresh for the next instant paint.
      idbCache.set('org_data', idbKey, merged, 24 * 60 * 60 * 1000).catch(() => {});
    },
    (err) => { console.error('[ConvStore] chat labels subscription error:', err); }
  );
}

async function fetchChatLabels(
  set: (partial: Partial<ConversationStore>) => void
): Promise<void> {
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;

  if (!extensionName || !orgId || !saasCode) return;

  const idbKey = `chat_labels_${extensionName}_${orgId}`;

  // Instant paint from the IDB cache (if any) while the live listener spins up.
  // NOTE: the cache is no longer authoritative — the onSnapshot listener below
  // provides the fresh value and keeps it in sync across sessions.
  const idbCached = await idbCache.get<Record<string, ConvChatLabel>>('org_data', idbKey);
  if (idbCached && Object.keys(idbCached).length > 0) {
    const systemDisplayNames = new Set(
      Object.values(DEFAULT_CHAT_LABELS).map((l) => l.display_label.toLowerCase())
    );
    const cleaned: Record<string, ConvChatLabel> = {};
    Object.entries(idbCached).forEach(([k, v]) => {
      if (k in DEFAULT_CHAT_LABELS || v.systemClass || !systemDisplayNames.has(v.display_label?.toLowerCase())) {
        cleaned[k] = v;
      }
    });
    set({ chatLabels: cleaned });
  }

  // Live subscription (replaces the previous one-shot read): a label added by
  // another user/admin now appears live, not only after the cache TTL/reload.
  subscribeChatLabels(set);
}

/**
 * Resolve media attachments for messages that have unresolved media.
 * Called after messages are set in the store — resolves Twilio media list URIs
 * into direct image URLs, then updates the store with NEW object references
 * to trigger React re-renders.
 */
async function resolveMediaInStore(
  messages: Message[],
  set: (partial: Partial<ConversationStore>) => void,
  get: () => ConversationStore
): Promise<void> {
  const needsResolve = messages.filter(
    (m) => m.metadata?.needsMediaResolve && m.metadata?.mediaListUri
  );
  if (needsResolve.length === 0) return;

  const tpaModule = useModuleStore.getState().tpaModule;
  if (!tpaModule) return;

  // Check if module has resolveMediaAttachments
  const mod = tpaModule as unknown as {
    resolveMediaAttachments(uri: string): Promise<MessageAttachment[]>;
  };
  if (typeof mod.resolveMediaAttachments !== 'function') return;

  // Process in batches of 10 to avoid overwhelming the API, loop until all resolved
  const BATCH_SIZE = 10;
  for (let offset = 0; offset < needsResolve.length; offset += BATCH_SIZE) {
    try {
      const batch = needsResolve.slice(offset, offset + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(async (msg) => {
          const uri = msg.metadata?.mediaListUri as string;
          const resolved = await mod.resolveMediaAttachments(uri);
          return { msgId: msg.id, resolved };
        })
      );

      // Build a map of resolved media
      const resolvedMap = new Map<string, MessageAttachment[]>();
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value.resolved.length > 0) {
          resolvedMap.set(r.value.msgId, r.value.resolved);
        }
      }

      if (resolvedMap.size > 0) {
        // Create NEW object references so Zustand detects the change → React re-renders
        const current = get().messages;
        const updated = current.map((m) => {
          const resolved = resolvedMap.get(m.id);
          if (!resolved) return m;
          return {
            ...m,
            attachments: resolved,
            mediaUrl: resolved[0].url,
            metadata: { ...m.metadata, needsMediaResolve: false },
          };
        });
        set({ messages: updated });
      }
    } catch {
      // Silently fail for this batch — placeholder attachments remain visible
    }
  }
}

/** True when `filterAgent` is a specific agent's email rather than a quick tab
 *  ('all' / 'mine' / 'unassigned'). Views narrowed this way must not have rows
 *  injected past the filter by realtime events. */
const isAgentEmailFilter = (filterAgent: string): boolean =>
  !!filterAgent && filterAgent !== 'all' && filterAgent !== 'mine' && filterAgent !== 'unassigned';

export const useConversationStore = create<ConversationStore>((set, get) => ({
  conversations: [],
  activeConversation: null,
  activeSender: null,
  messages: [],
  loading: false,
  loadingMore: false,
  messagesLoading: false,
  loadingOlderMessages: false,
  messagesHasMore: false,
  searchQuery: '',
  filterChannel: null,
  filterLabel: null,
  filterAgent: 'all',
  jumpBefore: null,
  error: null,
  nextCursor: null,
  hasMore: false,
  chatLabels: { ...DEFAULT_CHAT_LABELS },
  deletedChatLabels: {},
  chatLabelsLoaded: false,
  phoneSearchActive: false,
  realtimeMessageIds: new Set<string>(),
  enrichingConvIds: new Set<string>(),
  mentions: [],
  pastReminders: [],
  pastRemindersLoading: false,
  activeReminders: [],
  chatReminderConvId: null,
  chatReminderRows: [],
  chatRemindersLoading: false,
  chatRemindersFailed: false,
  activeRemindersLoading: false,
  activeRemindersFailed: false,
  activeRemindersHasMore: false,
  activeRemindersLoadingMore: false,
  pastRemindersHasMore: false,
  pastRemindersLoadingMore: false,
  drafts: {},
  starredMessages: {},

  setActiveConversation: (c) => {
    const current = get().activeConversation;
    // Skip re-fetch if same conversation is already active with loaded messages
    // (prevents HMR / re-render waste — use refreshMessages() for explicit refresh)
    if (c && current && c.id === current.id && get().messages.length > 0) {
      return;
    }
    set({ activeConversation: c, messages: [], messagesHasMore: false, loadingOlderMessages: false, realtimeMessageIds: new Set<string>() });
    if (c) {
      get().fetchMessages(c.id);
      // Lazy-load inbox details (label, assignee) from Firestore in background
      get().fetchActiveConvDetails();
    }
  },

  resolveConversationById: async (id) => {
    // (1) Already in the currently loaded list — return it directly.
    const existing = get().conversations.find((c) => c.id === id);
    if (existing) return existing;

    // Start from a minimal stub; upgrade fields as we learn more.
    // Conversation ids for SMS/WhatsApp TPAs are `senderPhone::contactPhone`
    // (see twilio.module.ts:761, routemobilewa.module.ts:615).
    let contactPhone = '';
    // Default channel derived from the conversation ID and the active TPA:
    //   1. Twilio (and other dual-channel TPAs) prefix WhatsApp addresses
    //      with `whatsapp:` — if either side of the id has that prefix,
    //      the channel is WHATSAPP.
    //   2. WhatsApp-only TPAs (whatsapp, whatsappweb, gupshup, karix,
    //      pinnacle, routemobilewa, …) don't use the prefix because the
    //      entire app is WhatsApp — infer from appCodeTPA / extensionName.
    //   3. Otherwise fall back to SMS; the inbox doc (read below) will
    //      override with `data.channel` if it's stored.
    const appCodeTPA = (useAppStore.getState().appCodeTPA || '').toLowerCase();
    const extensionName = (useAppStore.getState().extensionName || '').toLowerCase();
    const WHATSAPP_ONLY_TPAS = new Set([
      'whatsapp', 'whatsappweb', 'whatsappbsp',
      'gupshup', 'karix', 'pinnacle', 'routemobilewa',
    ]);
    const idHasWhatsAppPrefix = id.toLowerCase().includes('whatsapp:');
    const tpaIsWhatsAppOnly =
      WHATSAPP_ONLY_TPAS.has(appCodeTPA) ||
      extensionName.startsWith('whatsapp') ||
      extensionName.startsWith('gupshup') ||
      extensionName.startsWith('karix') ||
      extensionName.startsWith('pinnacle') ||
      extensionName.startsWith('routemobilewa');
    let channel: Channel =
      idHasWhatsAppPrefix || tpaIsWhatsAppOnly ? 'WHATSAPP' : 'SMS';
    if (id.includes('::')) {
      const [, contact] = id.split('::');
      if (contact) {
        // Strip the `whatsapp:` channel prefix when surfacing the phone.
        contactPhone = contact.replace(/^whatsapp:/i, '');
      }
    }

    const stub: Conversation = {
      id,
      contactName: contactPhone || id,
      contactPhone,
      lastMessage: '',
      lastMessageTime: new Date(),
      unreadCount: 0,
      channel,
      metadata: {},
    };

    // (2) Read the inbox doc — it stores the last message, channel, label,
    //     assignee, contact fields and more (see getInboxConvDocPath).
    const path = getInboxConvDocPath(id);
    if (!path) return stub;

    try {
      const snap = await getDoc(doc(db, path));
      if (!snap.exists()) return stub;
      const data = snap.data() as Record<string, unknown>;

      const subject = typeof data.subject === 'string' ? data.subject : undefined;
      const contactNameFromDoc =
        subject
        || (typeof data.latestExistingRecordName === 'string' ? data.latestExistingRecordName : undefined)
        || (typeof data.contactName === 'string' ? data.contactName : undefined)
        || (typeof data.name === 'string' ? data.name : undefined);

      const contactPhoneFromDoc =
        (typeof data.contactPhone === 'string' ? data.contactPhone : undefined)
        || (typeof data.to === 'string' ? data.to : undefined)
        || (typeof data.contactNumber === 'string' ? data.contactNumber : undefined);

      const channelFromDoc = typeof data.channel === 'string' ? data.channel.toUpperCase() : undefined;
      const validChannels: Channel[] = ['SMS', 'MMS', 'WHATSAPP', 'VOICE', 'EMAIL', 'INTERCOM', 'INSTAGRAM', 'TELEGRAM'];
      if (channelFromDoc && (validChannels as string[]).includes(channelFromDoc)) {
        channel = channelFromDoc as Channel;
      }

      const lastMessageFromDoc =
        (typeof data.text === 'string' ? data.text : undefined)
        || (typeof data.body === 'string' ? data.body : undefined)
        || (typeof data.lastMessage === 'string' ? data.lastMessage : undefined)
        || '';

      const rawTime = data.time ?? data.createdTime ?? data.lastMessageTime;
      const lastMessageTime =
        typeof rawTime === 'number' ? new Date(rawTime)
        : typeof rawTime === 'string' ? new Date(rawTime)
        : new Date();

      return {
        ...stub,
        contactName: contactNameFromDoc || contactPhoneFromDoc || stub.contactName,
        contactPhone: contactPhoneFromDoc || stub.contactPhone,
        lastMessage: lastMessageFromDoc,
        lastMessageTime,
        channel,
        label: typeof data.label === 'string' ? data.label : undefined,
        assigneeEmail: typeof data.assigneeEmail === 'string' ? data.assigneeEmail : undefined,
        optOut: parseOptOut(data.optOut),
      };
    } catch {
      return stub;
    }
  },

  hydratePinnedConversations: async () => {
    const pinnedIds = useSettingsStore.getState().pinnedConversationIds;
    if (pinnedIds.length === 0) return;

    // Only hydrate the plain, unfiltered list. Whenever the query is narrowed,
    // injecting rows the query deliberately excluded would misrepresent it — a
    // chat last touched today must not appear in a "jump to January" view, and a
    // label/agent/channel filter means the same. Legacy re-applied the active
    // filter to lazily injected rows for the same reason
    // (`applyFilterConvItemsAfterFetchLazyDetailsFromDB`).
    const viewIsNarrowed = (s: ConversationStore) =>
      s.loading
      || !!s.searchQuery
      || s.phoneSearchActive
      || !!s.filterLabel
      || !!s.filterChannel
      || !!s.jumpBefore
      || !!(s.filterAgent && s.filterAgent !== 'all');
    const state = get();
    if (viewIsNarrowed(state)) return;

    const present = new Set(state.conversations.map((c) => normalizeConvId(c.id)));
    const missing = pinnedIds.filter((id) => !present.has(normalizeConvId(id)));
    if (missing.length === 0) return;

    const fetched = await fetchPinnedConversationsByIds(missing);
    if (fetched.length === 0) return;

    // "Agents can view only their own chats" is enforced through the query's
    // assigneeEmail filter (with filterAgent still 'all'), which viewIsNarrowed
    // can't see — so a pinned chat reassigned to another agent must be dropped
    // here, not injected past the permission. Unassigned rows pass: the client
    // filter in use-conversations.ts already gates those per permission.
    const { getInboxPermissions } = await import('@/hooks/use-permissions');
    const { canViewOtherUsersChats } = getInboxPermissions();
    const myEmail = useAuthStore.getState().user?.email;
    const canSee = (c: Conversation) =>
      canViewOtherUsersChats || !c.assigneeEmail || c.assigneeEmail === myEmail;

    // Re-check after the await: a filter/search/jump — or a reload kicked off
    // by a sender switch — may have taken over the view while we were fetching.
    if (viewIsNarrowed(get())) return;

    // Re-read: the list may have been replaced while we were fetching.
    const current = get().conversations;
    const nowPresent = new Set(current.map((c) => normalizeConvId(c.id)));

    // A specific sender scopes the query (`senderNumber` in queryOptions), so a
    // chat pinned on a different sender doesn't belong in this list.
    const activeSenderDigits = (get().activeSender?.phoneNumber || '').replace(/\D/g, '');
    const belongsToActiveSender = (c: Conversation) => {
      if (!activeSenderDigits) return true;
      const sender = ((c.metadata?.channelId as string | undefined)
        || (c.metadata?.from as string | undefined)
        || '').replace(/\D/g, '');
      return !sender || sender === activeSenderDigits;
    };

    const toAdd = fetched.filter(
      (c) => !nowPresent.has(normalizeConvId(c.id)) && belongsToActiveSender(c) && canSee(c)
    );
    if (toAdd.length === 0) return;

    console.log(`[ConvStore] Hydrated ${toAdd.length} pinned conversation(s) outside the loaded page`);

    // Insert at the chronological slot rather than prepending. The list is
    // newest-first and the pinned-first pass in conversation-list.tsx is what
    // lifts these to the top — so a prepend would strand the row above newer
    // chats the moment it's unpinned, until the next refetch.
    const next = [...current];
    for (const conv of toAdd) {
      const t = conversationTimeMs(conv);
      const idx = next.findIndex((c) => conversationTimeMs(c) < t);
      if (idx === -1) next.push(conv);
      else next.splice(idx, 0, conv);
    }
    set({ conversations: next });

    // The inbox doc already carried label/assignee/subject, so the only missing
    // piece is the CRM record name.
    const candidates = toAdd.filter((c) => !c.crmRecord && c.contactPhone);
    if (candidates.length > 0) {
      void enrichConversationsWithCRMRecord(candidates, set, get);
    }
  },

  setActiveSender: (sender) => {
    const isPhoneSearch = get().phoneSearchActive;
    set({ activeSender: sender, ...(!isPhoneSearch && { conversations: [], activeConversation: null, messages: [], nextCursor: null, hasMore: false }) });
    if (!isPhoneSearch) {
      get().fetchConversations();
    }
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
    // If search is cleared and we were in phone search mode, reset to normal
    if (!query && get().phoneSearchActive) {
      set({ phoneSearchActive: false });
      get().fetchConversations();
    }
  },

  setFilterChannel: (channel) => set({ filterChannel: channel }),

  setFilterLabel: (label) => {
    const prev = get().filterLabel;
    set({ filterLabel: label });
    // Re-fetch conversations when any label filter changes
    // - 'unreplied' uses server-side Firestore query (lastMessageIsIncoming)
    // - Other labels (open, hold, closed, custom) need fresh inbox data
    // - '__unread__' is a purely local unreadCount check, but it can only
    //   skip the re-fetch when the current list is the unfiltered one.
    //   Coming from a server-side filter (label/unreplied/send-failed) the
    //   store holds that Firestore-filtered subset — filtering it locally
    //   would show "no unread" no matter what the inbox actually contains.
    const prevIsServerSide = !!prev && prev !== '__unread__';
    const skipRefetch = label === prev || (label === '__unread__' && !prevIsServerSide);
    if (!skipRefetch) {
      get().fetchConversations();
    }
  },

  setFilterAgent: (agent) => {
    const prev = get().filterAgent;
    set({ filterAgent: agent });
    if (agent !== prev) {
      get().fetchConversations();
    }
  },

  jumpToConversationsAtDate: async (date) => {
    // Clearing the jump — refetch without the cutoff
    if (!date) {
      set({ jumpBefore: null });
      await get().fetchConversations();
      return;
    }
    // Use end-of-day for the selected date so all convos touched that day are included
    const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
    set({ jumpBefore: endOfDay });
    await get().fetchConversations();
  },

  fetchConversations: async () => {
    const startTime = performance.now();
    const logTiming = (msg: string) => {
      const elapsed = performance.now() - startTime;
      console.log(`[ConvStore +${elapsed.toFixed(0)}ms]`, msg);
    };

    logTiming('fetchConversations called');
    dbg.convFetchTriggered(new Error().stack?.split('\n').slice(1, 4).map(l => l.trim()).join(' ← ') || 'unknown');
    const tpaModule = useModuleStore.getState().tpaModule;
    if (!tpaModule) {
      logTiming('No TPA module, aborting');
      return;
    }

    const { activeSender, searchQuery, filterChannel, filterLabel, filterAgent, jumpBefore } = get();

    // What this request is actually asking for. Two calls with the same key
    // are interchangeable; a different key means the user changed something.
    const requestKey = JSON.stringify([
      activeSender?.id ?? null,
      searchQuery,
      filterChannel,
      filterLabel,
      filterAgent,
      jumpBefore?.getTime() ?? null,
    ]);

    // Skip only a genuinely duplicate in-flight request — that is all the
    // original `if (get().loading) return` guard was meant to catch (React
    // StrictMode double-invoke). Applied to *every* call it also swallowed
    // the fetch for a filter clicked while another was still loading, so
    // that filter never queried and the list kept rendering the previous
    // filter's results under the new filter's chip.
    if (get().loading && convFetchKey === requestKey) {
      logTiming('Identical fetch already in flight, skipping');
      return;
    }

    const myToken = ++convFetchToken;
    convFetchKey = requestKey;
    /** True once a newer fetch has started — this one must not touch the store. */
    const isStale = () => convFetchToken !== myToken;

    set({ loading: true, error: null, conversations: [], nextCursor: null, hasMore: false });
    logTiming('Started loading conversations');
    try {
      logTiming('Calling tpaModule.getConversations()');
      // Determine label-based query filters
      const isUnreplied = filterLabel === 'unreplied';
      const isUnread = filterLabel === '__unread__';
      const isSendFailed = filterLabel === '__send_failed__';
      // Labels like 'open', 'hold', 'closed', custom labels → pass as Firestore filter
      const labelFilter = filterLabel && !isUnreplied && !isUnread && !isSendFailed ? filterLabel : undefined;

      // Determine agent-based query filter.
      // Enforce inbox permission: if user can't view other users' chats,
      // restrict to own conversations — except when viewing the "unassigned"
      // tab (if that permission is allowed, unassigned chats should show).
      const currentUserEmail = useAuthStore.getState().user?.email;
      const { getInboxPermissions } = await import('@/hooks/use-permissions');
      const { canViewOtherUsersChats, canViewUnassignedChats } = getInboxPermissions();

      let agentFilter: string | undefined;
      if (!canViewOtherUsersChats) {
        if (filterAgent === 'unassigned' && canViewUnassignedChats) {
          // Unassigned tab allowed — don't filter by assignee at Firestore level;
          // client-side filter in use-conversations.ts narrows to unassigned only.
          agentFilter = undefined;
        } else {
          // Restrict to own conversations
          agentFilter = currentUserEmail;
        }
      } else {
        agentFilter =
          filterAgent === 'mine' ? currentUserEmail :
          filterAgent === 'all' || filterAgent === 'unassigned' || !filterAgent ? undefined :
          filterAgent; // specific agent email
      }

      const queryOptions = {
        search: searchQuery || undefined,
        channel: filterChannel || activeSender?.channel || undefined,
        senderNumber: activeSender?.phoneNumber || undefined,
        unrepliedOnly: isUnreplied || undefined,
        label: labelFilter,
        sendFailed: isSendFailed || undefined,
        assigneeEmail: agentFilter,
        updatedBefore: jumpBefore?.getTime(),
      };

      const tpaId = tpaModule.id;
      const isFirestoreTPA = tpaId === 'whatsapp';
      // Twilio routes label/assignee/unreplied/sendFailed through Firestore
      // (no Twilio API call), so bypass the SWR cache for those filters.
      const usesFirestoreForFilter = tpaId === 'twilio' && !!(
        queryOptions.label ||
        queryOptions.assigneeEmail ||
        queryOptions.unrepliedOnly ||
        queryOptions.sendFailed
      );
      const ext = useAppStore.getState().extensionName || '';

      let result: Awaited<ReturnType<typeof tpaModule.getConversations>>;

      if (isFirestoreTPA || usesFirestoreForFilter || !ext) {
        // WhatsApp uses Firestore with onSnapshot SWR — no extra caching needed
        // Twilio label/assignee filters also go through Firestore directly
        result = await tpaModule.getConversations(queryOptions);
      } else {
        // REST TPAs (Twilio, MessageBird, etc.) — use SWR for instant page loads
        result = await swrFetch({
          key: cacheKeys.conversations(ext, {
            search: searchQuery,
            channel: filterChannel || activeSender?.channel,
            sender: activeSender?.phoneNumber,
            label: filterLabel || undefined,
            agent: filterAgent || undefined,
          }),
          store: 'proxy_cache',
          fetcher: () => tpaModule.getConversations(queryOptions),
          ttl: TTL.CONVERSATIONS,
          onRevalidate: (fresh) => {
            // Revalidation resolves independently of the await below, so it
            // can land long after the user moved to another filter.
            //
            // Gate on *request* identity, not fetch generation. swrFetch keeps
            // one revalidation per cache key (swr.ts drops a second one while
            // the first is in flight), so on filter A -> B -> A the surviving
            // callback belongs to the FIRST A fetch while A is once again what
            // the user is looking at. A generation check would call that stale
            // and bin the fresh rows, leaving the visible list on cached data
            // with no revalidation left to repair it — for up to the 7-day TTL.
            // What matters is whether this payload still describes the current
            // filter, which is exactly what requestKey encodes.
            if (convFetchKey !== requestKey) return;
            const freshConvs = fresh?.conversations || [];
            if (freshConvs.length > 0) {
              // Merge fresh data with existing inbox details (label, assignee, crmRecord, etc.)
              const existing = get().conversations;
              const existingMap = new Map(existing.map(c => [c.id, c]));
              const merged = freshConvs.map(fc => {
                const ex = existingMap.get(fc.id);
                if (!ex) return fc;
                // IMPORTANT: spread fresh metadata LAST so lastMessageStatus /
                // lastMessageDirection / lastInboundTime from the server win
                // over stale cached values. Otherwise an incoming conv could
                // keep a cached outbound tick + status next to the new
                // incoming preview (double-tick-on-inbound bug).
                return {
                  ...fc,
                  label: ex.label,
                  assigneeEmail: ex.assigneeEmail,
                  crmRecordName: ex.crmRecordName || fc.crmRecordName,
                  crmRecord: ex.crmRecord || fc.crmRecord,
                  pinnedMessageId: ex.pinnedMessageId,
                  // REST rows carry no inbox-doc fields — keep the opt-out lock.
                  optOut: ex.optOut,
                  // REST responses carry no read state (unreadCount always 0)
                  // — keep the doc-derived/local badge instead of wiping it.
                  unreadCount: Math.max(ex.unreadCount || 0, fc.unreadCount || 0),
                  // `...fc` above dropped the local "Mark as unread" flag —
                  // without it the kept badge renders as a count, not a dot.
                  markedUnread: ex.markedUnread || fc.markedUnread || undefined,
                  metadata: { ...ex.metadata, ...fc.metadata },
                };
              });
              set({
                conversations: merged,
                nextCursor: fresh?.nextCursor || null,
                hasMore: !!fresh?.nextCursor,
              });
              logTiming(`SWR revalidated: ${merged.length} conversations`);
              // `merged` is built from the fresh page only — re-inject pinned
              // chats living outside it, or they vanish on every revalidate.
              void get().hydratePinnedConversations();
            }
          },
        });
      }

      logTiming(`Got ${result?.conversations?.length || 0} conversations from ${isFirestoreTPA ? 'Firestore' : 'API/cache'}`);

      // A newer filter started while this was in flight. Its own call owns
      // `loading` and the list now, so drop these results entirely rather
      // than rendering them under the filter the user actually picked.
      if (isStale()) {
        logTiming('Superseded by a newer fetch — discarding results');
        return;
      }

      let convs = result?.conversations || [];
      // The Twilio Firestore-filter converter derives unreadCount straight
      // from the inbox doc's read watermark, which lags markAsRead's debounced
      // CF write. If the open conversation was just read, a refetch of a
      // filtered list would momentarily re-show its badge. Keep the open chat
      // at zero unread — it is being read right now. (REST path already guards
      // this inside fetchInboxDetailsForConversations.)
      const openConvId = get().activeConversation?.id;
      if (usesFirestoreForFilter && openConvId) {
        convs = convs.map((c) => (c.id === openConvId ? { ...c, unreadCount: 0 } : c));
      }
      set({
        conversations: convs,
        nextCursor: result?.nextCursor || null,
        hasMore: !!result?.nextCursor,
        loading: false,
      });
      logTiming('Conversations set in store');

      // Lazy-fetch inbox details (label, assignee) and chat labels in background.
      // WhatsApp and Twilio-via-Firestore already contain inbox details — skip re-fetch.
      if (!isFirestoreTPA && !usesFirestoreForFilter) {
        fetchInboxDetailsForConversations(convs, set, get);
      } else {
        // For WhatsApp / Twilio-with-Firestore-filter the converter
        // already extracted basic inbox fields directly from the doc, so
        // we skip the re-fetch above. But the CRM record-enrichment hook
        // lives inside fetchInboxDetailsForConversations — invoke it
        // directly here so the conversation list still gets matched
        // record names backfilled for these paths.
        const candidates = convs.filter((c) => !c.crmRecord && c.contactPhone);
        if (candidates.length > 0) {
          void enrichConversationsWithCRMRecord(candidates, set, get);
        }
      }
      fetchChatLabels(set);
      // Pinned chats older than this page aren't in `convs` — pull them in by id
      // so they show at the top immediately, matching the legacy
      // `getPersonalPinnedChats(renderPersonalPinnedChats)` call that ran right
      // after `getConversationsList()`.
      void get().hydratePinnedConversations();
      logTiming('Started background fetches (labels, assignees)');

      // Twilio Firestore-filter path: serially enrich each conversation with
      // its latest message from Twilio API and apply updates incrementally.
      if (usesFirestoreForFilter && tpaId === 'twilio') {
        // New list — abort any pass still running for the previous filter.
        twilioEnrichmentToken++;
        enrichTwilioLatestMessages(convs, set, get);
      }
    } catch (error) {
      logTiming(`Error: ${error instanceof Error ? error.message : 'Unknown'}`);
      // A superseded fetch must not clear `loading` or surface its error —
      // the newer fetch owns both.
      if (isStale()) return;
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load conversations',
      });
    }
  },

  loadMoreConversations: async () => {
    const { nextCursor, loadingMore, loading } = get();
    if (!nextCursor || loadingMore || loading) return;

    const tpaModule = useModuleStore.getState().tpaModule;
    if (!tpaModule) return;

    // This page extends the list the current fetch built, and it is appended
    // rather than assigned. The `loading` check above only covers the instant
    // we start: a filter switched while this page is in flight would splice
    // the old filter's conversations onto the new filter's list. Deliberately
    // read the token without bumping it — pagination extends a list, it never
    // replaces one, so it must not supersede the fetch that owns it.
    const myToken = convFetchToken;

    set({ loadingMore: true });
    try {
      const { filterLabel, filterAgent, jumpBefore, activeSender } = get();
      const isUnreplied = filterLabel === 'unreplied';
      const isUnread = filterLabel === '__unread__';
      const isSendFailed = filterLabel === '__send_failed__';
      const labelFilter = filterLabel && !isUnreplied && !isUnread && !isSendFailed ? filterLabel : undefined;

      const currentUserEmail = useAuthStore.getState().user?.email;
      const { getInboxPermissions: getInboxPerms } = await import('@/hooks/use-permissions');
      const { canViewOtherUsersChats: canViewOthers, canViewUnassignedChats: canViewUnassigned } = getInboxPerms();

      let agentFilter: string | undefined;
      if (!canViewOthers) {
        if (filterAgent === 'unassigned' && canViewUnassigned) {
          agentFilter = undefined;
        } else {
          agentFilter = currentUserEmail;
        }
      } else {
        agentFilter =
          filterAgent === 'mine' ? currentUserEmail :
          filterAgent === 'all' || filterAgent === 'unassigned' || !filterAgent ? undefined :
          filterAgent;
      }

      const result = await tpaModule.getConversations({
        cursor: nextCursor,
        senderNumber: activeSender?.phoneNumber || undefined,
        unrepliedOnly: isUnreplied || undefined,
        label: labelFilter,
        sendFailed: isSendFailed || undefined,
        assigneeEmail: agentFilter,
        updatedBefore: jumpBefore?.getTime(),
      });

      // A different filter took over while this page was loading — its list
      // is on screen now, so appending these rows would contaminate it.
      if (convFetchToken !== myToken) {
        set({ loadingMore: false });
        return;
      }

      // Merge new conversations, deduplicating by normalized id
      // (from::to and to::from should be treated as the same conversation, and
      // `a--b` is the same chat as `a::b` — see normalizeConvId)
      const normalizeId = (id: string) => normalizeConvId(id).split('::').sort().join('::');
      const existing = get().conversations;
      const existingNormIds = new Set(existing.map((c) => normalizeId(c.id)));
      const incoming = result?.conversations || [];

      // Update existing conversations if the incoming one has a newer lastMessageTime
      const updatedExisting = existing.map((c) => {
        const normId = normalizeId(c.id);
        const newer = incoming.find(
          (ic) => normalizeId(ic.id) === normId && ic.lastMessageTime > c.lastMessageTime
        );
        return newer ? { ...c, lastMessage: newer.lastMessage, lastMessageTime: newer.lastMessageTime, metadata: newer.metadata } : c;
      });

      const newConvs = incoming.filter((c) => !existingNormIds.has(normalizeId(c.id)));
      const skipped = incoming.length - newConvs.length;
      console.log(`[ConvStore] loadMoreConversations: incoming=${incoming.length}, new=${newConvs.length}, skipped(dupes)=${skipped}, total after=${updatedExisting.length + newConvs.length}`);
      if (skipped > 0) {
        const dupes = incoming.filter((c) => existingNormIds.has(normalizeId(c.id)));
        console.log('[ConvStore] Skipped duplicate conversation IDs:', dupes.map((c) => c.id));
      }

      set({
        conversations: [...updatedExisting, ...newConvs],
        nextCursor: result?.nextCursor || null,
        hasMore: !!result?.nextCursor,
        loadingMore: false,
      });

      // Lazy-fetch inbox details for the newly loaded conversations.
      // WhatsApp and Twilio-via-Firestore (label/assignee filters) already have
      // inbox details baked in — skip the redundant re-fetch for them.
      const tpaIdNow = useModuleStore.getState().tpaModule?.id;
      const twilioFirestoreFilter = tpaIdNow === 'twilio' && !!(
        filterLabel || filterAgent && filterAgent !== 'all' && filterAgent !== 'mine' && filterAgent !== 'unassigned'
      );
      if (newConvs.length > 0 && tpaIdNow !== 'whatsapp' && !twilioFirestoreFilter) {
        fetchInboxDetailsForConversations(newConvs, set, get);
      } else if (newConvs.length > 0) {
        // WhatsApp / Twilio-with-Firestore-filter: enrichment hook still
        // needs to run since fetchInboxDetailsForConversations is skipped.
        const candidates = newConvs.filter((c) => !c.crmRecord && c.contactPhone);
        if (candidates.length > 0) {
          void enrichConversationsWithCRMRecord(candidates, set, get);
        }
        // Appended rows carry their own `enrichmentPending` flags, and only
        // fetchConversations used to resolve them — so every conversation
        // past the first page kept its skeleton preview forever. Deliberately
        // no token bump: the first page may still be enriching.
        if (twilioFirestoreFilter) {
          enrichTwilioLatestMessages(newConvs, set, get);
        }
      }
    } catch (error) {
      set({
        loadingMore: false,
        error: error instanceof Error ? error.message : 'Failed to load more conversations',
      });
    }
  },

  fetchMessages: async (conversationId) => {
    const tpaModule = useModuleStore.getState().tpaModule;
    if (!tpaModule) return;

    // Deduplicate: if already fetching this exact conversation, reuse the in-flight promise.
    // Prevents duplicate API calls from React StrictMode double-mount in development.
    if (activeFetchMessagesId === conversationId && activeFetchMessagesPromise) {
      return activeFetchMessagesPromise;
    }

    const PAGE_SIZE = 50;
    // Smallest page size among TPA modules (MessageBird REST = 20).
    // Used for hasMore detection since not all modules return PAGE_SIZE per call.
    const MIN_FULL_PAGE = 20;
    set({ messagesLoading: true, messagesHasMore: false });

    const doFetch = async () => {
      try {
        // WhatsApp and MessageBird page messages out of Firestore rather than a
        // cacheable REST endpoint, so they opt out of the SWR wrapper below.
        const isFirestoreMessages = tpaModule.id === 'whatsapp' || tpaModule.id === 'messagebird';
        // Skip the notes read only for modules whose getMessages() already returns
        // notes/system docs — otherwise they'd arrive twice. MessageBird answers
        // per version: true on its old REST path, false on Nest (API messages only),
        // which is why a Nest account stored notes that nothing ever read.
        const skipNotesFetch = tpaModule.includesInboxNotesInGetMessages?.() === true;
        dbg.notesFetchDecision(conversationId, tpaModule.id, skipNotesFetch);
        const ext = useAppStore.getState().extensionName || '';

        // For REST TPAs — cache the first message page via SWR
        const messagesFetcher = () => tpaModule.getMessages(conversationId, { pageSize: PAGE_SIZE }).then((m) => m || []);

        const apiMessagesPromise = (!isFirestoreMessages && ext)
          ? swrFetch<Message[]>({
              key: cacheKeys.messages(ext, conversationId),
              store: 'proxy_cache',
              fetcher: messagesFetcher,
              ttl: TTL.MESSAGES,
              onRevalidate: (fresh) => {
                // Landed after the user moved on — applying it would paint this
                // conversation's messages under another conversation's header.
                if (get().activeConversation?.id !== conversationId) return;
                // Background refresh — replace API messages, keep existing notes (Firestore-cached)
                const current = get().messages;
                const notes = current.filter(m => m.isNote || m.isSystemMessage);
                const idSet = new Set<string>();
                const merged: Message[] = [];
                for (const m of fresh) { if (!idSet.has(m.id)) { idSet.add(m.id); merged.push(m); } }
                for (const n of notes) { if (!idSet.has(n.id)) { idSet.add(n.id); merged.push(n); } }
                merged.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
                set({ messages: merged });
              },
            })
          : messagesFetcher();

        // The notes read is bounded by the TPA page's time range, so it has to
        // wait for that page rather than race it. The extra latency is the notes
        // query alone (and none at all when the page comes from the SWR cache);
        // in exchange the query returns the handful of notes that can actually
        // render instead of a 100-doc window that is then mostly discarded.
        const apiMessages = await apiMessagesPromise;

        // Notes from Firestore aren't paginated, so without this bound they'd
        // arrive from all time and duplicate as older TPA pages load.
        const oldestTpaTime = apiMessages.length > 0
          ? Math.min(...apiMessages.map((m) => m.timestamp.getTime()))
          : 0;

        const notes = skipNotesFetch
          ? []
          // No messages on the page (new conversation, API failure) → no bound to
          // apply; fall back to the most recent notes.
          : await fetchNotesForConversation(conversationId, oldestTpaTime > 0 ? { since: oldestTpaTime } : undefined);

        // Belt-and-braces: the query already applied the bound, but keep the
        // filter so an unbounded fallback read can't leak out-of-window notes.
        const filteredNotes = oldestTpaTime > 0
          ? notes.filter((n) => n.timestamp.getTime() >= oldestTpaTime)
          : notes;
        dbg.notesMerged(notes.length, filteredNotes.length, oldestTpaTime);

        // Merge and deduplicate, then sort by timestamp
        const idSet = new Set<string>();
        const merged: Message[] = [];
        for (const m of apiMessages) { if (!idSet.has(m.id)) { idSet.add(m.id); merged.push(m); } }
        for (const n of filteredNotes) { if (!idSet.has(n.id)) { idSet.add(n.id); merged.push(n); } }
        merged.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        // Stale result — the user switched conversations while this was in flight.
        // Writing it would replace the open chat's messages with this one's (slow
        // TPAs like MessageBird Nest, which resolve media before returning, lose
        // this race routinely). Leave `messagesLoading` alone: the fetch for the
        // conversation that IS open owns it.
        if (get().activeConversation?.id !== conversationId) return;

        set({
          // Authors harvested from the notes read above land here, so outbound
          // messages show who sent them on the first paint instead of after the
          // viewport lookup (which now finds them cached and issues no query).
          messages: applyCachedAuthors(merged),
          messagesLoading: false,
          // MessageBird REST returns 20/page, Twilio returns 50/page.
          // Use MIN_FULL_PAGE so hasMore works for all module page sizes.
          // loadOlderMessages() will set the correct hasMore from the module's response.
          messagesHasMore: apiMessages.length >= MIN_FULL_PAGE,
        });

        // Per-message author meta is fetched lazily on-viewport via
        // requestMessageAuthor() — see useLazyMessageAuthor hook in
        // ChatMessage. This avoids reading meta docs for messages the
        // user never scrolls to.

        // Media resolution is deferred — ChatMessage triggers it via
        // resolveMessageMedia() when the message enters the viewport.
      } catch (error) {
        set({
          messagesLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load messages',
        });
      } finally {
        // Only release the in-flight marker if it still points at this fetch.
        // A slow fetch finishing after the user opened another conversation would
        // otherwise clear the newer fetch's marker, and the dedup guard above
        // would let a duplicate request for that conversation start.
        if (activeFetchMessagesId === conversationId) {
          activeFetchMessagesId = null;
          activeFetchMessagesPromise = null;
        }
      }
    };

    activeFetchMessagesId = conversationId;
    activeFetchMessagesPromise = doFetch();
    return activeFetchMessagesPromise;
  },

  mergeNotesIntoMessages: async (conversationId) => {
    const tpaModule = useModuleStore.getState().tpaModule;
    if (tpaModule?.includesInboxNotesInGetMessages?.() === true) return;

    const current = get().messages;
    const oldest = current.length > 0
      ? Math.min(...current.map((m) => m.timestamp.getTime()))
      : 0;

    const notes = await fetchNotesForConversation(
      conversationId,
      oldest > 0 ? { since: oldest } : undefined
    );
    if (notes.length === 0) return;
    if (get().activeConversation?.id !== conversationId) return;

    const existing = get().messages;
    const ids = new Set(existing.map((m) => m.id));
    const fresh = notes.filter((n) => !ids.has(n.id));
    if (fresh.length === 0) return;

    const merged = [...existing, ...fresh].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
    set({ messages: merged });
  },

  jumpToMessagesAtDate: async (conversationId, date) => {
    const tpaModule = useModuleStore.getState().tpaModule;
    if (!tpaModule) return;

    set({ messagesLoading: true, messagesHasMore: false, messages: [] });
    try {
      const msgs = await tpaModule.getMessages(conversationId, { before: date });
      // Same staleness guard as fetchMessages — a jump that resolves after the
      // user moved to another chat must not paint into it.
      if (get().activeConversation?.id !== conversationId) return;
      set({
        messages: msgs.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
        messagesHasMore: msgs.length > 0,
        messagesLoading: false,
      });
    } catch (err) {
      console.error('[ConvStore] jumpToMessagesAtDate failed:', err);
      set({ messagesLoading: false });
    }
  },

  loadOlderMessages: async () => {
    const { activeConversation, messages, loadingOlderMessages, messagesHasMore } = get();
    if (!activeConversation || loadingOlderMessages || !messagesHasMore) return;

    const tpaModule = useModuleStore.getState().tpaModule;
    if (!tpaModule) return;

    set({ loadingOlderMessages: true });
    try {
      // Use cursor-based pagination via loadMoreMessages (uses stored next_page_uri)
      const result = await tpaModule.loadMoreMessages(activeConversation.id);

      // Stale page — another conversation was opened mid-request. `messages` was
      // captured before the await, so prepending would resurrect the old chat's
      // thread on top of the new one.
      if (get().activeConversation?.id !== activeConversation.id) {
        set({ loadingOlderMessages: false });
        return;
      }

      // Deduplicate by id and prepend older messages
      const existingIds = new Set(messages.map((m) => m.id));
      const newMsgs = result.messages.filter((m) => !existingIds.has(m.id));

      // Notes for the stretch of history this page just uncovered. The initial
      // read is bounded by the page that was on screen, so without this the
      // notes attached to older messages would never appear no matter how far
      // back the user scrolled. Bounded on both sides, so the slice already
      // rendered isn't read again.
      let olderNotes: Message[] = [];
      const skipNotesFetch = tpaModule.includesInboxNotesInGetMessages?.() === true;
      if (!skipNotesFetch && newMsgs.length > 0) {
        const previousOldest = messages.length > 0
          ? Math.min(...messages.map((m) => m.timestamp.getTime()))
          : 0;
        const newOldest = Math.min(...newMsgs.map((m) => m.timestamp.getTime()));
        olderNotes = await fetchNotesForConversation(activeConversation.id, {
          since: newOldest,
          ...(previousOldest > 0 && { until: previousOldest }),
        });
        olderNotes = olderNotes.filter((n) => !existingIds.has(n.id));
      }

      // Stale page — another conversation was opened mid-request. `messages` was
      // captured before the await, so prepending would resurrect the old chat's
      // thread on top of the new one.
      if (get().activeConversation?.id !== activeConversation.id) {
        set({ loadingOlderMessages: false });
        return;
      }

      const prepended = olderNotes.length > 0
        ? [...newMsgs, ...olderNotes].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
        : newMsgs;

      set({
        messages: [...prepended, ...messages],
        loadingOlderMessages: false,
        messagesHasMore: result.hasMore,
      });

      // Media resolution deferred to viewport via resolveMessageMedia()
    } catch {
      set({ loadingOlderMessages: false });
    }
  },

  refreshMessages: async () => {
    const { activeConversation } = get();
    if (!activeConversation) return;

    const tpaModule = useModuleStore.getState().tpaModule;
    if (!tpaModule) return;

    // Fetch messages WITHOUT clearing the existing ones (no loading flash)
    set({ messagesLoading: true });
    try {
      // Only skip when the module's getMessages() already includes notes/system docs
      const skipNotesFetch = tpaModule.includesInboxNotesInGetMessages?.() === true;
      dbg.notesFetchDecision(activeConversation.id, tpaModule.id, skipNotesFetch);

      // Sequenced for the same reason as fetchMessages: the notes query is
      // bounded by the page's oldest message, so it needs that page first.
      const apiMessages = await tpaModule.getMessages(activeConversation.id).then((m) => m || []);
      const oldestForNotes = apiMessages.length > 0
        ? Math.min(...apiMessages.map((m) => m.timestamp.getTime()))
        : 0;
      const notes = skipNotesFetch
        ? []
        : await fetchNotesForConversation(activeConversation.id, oldestForNotes > 0 ? { since: oldestForNotes } : undefined);

      // Only include notes/system messages within the TPA message time range
      const oldestTpaTime = apiMessages.length > 0
        ? Math.min(...apiMessages.map((m) => m.timestamp.getTime()))
        : 0;
      const filteredNotes = oldestTpaTime > 0
        ? notes.filter((n) => n.timestamp.getTime() >= oldestTpaTime)
        : notes;
      dbg.notesMerged(notes.length, filteredNotes.length, oldestTpaTime);

      // Merge and deduplicate, then sort by timestamp
      const idSet = new Set<string>();
      const merged: Message[] = [];
      for (const m of apiMessages) { if (!idSet.has(m.id)) { idSet.add(m.id); merged.push(m); } }
      for (const n of filteredNotes) { if (!idSet.has(n.id)) { idSet.add(n.id); merged.push(n); } }
      merged.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

      // Stale refresh — a different conversation is open now. Its own fetch owns
      // `messagesLoading`, so don't touch that either.
      if (get().activeConversation?.id !== activeConversation.id) return;

      set({ messages: applyCachedAuthors(merged), messagesLoading: false });

      // Media resolution deferred to viewport via resolveMessageMedia()
    } catch {
      set({ messagesLoading: false });
    }
  },

  addMessage: (message) =>
    set({ messages: [...get().messages, message] }),

  clearRealtimeMessageIds: () => set({ realtimeMessageIds: new Set<string>() }),

  addOrUpdateMessage: (message, fromRealtime) => {
    const current = get().messages;
    const idx = current.findIndex((m) => m.id === message.id);
    if (idx >= 0) {
      // Update existing message (new object reference for Zustand)
      // Preserve the higher status to prevent async race condition downgrades
      // (e.g. msg-status event sets "delivered" before getMessageById resolves with "sent")
      const updated = [...current];
      const existing = current[idx];
      const STATUS_WEIGHT: Record<string, number> = { queued: 0, scheduled: 0.5, sent: 1, delivered: 2, read: 3, failed: 4 };
      const existingWeight = STATUS_WEIGHT[existing.status] ?? -1;
      const incomingWeight = STATUS_WEIGHT[message.status] ?? -1;
      // Preserve scheduledAt from existing metadata if not present in incoming
      const mergedMetadata = (existing.metadata?.scheduledAt && !message.metadata?.scheduledAt)
        ? { ...message.metadata, scheduledAt: existing.metadata.scheduledAt }
        : message.metadata;
      // Preserve attachments / mediaUrl when the incoming copy has none
      // but the existing one does. The canonical message source (e.g.
      // Twilio's eventually-consistent Messages API) echoes a freshly
      // sent media message back with num_media: 0 for a few seconds, so
      // a status-reconciliation update would otherwise clobber the
      // optimistic media and the row flips to text-only. Mirrors the
      // "preserve the higher status" guard below.
      const incomingHasAttachments = !!message.attachments && message.attachments.length > 0;
      const existingHasAttachments = !!existing.attachments && existing.attachments.length > 0;
      const keptAttachments = !incomingHasAttachments && existingHasAttachments
        ? existing.attachments
        : message.attachments;
      const keptMediaUrl = message.mediaUrl ?? existing.mediaUrl;
      // Carry the stable client-side key forward. The canonical /
      // realtime copy has no clientId, so without this the React list
      // key would flip from clientId → SID on first reconciliation and
      // the row would remount (entrance animation replays = visible
      // "jump"). Keeping existing.clientId makes the update silent.
      const keptClientId = message.clientId ?? existing.clientId;
      const merged = {
        ...message,
        metadata: mergedMetadata,
        ...(keptAttachments ? { attachments: keptAttachments } : {}),
        ...(keptMediaUrl ? { mediaUrl: keptMediaUrl } : {}),
        ...(keptClientId ? { clientId: keptClientId } : {}),
      };
      updated[idx] = incomingWeight >= existingWeight
        ? merged
        : { ...merged, status: existing.status };
      set({ messages: updated });

      // Sync conversation metadata if this outbound message has a newer status
      if (message.direction === 'outbound' && message.conversationId) {
        const STATUS_WEIGHT: Record<string, number> = { queued: 0, scheduled: 0.5, sent: 1, delivered: 2, read: 3, failed: 4 };
        const convs = get().conversations;
        const conv = convs.find((c) => c.id === message.conversationId);
        if (conv) {
          const currentMetaWeight = STATUS_WEIGHT[(conv.metadata?.lastMessageStatus as string) || ''] ?? -1;
          const newWeight = STATUS_WEIGHT[message.status] ?? -1;
          if (newWeight > currentMetaWeight) {
            const active = get().activeConversation;
            const isMsgCancelled = message.metadata?.rawStatus === 'canceled' || !!(message.metadata?.cancelledAt);
            const updates = {
              metadata: {
                ...(conv.metadata || {}),
                lastMessageStatus: message.status,
                lastMessageDirection: 'outbound',
                lastMessageCancelled: isMsgCancelled || undefined,
              },
            };
            set({
              conversations: convs.map((c) => c.id === message.conversationId ? { ...c, ...updates } : c),
              activeConversation: active?.id === message.conversationId ? { ...active, ...updates } : active,
            });
          }
        }
      }
    } else {
      // Append new message (it's the latest)
      const updates: Partial<ReturnType<typeof get>> = { messages: [...current, message] };
      // Track realtime message IDs for new-message animation
      if (fromRealtime) {
        const next = new Set(get().realtimeMessageIds);
        next.add(message.id);
        updates.realtimeMessageIds = next;
      }
      set(updates);

      // Sync conversation list: update subject + timestamp and move to top
      if (message.conversationId) {
        const normalizeId = (id: string) => id.split('::').sort().join('::');
        const normTarget = normalizeId(message.conversationId);
        const cm = message as Record<string, unknown>;
        const subject = cm.isReactionMessage && cm.reactionText
          ? `Reacted '${cm.reactionText}'`
          : message.body
          || (message.attachments?.length ? '📎 Attachment' : 'New message');
        const convs = get().conversations;
        const cIdx = convs.findIndex((c) => c.id === message.conversationId || normalizeId(c.id) === normTarget);
        if (cIdx >= 0) {
          const updated = {
            ...convs[cIdx],
            lastMessage: subject,
            lastMessageTime: message.timestamp,
          };
          const rest = convs.filter((_, i) => i !== cIdx);
          set({ conversations: [updated, ...rest] });
        }
      }
    }

    // Resolve media attachments if needed (for both new and updated messages)
    if (message.metadata?.needsMediaResolve) {
      resolveMediaInStore([message], set, get);
    }
  },

  markAsRead: (conversationId, _fromCrossTab?: boolean) => {
    // Caller is responsible for deciding whether there's something to
    // mark as read (inbox callers gate on `unreadCount > 0`; chat-view
    // callers gate on `realtimeMessageIds.size > 0`). When invoked
    // locally we always clear the badge AND fire the server CF — no
    // internal "hadUnread" snapshot, since the chat-view path can have
    // unreadCount === 0 even when there's clearly something new to
    // acknowledge (a realtime arrival while the chat is open).
    const conv = get().conversations.find((c) => c.id === conversationId);
    // A read supersedes any pending "mark as unread" hold on this conv.
    _lastUnreadMarkAt.delete(conversationId);

    set({
      conversations: get().conversations.map((c) =>
        c.id === conversationId ? { ...c, unreadCount: 0, markedUnread: false } : c
      ),
    });
    // Broadcast to other tabs (skip if this call already came from another tab)
    if (!_fromCrossTab) {
      crossTabChannel?.postMessage({ type: 'mark-as-read', conversationId });
    }

    // Cross-tab listeners skip the CF — the origin tab fired it.
    if (!_fromCrossTab && conv?.contactPhone) {
      const contactId = toContactId(conv.contactPhone);
      get()
        .markMessageAsReadInConversation(conversationId, contactId)
        .catch(() => {});
    }
  },

  markAsUnread: (conversationId, _fromCrossTab?: boolean) => {
    const conv = get().conversations.find((c) => c.id === conversationId);
    if (!conv) return;
    // Badge of 1, WhatsApp-Web style — never touch a real count that's
    // already showing. The next open goes through markAsRead as usual,
    // which zeroes the badge and resets the doc flag.
    const nowMs = Date.now();
    _lastUnreadMarkAt.set(conversationId, nowMs);
    // Let the very next open fire the read CF again even inside the
    // short dedup window — the doc flag must be cleared server-side.
    _lastReadCallAt.delete(conversationId);

    set({
      conversations: get().conversations.map((c) =>
        c.id === conversationId ? { ...c, unreadCount: Math.max(c.unreadCount || 0, 1), markedUnread: true } : c
      ),
    });
    if (!_fromCrossTab) {
      crossTabChannel?.postMessage({ type: 'mark-as-unread', conversationId });
    }

    // Cross-tab listeners skip the CF — the origin tab fired it.
    if (_fromCrossTab || !conv.contactPhone) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;
    // RingCentral reports its own read state; the inbox doc flag is never
    // read for it (same skip as markMessageAsReadInConversation).
    if (appStore.appCodeTPA?.toLowerCase().includes('ringcentral')) return;

    const contactId = toContactId(conv.contactPhone);
    const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
    const { selectedAdminUserId } = useAuthStore.getState();
    callServiceAPI({
      appCode: extensionName,
      TPAService: extensionName.split('for')[0],
      action: 'UA_INBOX_MARK_CONVERSATION_UNREAD',
      data: { conversationID: conversationId, contactId },
      _ua_lic_adminUserID: selectedAdminUserId,
      headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
    }).catch((error) => {
      console.error('[ConvStore] Failed to mark as unread:', error);
    });
  },

  updateMessageStatus: (messageId, newStatus) => {
    const STATUS_WEIGHT: Record<string, number> = {
      queued: 0, sent: 1, delivered: 2, read: 3, failed: 4,
    };
    const messages = get().messages;
    let statusChanged = false;
    const updatedMessages = messages.map((m) => {
      if (m.id !== messageId) return m;
      const currentWeight = STATUS_WEIGHT[m.status] ?? -1;
      const newWeight = STATUS_WEIGHT[newStatus] ?? -1;
      if (newWeight <= currentWeight) return m;
      statusChanged = true;
      return { ...m, status: newStatus };
    });
    set({ messages: updatedMessages });

    // Also update the conversation metadata if this is the latest outbound message
    if (statusChanged) {
      const msg = updatedMessages.find((m) => m.id === messageId);
      if (msg && msg.direction === 'outbound' && msg.conversationId) {
        const convs = get().conversations;
        const conv = convs.find((c) => c.id === msg.conversationId);
        if (conv) {
          const currentMetaWeight = STATUS_WEIGHT[(conv.metadata?.lastMessageStatus as string) || ''] ?? -1;
          const newWeight = STATUS_WEIGHT[newStatus] ?? -1;
          if (newWeight > currentMetaWeight) {
            const active = get().activeConversation;
            const updates = {
              metadata: {
                ...(conv.metadata || {}),
                lastMessageStatus: newStatus,
                lastMessageDirection: 'outbound',
              },
            };
            set({
              conversations: convs.map((c) =>
                c.id === msg.conversationId ? { ...c, ...updates } : c
              ),
              activeConversation:
                active?.id === msg.conversationId ? { ...active, ...updates } : active,
            });
          }
        }
      }
    }
  },

  updateConversation: (conversationId, updates) => {
    const active = get().activeConversation;
    set({
      conversations: get().conversations.map((c) =>
        c.id === conversationId ? { ...c, ...updates } : c
      ),
      activeConversation:
        active?.id === conversationId ? { ...active, ...updates } : active,
    });
  },

  prependOrUpdateConversation: (conversationId, lastMessage, lastMessageTime, incrementUnread = true, newConvDefaults?) => {
    const normalizeId = (id: string) => id.split('::').sort().join('::');
    const normTarget = normalizeId(conversationId);
    const existing = get().conversations;
    const idx = existing.findIndex((c) => c.id === conversationId || normalizeId(c.id) === normTarget);
    dbg.convPrepend(conversationId, idx >= 0, normTarget, existing.map(c => c.id));
    if (idx >= 0) {
      // A row sitting on a manual "Mark as unread" dot carries a placeholder
      // badge of 1 — a real inbound replaces it (count 1), not stacks on it.
      const wasManualUnread = !!existing[idx].markedUnread;
      const conv = {
        ...existing[idx],
        lastMessage,
        lastMessageTime,
        unreadCount: incrementUnread
          ? (wasManualUnread ? 1 : existing[idx].unreadCount + 1)
          : existing[idx].unreadCount,
        ...(incrementUnread && wasManualUnread && { markedUnread: false }),
      };
      const rest = existing.filter((_, i) => i !== idx);
      set({ conversations: [conv, ...rest] });
    } else if (newConvDefaults && !get().phoneSearchActive) {
      // New conversation not in the list — construct from the message data we already have
      // and prepend it instead of reloading the entire conversation list.
      // Skip when phone search is active (CRM context) — only show conversations for the searched number.
      // Under an agent pill the row starts with no assigneeEmail, so
      // use-conversations.ts hides it until fetchInboxDetailsForConversations
      // (below) fills in the owner — then it shows only if it's that agent's.
      const parts = conversationId.split('::');
      const contactPart = parts.length >= 2 ? parts[1] : parts[0];
      const cleanContact = contactPart.replace('whatsapp:', '').replace('+', '');
      const isWhatsApp = conversationId.includes('whatsapp:');

      const newConv: Conversation = {
        id: conversationId,
        contactName: newConvDefaults.contactName || cleanContact,
        contactPhone: newConvDefaults.contactPhone || cleanContact,
        lastMessage,
        lastMessageTime,
        unreadCount: incrementUnread ? 1 : 0,
        channel: newConvDefaults.channel || (isWhatsApp ? 'WHATSAPP' : 'SMS'),
        metadata: newConvDefaults.metadata || {},
        ...newConvDefaults,
        // Ensure these are always from the parameters, not overridden by defaults
      };
      // Override with parameter values (in case newConvDefaults had lastMessage/lastMessageTime)
      newConv.lastMessage = lastMessage;
      newConv.lastMessageTime = lastMessageTime;

      dbg.convFetchTriggered('prependOrUpdateConversation — created new conv from message data: ' + conversationId);
      set({ conversations: [newConv, ...existing] });

      // Lazy-fetch inbox details (label, assignee, pinned msg) for this single conversation
      fetchInboxDetailsForConversations([newConv], set, get);
    } else {
      // No defaults available (e.g. msg-status for a very old conversation) — skip.
      // Don't reload the entire list, just log it.
      dbg.convFetchTriggered('prependOrUpdateConversation — conv not found, no defaults, skipping: ' + conversationId);
    }
  },

  togglePin: (conversationId) => {
    const settings = useSettingsStore.getState();
    const current = settings.pinnedConversationIds;
    const target = normalizeConvId(conversationId);
    const isPinned = current.some((id) => normalizeConvId(id) === target);

    // Pin cap mirrors legacy (`chatMoreOptionItemOnClick`). Unpin is always
    // allowed, so only the pin direction is gated.
    if (!isPinned && current.length >= MAX_PINNED_CHATS) {
      toast.error(`You can pin up to ${MAX_PINNED_CHATS} chats.`);
      return;
    }

    const next = isPinned
      ? current.filter((id) => normalizeConvId(id) !== target)
      : [...current, conversationId];

    // Persist the personal setting (source of truth for pin state).
    settings.setPinnedConversationIds(next);

    // Mirror onto the in-memory conversations so consumers reading
    // `conversation.pinned` stay consistent within this session.
    const active = get().activeConversation;
    set({
      conversations: get().conversations.map((c) =>
        c.id === conversationId ? { ...c, pinned: !isPinned } : c
      ),
      activeConversation:
        active?.id === conversationId ? { ...active, pinned: !isPinned } : active,
    });
  },

  pinMessage: (conversationId, messageId) => {
    const active = get().activeConversation;
    set({
      conversations: get().conversations.map((c) =>
        c.id === conversationId ? { ...c, pinnedMessageId: messageId ?? undefined } : c
      ),
      activeConversation:
        active?.id === conversationId ? { ...active, pinnedMessageId: messageId ?? undefined } : active,
    });
  },

  removeConversation: (conversationId) => {
    const active = get().activeConversation;
    set({
      conversations: get().conversations.filter((c) => c.id !== conversationId),
      activeConversation: active?.id === conversationId ? null : active,
      messages: active?.id === conversationId ? [] : get().messages,
    });
  },

  updateConvLabel: async (conversationId, label, opts) => {
    // 'unreplied' is a virtual computed status (from lastMessageIsIncoming),
    // not a user-applied label — never allow setting it on a conversation.
    if (label === 'unreplied') {
      console.warn('[ConvStore] Blocked attempt to set label="unreplied" on conversation', conversationId);
      return;
    }

    // Enforce label permissions at the source of truth. The picker UI also
    // gates this, but any gap there (e.g. the always-on "No Label" clear, or
    // switching away from a locked label) must not become a persisted bypass:
    //  - removing/replacing the CURRENT label requires modify rights on it
    //  - applying a NEW label requires modify rights on that label
    // Admins resolve to full_access, so this is a no-op for them.
    const isClearing = !label || label === 'null';
    // A label the user just created (has Manage rights to create) is always
    // applicable by its creator — it has no explicit per-label permission yet,
    // so it would otherwise resolve to the (possibly restrictive) category
    // default and be wrongly blocked right after creation.
    const isNewLabel = opts?.isNewLabel === true;
    // Resolve the current label from the list OR the open (active) conversation,
    // so the "can't remove current label" guard still applies when a chat is
    // open but not present in the loaded list.
    const currentLabel = (
      get().conversations.find((c) => c.id === conversationId)
      ?? (get().activeConversation?.id === conversationId ? get().activeConversation : undefined)
    )?.label;
    const canModify = (key?: string | null): boolean =>
      !key || key === 'null' || labelPermissionAllowsModify(getLabelPermissionStandalone(key));
    if (currentLabel && currentLabel !== label && !canModify(currentLabel)) {
      console.warn('[ConvStore] Blocked label change — no permission to modify current label:', currentLabel);
      return;
    }
    if (!isClearing && !isNewLabel && !canModify(label)) {
      console.warn('[ConvStore] Blocked label apply — no permission for label:', label);
      return;
    }

    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    // Optimistically update UI
    const convs = get().conversations.map((c) =>
      c.id === conversationId ? { ...c, label } : c
    );
    set({ conversations: convs });

    // Also update active conversation if it matches
    const active = get().activeConversation;
    if (active?.id === conversationId) {
      set({ activeConversation: { ...active, label } });
    }

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      const conv = get().conversations.find((c) => c.id === conversationId)
        ?? (get().activeConversation?.id === conversationId ? get().activeConversation : undefined);
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_UPDATE_LABEL',
        data: {
          conversationID: conversationId,
          contactId: conv?.contactPhone || null,
          label,
        },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });

      // SaaS sync callback
      _handleAfterLabelUpdated(conversationId, conv?.contactPhone || null, label);
    } catch (error) {
      console.error('[ConvStore] Failed to update label:', error);
    }
  },

  updateConvAssignee: async (conversationId, assigneeEmail) => {
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    // Find the conversation to get metadata for additionalData
    const conv = get().conversations.find((c) => c.id === conversationId)
      ?? (get().activeConversation?.id === conversationId ? get().activeConversation : undefined);
    const oldAssignee = conv?.assigneeEmail;

    // Optimistically update UI
    const convs = get().conversations.map((c) =>
      c.id === conversationId ? { ...c, assigneeEmail } : c
    );
    set({ conversations: convs });

    // Also update active conversation if it matches
    const active = get().activeConversation;
    if (active?.id === conversationId) {
      set({ activeConversation: { ...active, assigneeEmail } });
    }

    try {
      // Build additionalData matching old app's format
      const additionalData: Record<string, unknown> = {
        assigneeEmail,
        id: conversationId,
        from: conv?.metadata?.from || '',
        to: conv?.contactPhone || '',
        channel: conv?.channel || '',
        channelId: conv?.metadata?.channelId || '',
        updatedDatetime: Date.now(),
        name: conv?.contactName || conv?.contactPhone || '',
        lastMessage: {
          msgResolvedText: conv?.lastMessage || '',
          _uaCreatedTime: conv?.lastMessageTime ? new Date(conv.lastMessageTime).getTime() : Date.now(),
        },
      };
      if (oldAssignee && oldAssignee !== 'unassigned') {
        additionalData.oldAssigneeEmail = oldAssignee;
      }

      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_UPDATE_ASSIGNEE',
        data: {
          conversationID: conversationId,
          contactId: conv?.contactPhone || null,
          assigneeEmail,
          additionalData,
        },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });

      // SaaS sync callback
      _handleAfterAssigneeUpdated(conversationId, conv?.contactPhone || null, assigneeEmail);
    } catch (error) {
      console.error('[ConvStore] Failed to update assignee:', error);
    }
  },

  autoAssignIfUnassigned: async (conversationId, assigneeEmail) => {
    const path = getInboxConvDocPath(conversationId);
    if (!path) return;
    const snap = await getDocFromServer(doc(db, path));
    const serverAssignee = snap.exists() ? snap.data().assigneeEmail : undefined;
    if (typeof serverAssignee === 'string' && serverAssignee && serverAssignee !== 'unassigned') {
      get().updateConversation(conversationId, { assigneeEmail: serverAssignee });
      return;
    }
    await get().updateConvAssignee(conversationId, assigneeEmail);
  },

  stopActiveFlow: async (conversationId) => {
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    const conv = get().conversations.find((c) => c.id === conversationId);
    const prevFlow = conv?.activeFlow ?? null;

    // Optimistically unlock the composer (clear the marker locally).
    const convs = get().conversations.map((c) =>
      c.id === conversationId ? { ...c, activeFlow: undefined } : c
    );
    set({ conversations: convs });
    const active = get().activeConversation;
    if (active?.id === conversationId) {
      set({ activeConversation: { ...active, activeFlow: undefined } });
    }

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_STOP_ACTIVE_FLOW',
        data: {
          conversationId,
          contactId: conv?.contactPhone || null,
        },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to stop active flow:', error);
      // Roll back the optimistic unlock so the UI reflects reality.
      const rolledBack = get().conversations.map((c) =>
        c.id === conversationId ? { ...c, activeFlow: prevFlow ?? undefined } : c
      );
      set({ conversations: rolledBack });
      const active2 = get().activeConversation;
      if (active2?.id === conversationId) {
        set({ activeConversation: { ...active2, activeFlow: prevFlow ?? undefined } });
      }
    }
  },

  addCustomLabel: async (key, label) => {
    // Enforce the "Manage Labels" permission at the source of truth — mirrors
    // the picker's canCreateLabels: allowed only when the group-wide Manage
    // Labels toggle is on (which also honours a legacy full_access category
    // default). Admins => always allowed.
    const canCreate = getCanManageLabelsStandalone();
    if (!canCreate) {
      console.warn('[ConvStore] Blocked label creation — user lacks Manage (create) permission');
      return;
    }
    // A custom label must never take a system status key (e.g. "Hold" → 'hold'):
    // it would override the built-in status and could then be deleted as custom.
    if (isSystemLabelKey(key)) {
      console.warn('[ConvStore] Refused to create a label with a system key:', key);
      return;
    }

    // Optimistically add to local labels (a re-created key is no longer "removed")
    const labels = { ...get().chatLabels, [key]: label };
    const deletedChatLabels = { ...get().deletedChatLabels };
    delete deletedChatLabels[key];
    set({ chatLabels: labels, deletedChatLabels });

    // Persist to Firebase
    try {
      const appStore = useAppStore.getState();
      const extensionName = appStore.extensionName;
      const orgId = appStore.appConfig?.orgId;
      const saasCode = appStore.appCodeSAAS;
      if (!extensionName || !orgId || !saasCode) return;

      const orgPath = `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}`;
      // Write ONLY this label's key (a merge set treats the nested key literally,
      // dots included). Re-sending every label from local state would resurrect any
      // label another session deleted that this client still holds.
      // Also clear a deleted-label record for this key, so a re-created label
      // doesn't linger in Kanban's "Removed labels" column.
      await setDoc(
        doc(db, orgPath),
        { ua_conv_chat_labels_list: { [key]: label }, [DELETED_LABELS_FIELD]: { [key]: deleteField() } },
        { merge: true }
      );
      // Sync IDB cache
      const idbKey = `chat_labels_${extensionName}_${orgId}`;
      idbCache.set('org_data', idbKey, labels, 24 * 60 * 60 * 1000).catch(() => {});
    } catch (error) {
      console.error('[ConvStore] Failed to save custom label:', error);
    }
  },

  deleteCustomLabel: async (key) => {
    const fail = (message = "Couldn't delete the label. Please try again.") => toast.error(message);
    // Role gate: the group-wide "Delete Labels" toggle — separate from Manage
    // Labels (add) and off by default. Admins resolve to allowed inside
    // getCanDeleteLabelsStandalone; non-admins read as not allowed until their
    // permissions finish loading.
    if (!getCanDeleteLabelsStandalone()) {
      console.warn('[ConvStore] Blocked label deletion — user lacks Delete Labels permission');
      fail();
      return;
    }
    const existing = get().chatLabels[key];
    // Only custom labels are deletable. System statuses (Open/On Hold/Closed/
    // Blocked) and the virtual 'unreplied' are built-in — checked by KEY, since
    // an org-stored override of a system key may not carry systemClass.
    if (!existing || isSystemLabelKey(key)) {
      console.warn('[ConvStore] Refused to delete system/unknown label:', key);
      return;
    }
    // Also requires modify-level access to THIS label (View & Apply), so a
    // view-only label can't be deleted. Admins resolve to full_access.
    if (!labelPermissionAllowsModify(getLabelPermissionStandalone(key))) {
      console.warn('[ConvStore] Refused to delete view-only label:', key);
      fail();
      return;
    }

    // App context must be ready BEFORE the optimistic removal — otherwise we'd
    // drop the label from the UI with no way to persist it and no rollback.
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    const orgId = appStore.appConfig?.orgId;
    const saasCode = appStore.appCodeSAAS;
    if (!extensionName || !orgId || !saasCode) {
      console.warn('[ConvStore] Cannot delete label — app context not ready');
      fail();
      return;
    }
    const orgPath = `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}`;
    // The labels on screen came from the doc the listener watches. If the org
    // context moved since (the background org correction can change orgId
    // in-session), orgPath points at ANOTHER org's list, and deleting there would
    // remove that org's label with the same key. Re-point the listener and let the
    // user retry once the right list has loaded.
    if (subscribedLabelsPath && subscribedLabelsPath !== orgPath) {
      console.warn('[ConvStore] Label list is from a different org doc — refusing delete until it refreshes');
      subscribeChatLabels(set);
      fail('Labels are refreshing. Please try again in a moment.');
      return;
    }

    // Optimistically drop it from memory AND the IDB cache: fetchConversations
    // repaints chatLabels from IDB, so a fetch finishing during the await below
    // would otherwise bring the label back.
    const idbKey = `chat_labels_${extensionName}_${orgId}`;
    const writeCache = (value: Record<string, ConvChatLabel>) =>
      idbCache.set('org_data', idbKey, value, 24 * 60 * 60 * 1000).catch(() => {});
    const labels = { ...get().chatLabels };
    delete labels[key];
    // Remember the deleted label (Kanban's "Removed labels" column lists chats
    // that still carry it), keeping only the MAX_DELETED_LABELS most recent.
    const prevDeleted = get().deletedChatLabels;
    const deletedEntry: DeletedChatLabel = {
      display_label: existing.display_label || key,
      deletedAt: Date.now(),
      ...(existing.color ? { color: existing.color } : {}),
    };
    const pruned = Object.entries(prevDeleted)
      .filter(([k]) => k !== key)
      .sort(([, a], [, b]) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0))
      .slice(MAX_DELETED_LABELS - 1)
      .map(([k]) => k);
    const nextDeleted: Record<string, DeletedChatLabel> = { ...prevDeleted, [key]: deletedEntry };
    for (const k of pruned) delete nextDeleted[k];
    set({ chatLabels: labels, deletedChatLabels: nextDeleted });
    writeCache(labels);

    try {
      // Delete ONLY this key from the stored map. Use a FieldPath so `key` is one
      // LITERAL segment — a dotted string ('...list.' + key) would be split on every
      // '.', so a label like "V.I.P" (key "v.i.p") would target a non-existent nested
      // path (a silent no-op) or, worse, strip a field off an unrelated label. Never
      // rewrite the whole ua_conv_chat_labels_list from local state — that would wipe
      // any custom labels this client hasn't synced (concurrent adds, partial loads).
      await updateDoc(
        doc(db, orgPath),
        new FieldPath('ua_conv_chat_labels_list', key), deleteField(),
        new FieldPath(DELETED_LABELS_FIELD, key), deletedEntry,
        ...pruned.flatMap((k) => [new FieldPath(DELETED_LABELS_FIELD, k), deleteField()]),
      );
      // A repaint that read the cache before the optimistic write can still land
      // during the await — make sure the label stays gone from memory and cache.
      if (Object.prototype.hasOwnProperty.call(get().chatLabels, key)) {
        const current = { ...get().chatLabels };
        delete current[key];
        set({ chatLabels: current });
        writeCache(current);
      }
      // The inbox filter pill for this label is gone — don't leave the list
      // silently filtered by a label that no longer exists.
      if (get().filterLabel === key) get().setFilterLabel(null);
      // Chats still tagged with this label keep their stored value and render as
      // an orphan badge (see conv-label-picker) until re-tagged — we deliberately
      // do NOT bulk-reassign every conversation.
    } catch (error) {
      console.error('[ConvStore] Failed to delete custom label:', error);
      // Roll back memory and cache so the UI matches the server, and say so.
      const restored = { ...get().chatLabels, [key]: existing };
      set({ chatLabels: restored, deletedChatLabels: prevDeleted });
      writeCache(restored);
      fail();
    }
  },

  ensureChatLabelsLoaded: async () => {
    // If we only have system defaults, custom labels haven't been fetched yet.
    const current = get().chatLabels;
    const hasOnlyDefaults = Object.keys(current).every((k) => k in DEFAULT_CHAT_LABELS);
    if (hasOnlyDefaults) {
      await fetchChatLabels(set);
    } else {
      // Already loaded — still ensure the live subscription is active so labels
      // added by another session sync into e.g. the permission settings editor.
      subscribeChatLabels(set);
    }
  },

  addNote: async (conversationId, body, from, to, channel, mentionedEmails, attachment) => {
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    const noteId = `ua-note-${Date.now()}`;
    const channelId = from.replace(/\D/g, '');

    // Current user as note author
    const currentUser = useAuthStore.getState().user;
    const effectiveUid = getEffectiveUid();
    const noteAuthor = currentUser
      ? { name: currentUser.displayName || currentUser.email?.split('@')[0] || '', email: currentUser.email || '', uid: effectiveUid || '' }
      : undefined;

    // Media notes follow the legacy schema: the media kind prefixes the note
    // type (`image--note`, `file--note`, …) exactly like the old app wrote them,
    // so both readers still match on "--note".
    const noteType = attachment ? `${attachment.type}--note` : 'text--note';

    // Optimistically add note as a message
    const noteMsg: Message = {
      id: noteId,
      conversationId,
      from,
      to,
      body,
      channel,
      direction: 'outbound',
      status: 'sent',
      timestamp: new Date(),
      isNote: true,
      noteType,
      noteAuthor,
      ...(attachment && { attachments: [attachment] }),
    };
    get().addMessage(noteMsg);

    // Build source object for note author attribution
    const orgId = appStore.appConfig?.orgId;
    const saasUserId = appStore.appConfig?.saasUserId;
    const source = {
      email: currentUser?.email || '',
      id: effectiveUid || '',
      name: currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
      pic: currentUser?.photoURL || '',
      type: 'uaapp',
      uaApp: extensionName,
      ...(orgId && { uaAppSaaSOrgID: orgId }),
      ...(saasUserId && { uaAppSaaSUserId: saasUserId }),
      itsMsgSource: true,
    };

    // Build the note data matching old app's schema
    const noteData = {
      id: noteId,
      text: body,
      chennalId: channelId,
      channel,
      isIncoming: false,
      from,
      to,
      createdTime: Date.now(),
      status: 'note-added',
      isUnRead: null,
      attachments: attachment
        ? [{
            type: attachment.type,
            url: attachment.url,
            ...(attachment.contentType && { contentType: attachment.contentType }),
            ...(attachment.name && { name: attachment.name }),
            // Intrinsic size + bytes travel with the doc so every later load can
            // reserve the image's box before it downloads.
            ...(attachment.width && { width: attachment.width }),
            ...(attachment.height && { height: attachment.height }),
            ...(attachment.size && { size: attachment.size }),
          }]
        : [],
      conversationID: conversationId,
      type: noteType,
      __IS_UA_CONVERTED: true,
      source,
    };

    try {
      const userId = getEffectiveUid();
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_ADD_OUTGOING_MESSAGE_INBOX',
        data: {
          userId,
          originService: 'ulgebra_chat_ui_waba',
          conversationId,
          contactId: toContactId(to),
          channelId,
          messageId: noteId,
          messageData: noteData,
          contactIdType: 'phone',
        },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });

      // Write mentions to UA_INBOX_MENTIONS/{email}/MENTIONED_MESSAGES/{docId}
      if (mentionedEmails && mentionedEmails.length > 0) {
        const mentionsPath = getInboxMentionsCollectionPath();
        if (mentionsPath) {
          await Promise.allSettled(
            mentionedEmails.map(async (email) => {
              const mentionDocRef = doc(collection(db, mentionsPath, email, 'MENTIONED_MESSAGES'));
              await setDoc(mentionDocRef, {
                conversationId,
                noteId,
                noteBody: body,
                mentionedBy: noteAuthor?.email || '',
                mentionedByName: noteAuthor?.name || '',
                createdTime: Date.now(),
              });
            })
          );
        }
      }
    } catch (error) {
      console.error('[ConvStore] Failed to add note:', error);
      // Mark note as failed
      const msgs = get().messages;
      set({
        messages: msgs.map((m) =>
          m.id === noteId ? { ...m, status: 'failed' as const } : m
        ),
      });
    }
  },

  fetchActiveConvDetails: async () => {
    const active = get().activeConversation;
    if (!active) return;

    const path = getInboxConvDocPath(active.id);
    if (!path) return;

    try {
      const snap = await getDoc(doc(db, path));
      if (snap.exists()) {
        const data = snap.data();
        const updates: Partial<Conversation> = {};
        if (data.label) updates.label = data.label;
        if (data.assigneeEmail) updates.assigneeEmail = data.assigneeEmail;
        // Chat Automation flow marker — present while a flow is paused mid-run,
        // cleared (null/absent) when it ends. Always assigned so a finished flow
        // unlocks the composer.
        updates.activeFlow = parseActiveFlow(data._activeFlow) ?? undefined;
        // KAN-155 opt-out — same doc, so it costs no extra read. Always assigned
        // so a START unlocks the composer.
        updates.optOut = parseOptOut(data.optOut) ?? undefined;

        // Read pinned message from inbox doc
        const pinnedMessage = data.pinnedMessage as Record<string, unknown> | undefined;
        if (pinnedMessage && typeof pinnedMessage === 'object' && Object.keys(pinnedMessage).length > 0) {
          const pinMeta = pinnedMessage.pinned as Record<string, unknown> | undefined;
          const pinnedMsgId = (pinMeta?.originalMsgId as string) || (pinnedMessage.sid as string) || (pinnedMessage.id as string);
          if (pinnedMsgId) {
            updates.pinnedMessageId = pinnedMsgId;
            updates.metadata = { ...active.metadata, ...updates.metadata, pinnedMessageData: pinnedMessage };
          }
        }

        // Read draft for active conversation
        const userUid = useAuthStore.getState().user?.uid;
        const effUid = getEffectiveUid();
        const draftKey = `messageDraft_${userUid || effUid}`;
        const draftText = data[draftKey];
        if (draftText && typeof draftText === 'string' && draftText.trim()) {
          // Only set if no local draft already exists (local takes priority)
          if (!get().drafts[active.id]) {
            set((s) => ({ drafts: { ...s.drafts, [active.id]: draftText } }));
          }
        }

        // Reminder: __userNextReminder_{uid} = { id, time } when active
        const rKey = `__userNextReminder_${userUid || effUid}`;
        const reminderData = data[rKey];
        if (reminderData && typeof reminderData === 'object' && reminderData.time) {
          let reminderSubject: string | undefined;
          let reminderCreatedTime: number | undefined;
          // The pointer outlives the reminder — the server clears it on
          // mark-as-read and on cancel/complete, but not when one fires. Trust
          // the task's own status so a done reminder stops claiming the chat.
          let stillActive = true;
          // Fetch reminder subject + createdTime from REMINDERS_TASKS/{eventId}
          if (reminderData.id && effUid) {
            try {
              const appSt = useAppStore.getState();
              const extName = appSt.extensionName;
              const tBase = extName?.includes('for') ? extName.split('for')[0] : extName;
              const reminderUid = userUid || effUid;
              if (tBase) {
                const taskPath = `ulgebraUsers/${effUid}/extensions/${tBase}forpage/UA_INBOX_TASKS/${reminderUid}/REMINDERS_TASKS/${reminderData.id}`;
                const taskSnap = await getDoc(doc(db, taskPath));
                if (taskSnap.exists()) {
                  const taskData = taskSnap.data();
                  if (taskData.status && taskData.status !== 'scheduled') {
                    stillActive = false;
                  } else {
                    reminderSubject = taskData.subject || taskData.text || undefined;
                    reminderCreatedTime = taskData.createdTime || undefined;
                  }
                }
                // Missing doc is not treated as stale: the cache-first wrapper
                // reads a never-seen task as absent on the first open.
              }
            } catch {
              // Reminder task fetch failed silently
            }
          }
          updates.metadata = stillActive
            ? {
                ...active.metadata,
                hasReminder: true,
                reminderEventId: reminderData.id,
                reminderTime: reminderData.time,
                reminderSubject,
                reminderCreatedTime,
              }
            : {
                ...active.metadata,
                hasReminder: false,
                reminderEventId: undefined,
                reminderTime: undefined,
                reminderSubject: undefined,
                reminderCreatedTime: undefined,
              };
        }

        if (Object.keys(updates).length > 0) {
          // Update activeConversation
          const currentActive = get().activeConversation;
          if (currentActive?.id === active.id) {
            set({ activeConversation: { ...currentActive, ...updates } });
          }
          // Also update in conversations array if present
          const convs = get().conversations;
          const idx = convs.findIndex((c) => c.id === active.id);
          if (idx >= 0) {
            const updatedConvs = [...convs];
            updatedConvs[idx] = { ...updatedConvs[idx], ...updates };
            set({ conversations: updatedConvs });
          }
        }
      }
    } catch {
      // Silently fail — conversation details are optional
    }
  },

  cancelScheduledMessage: async (messageId) => {
    const tpaModule = useModuleStore.getState().tpaModule;
    if (!tpaModule || !tpaModule.cancelScheduledMessage) {
      return { success: false, error: 'Cancel not supported' };
    }

    try {
      const result = await tpaModule.cancelScheduledMessage(messageId);
      if (result.success) {
        // Update the message status to 'failed' (canceled maps to failed in our status model)
        const messages = get().messages;
        const cancelledMsg = messages.find((m) => m.id === messageId);
        set({
          messages: messages.map((m) =>
            m.id === messageId ? { ...m, status: 'failed' as const, metadata: { ...m.metadata, cancelledAt: new Date().toISOString() } } : m
          ),
        });

        // Update conversation metadata so the conversation list shows cancelled icon
        if (cancelledMsg?.conversationId) {
          const convs = get().conversations;
          const active = get().activeConversation;
          const convId = cancelledMsg.conversationId;
          const updates = {
            metadata: {
              ...(convs.find((c) => c.id === convId)?.metadata || {}),
              lastMessageStatus: 'failed',
              lastMessageDirection: 'outbound',
              lastMessageCancelled: true,
            },
          };
          set({
            conversations: convs.map((c) => c.id === convId ? { ...c, ...updates } : c),
            activeConversation: active?.id === convId ? { ...active, ...updates } : active,
          });
        }
      }
      return result;
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to cancel' };
    }
  },

  searchByPhone: async (phone) => {
    const tpaModule = useModuleStore.getState().tpaModule;
    if (!tpaModule) return;

    // If phone is empty, reset to normal view
    if (!phone.trim()) {
      set({ phoneSearchActive: false, searchQuery: '' });
      get().fetchConversations();
      return;
    }

    // Support multiple phone numbers (comma-separated) — fetch conversations
    // for each number in parallel and merge/deduplicate the results.
    // KAN-50: conversation ids embed the country code, so a bare local number
    // must also be searched with the default CC applied. The number is kept
    // as typed too — the query may already carry a foreign or fixed-line CC
    // that applyCountryCode cannot recognize (mobile-only metadata) and would
    // otherwise corrupt by prepending the default CC a second time.
    const defaultCC = useSettingsStore.getState().preferences.defaultCountryCode;
    const phones = Array.from(new Set(
      phone.split(',').flatMap((p) => [
        p.trim().replace(/\D/g, ''),
        applyCountryCode(p.trim(), defaultCC).replace(/\D/g, ''),
      ]).filter(Boolean)
    ));
    if (phones.length === 0) return;

    const { activeSender } = get();
    const ext = useAppStore.getState().extensionName || '';
    const isFirestoreTPA = tpaModule.id === 'whatsapp';

    // Show cached results instantly for REST TPAs
    let hasCachedConversations = false;
    if (!isFirestoreTPA && ext) {
      // Check cache for each phone number
      const allCachedConvs: Conversation[] = [];
      for (const p of phones) {
        const cacheKey = cacheKeys.conversations(ext, { search: p });
        const cached = await idbCache.get<Awaited<ReturnType<typeof tpaModule.getConversations>>>('proxy_cache', cacheKey);
        if (cached?.conversations?.length) {
          for (const c of cached.conversations) {
            if (!allCachedConvs.some(ec => ec.id === c.id)) allCachedConvs.push(c);
          }
        }
      }
      if (allCachedConvs.length > 0) {
        set({ conversations: allCachedConvs, loading: false, phoneSearchActive: true, searchQuery: phone });
        hasCachedConversations = true;
      }
    }

    if (!hasCachedConversations) {
      set({ loading: true, error: null, phoneSearchActive: true, searchQuery: phone });
    }
    try {
      const results = await Promise.all(
        phones.map((p) => {
          const queryOpts = {
            contactNumber: p,
            channel: activeSender?.channel || undefined,
            senderNumber: activeSender?.phoneNumber || undefined,
          };
          // Use SWR for REST TPAs
          if (!isFirestoreTPA && ext) {
            return swrFetch({
              key: cacheKeys.conversations(ext, { search: p }),
              store: 'proxy_cache' as const,
              fetcher: () => tpaModule.getConversations(queryOpts),
              ttl: TTL.CONVERSATIONS,
              onRevalidate: (fresh) => {
                if (!fresh?.conversations?.length) return;
                // Update existing conversations in-place — preserve inbox details
                const existing = get().conversations;
                const freshMap = new Map(fresh.conversations.map(c => [c.id, c]));
                let changed = false;
                const updated = existing.map(ec => {
                  const fc = freshMap.get(ec.id);
                  if (!fc) return ec;
                  // Only update if lastMessage actually changed
                  const ecTime = ec.lastMessageTime instanceof Date ? ec.lastMessageTime.getTime() : Number(ec.lastMessageTime) || 0;
                  const fcTime = fc.lastMessageTime instanceof Date ? fc.lastMessageTime.getTime() : Number(fc.lastMessageTime) || 0;
                  if (fcTime > ecTime) {
                    changed = true;
                    // Keep the higher unread — REST fc always reports 0.
                    return { ...ec, lastMessage: fc.lastMessage, lastMessageTime: fc.lastMessageTime, unreadCount: Math.max(ec.unreadCount || 0, fc.unreadCount || 0) };
                  }
                  return ec;
                });
                // Add new conversations not in existing
                for (const fc of fresh.conversations) {
                  if (!existing.some(c => c.id === fc.id)) {
                    updated.push(fc);
                    changed = true;
                  }
                }
                if (changed) {
                  set({ conversations: updated });
                  // Fetch inbox details for new conversations only
                  const newConvs = updated.filter(c => !existing.some(e => e.id === c.id));
                  if (newConvs.length > 0) {
                    fetchInboxDetailsForConversations(newConvs, set, get);
                  }
                }
              },
            }).catch(() => null);
          }
          return tpaModule.getConversations(queryOpts).catch(() => null);
        })
      );

      // Merge and deduplicate by conversation ID
      const seen = new Set<string>();
      const allConvs: Conversation[] = [];
      for (const result of results) {
        for (const conv of result?.conversations || []) {
          if (!seen.has(conv.id)) {
            seen.add(conv.id);
            allConvs.push(conv);
          }
        }
      }

      // Phone search results — no pagination needed
      set({
        conversations: allConvs,
        nextCursor: null,
        hasMore: false,
        loading: false,
      });

      // Lazy-fetch inbox details for results
      fetchInboxDetailsForConversations(allConvs, set, get);
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to search conversations',
      });
    }
  },

  // ── Viewport-based media resolution ─────────────────────────────
  resolveMessageMedia: (messageId: string) => {
    const msg = get().messages.find((m) => m.id === messageId);
    if (!msg || !msg.metadata?.needsMediaResolve || !msg.metadata?.mediaListUri) return;

    // Resolve this single message's media
    resolveMediaInStore([msg], set, get);
  },

  // ── UA_INBOX_UTILITY methods ───────────────────────────────────────────

  markMessageAsReadInConversation: async (conversationId, contactId) => {
    if (!conversationId || !contactId) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    // RingCentral does not support server-side read receipt updates
    const appCodeTPA = appStore.appCodeTPA;
    if (appCodeTPA?.toLowerCase().includes('ringcentral')) return;

    // Short-window dedup — only catches truly rapid bursts (e.g. React
    // StrictMode double-mount, two effects committing in the same tick).
    // The longer 2-second debounce that used to live here was swallowing
    // legitimate "new message arrived between clicks → mark as read"
    // calls; the proper protection now lives at the `markAsRead` entry
    // point which gates on a snapshot of unreadCount > 0 before firing.
    const lastAt = _lastReadCallAt.get(conversationId) ?? 0;
    const now = Date.now();
    if (now - lastAt < 200) return;
    _lastReadCallAt.set(conversationId, now);

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_UPDATE_CONVERSATION_READ',
        // NOTE: `lastMessageIndex` intentionally omitted — server reads the
        // current `messagesCount` from the conv doc and sets
        // `__userLastReadCount_{uid}` to that value. Sending a client-known
        // index was racy (stale metadata after realtime inbounds).
        data: { conversationID: conversationId, contactId },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to mark as read:', error);
      // Let the next click retry immediately — the call didn't land.
      _lastReadCallAt.delete(conversationId);
    }
  },

  updateConvSubject: async (conversationId, subject) => {
    if (!conversationId || !subject) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    // Optimistic UI update
    const conv = get().conversations.find((c) => c.id === conversationId);
    const active = get().activeConversation;
    set({
      conversations: get().conversations.map((c) =>
        c.id === conversationId ? { ...c, contactName: subject } : c
      ),
      activeConversation: active?.id === conversationId ? { ...active, contactName: subject } : active,
    });

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_UPDATE_CONV_SUBJECT',
        data: { conversationID: conversationId, contactId: conv?.contactPhone || null, subject },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });

      // SaaS sync callback
      _handleAfterChatNameUpdated(conversationId, conv?.contactPhone || null, subject);
    } catch (error) {
      console.error('[ConvStore] Failed to update subject:', error);
    }
  },

  setPinnedMessage: async (conversationId, contactId, pinnedMessage) => {
    if (!conversationId || !contactId) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_SET_PINNED_MSG',
        data: { conversationID: conversationId, contactId, pinnedMessage: pinnedMessage || {} },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to set pinned message:', error);
    }
  },

  starMessage: async (conversationId, contactId, messageId, isStarred) => {
    if (!conversationId) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    // Optimistic UI update
    set((s) => ({
      starredMessages: { ...s.starredMessages, [messageId]: isStarred },
    }));

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_STAR_MESSAGE_UPDATE',
        data: { conversationID: conversationId, contactId, messageId, isStarred },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to star message:', error);
      // Revert on failure
      set((s) => ({
        starredMessages: { ...s.starredMessages, [messageId]: !isStarred },
      }));
    }
  },

  deleteConvOrMessage: async (type, conversationId, messageId = null) => {
    if (!conversationId || !type) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    // Optimistic UI removal
    if (type === 'conversation') {
      get().removeConversation(conversationId);
    } else if (type === 'message' && messageId) {
      set({ messages: get().messages.filter((m) => m.id !== messageId) });
    }

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_DELETE_MESSAGE',
        data: { type, conversationId: conversationId, messageId },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to delete:', error);
    }
  },

  setScheduledEvent: async (conversationId, contactId, reminder) => {
    if (!conversationId || !contactId) return false;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return false;

    const authState = useAuthStore.getState();
    const user = authState.user;
    const uid = user?.uid || getEffectiveUid();
    if (!uid) return false;

    const isReschedule = !!reminder.eventId;
    const now = Date.now();
    // A reschedule moves `performAt`; it does not re-create the reminder. Stamping
    // the current time here rewrote the task's creation time on the server, so the
    // drawer's "Set 3 days ago" flipped to "Set just now" and the original was
    // gone. Keep whatever the drawer already knows for this task.
    const knownCreated = reminder.eventId
      ? _knownReminder(reminder.eventId)?.createdTime
      : undefined;
    // `!== undefined`, not truthiness: a legacy document with no createdTime maps
    // to 0, and treating that as "unknown" restamps it with the current time —
    // the very rewrite this is here to prevent.
    const createdTime = isReschedule && knownCreated !== undefined ? knownCreated : now;
    const eventId = reminder.eventId || `scheduled_${uid}_${now}`;
    const subject = reminder.subject || 'Reminder';
    const conv =
      get().conversations.find((c) => c.id === conversationId) ??
      (get().activeConversation?.id === conversationId ? get().activeConversation : null);

    // The full task document, mirroring the legacy client
    // (public/js/firebase-things.js → doSetChatReminderOnclick). This shape is
    // load-bearing, not decoration: the server files the task as
    // REMINDERS_TASKS/{eventId} and stamps `__userNextReminder_{uid}` =
    // { id, time } from it. Send a partial payload and the callable still
    // answers {"status":"success"} while writing nothing at all — which is
    // exactly how reminders silently stopped working (KAN-50).
    const scheduledEventData: Record<string, unknown> = {
      eventId,
      conversationId,
      appCode: extensionName,
      uid,
      performAt: reminder.performAt,
      scheduleTime: reminder.performAt,
      eventType: 'chat-reminder',
      // `type` too — the inbox timeline reads the system message off `type`.
      type: 'chat-reminder',
      status: 'scheduled',
      createdTime,
      read: false,
      subject,
      performer: { uid, name: user?.displayName || '', email: user?.email || '' },
      eventBody: {
        conversationId,
        contactId,
        name: conv?.contactName || '',
        channelId: get().activeSender?.phoneNumber || '',
        channel: conv?.channel || '',
      },
      _ua_lic_adminUserID: authState.selectedAdminUserId ?? null,
      _appCode: extensionName,
      _uid: uid,
      _conversationId: conversationId,
      // The mirror of `createdTime`, like every other `_`-prefixed key mirrors its
      // twin. Sending `now` here left two answers to "when was this created" in
      // one document, and if the server reads this copy the preservation above is
      // undone with nothing visible on the client.
      _createdTime: createdTime,
      ...(isReschedule && { isRescheduled: true }),
    };

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_SET_SCHEDULED_EVENT',
        data: { conversationID: conversationId, contactId, scheduledEventData },
        _ua_lic_adminUserID: authState.selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });

      // Reflect it locally so the badge appears without waiting for a refetch.
      // reminderEventId is what cancel/complete later points at.
      const reminderMeta = {
        hasReminder: true,
        reminderEventId: eventId,
        reminderTime: reminder.performAt,
        reminderSubject: subject,
        reminderCreatedTime: createdTime,
      };
      set({
        conversations: get().conversations.map((c) =>
          c.id === conversationId ? { ...c, metadata: { ...c.metadata, ...reminderMeta } } : c
        ),
      });
      const active = get().activeConversation;
      if (active?.id === conversationId) {
        set({ activeConversation: { ...active, metadata: { ...active.metadata, ...reminderMeta } } });
      }
      // Put it in the drawer too, the way cancel/complete maintains the lists.
      // The panel and the bell dialog read `activeReminders`, not the badge, so
      // without this a second reminder on the same chat is missing from both
      // until an outside refresh lands.
      const upserted: PastReminder = {
        eventId,
        subject,
        performAt: reminder.performAt,
        createdTime,
        conversationId,
        contactId,
        status: 'scheduled',
        read: false,
      };
      set({
        activeReminders: [
          ...get().activeReminders.filter((r) => r.eventId !== eventId),
          upserted,
        ].sort((a, b) => a.performAt - b.performAt),
      });
      _syncChatRow(upserted, eventId);
      return true;
    } catch (error) {
      console.error('[ConvStore] Failed to set scheduled event:', error);
      return false;
    }
  },

  cancelOrCompleteReminder: async (conversationId, contactId, status, eventId) => {
    if (!conversationId || !contactId) return false;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return false;

    const conv =
      get().conversations.find((c) => c.id === conversationId) ??
      (get().activeConversation?.id === conversationId ? get().activeConversation : null);
    const meta = conv?.metadata ?? {};
    const targetEventId = eventId || (meta.reminderEventId as string | undefined);

    // Without the event id the server has nothing to match — it would accept
    // the call and change nothing. Fail loudly here instead.
    if (!targetEventId) {
      console.error('[ConvStore] Cannot %s reminder on %s — no eventId known', status, conversationId);
      return false;
    }

    // Re-send the stored document with the status flipped, the way the legacy
    // client does. Read it back first so fields we never held in memory
    // (performer, eventBody, appCode…) survive the round trip; fall back to
    // rebuilding from conversation metadata when the doc can't be read.
    let scheduledEventData: Record<string, unknown> | null = null;
    const tasksPath = getRemindersTasksPath();
    if (tasksPath) {
      try {
        // From the SERVER: the wrapper is cache-first, and a task this client has
        // never read resolves as "does not exist" — which used to drop us into the
        // rebuild below and send the wrong reminder's fields.
        const snap = await getDocFromServer(doc(db, `${tasksPath}/${targetEventId}`));
        if (snap.exists()) scheduledEventData = { ...(snap.data() as Record<string, unknown>) };
      } catch {
        // Fall through to the reconstruction below.
      }
    }
    if (!scheduledEventData) {
      // Rebuild from the DRAWER ROW for this exact eventId. The conversation's
      // badge metadata describes the chat's *next* reminder, which is a different
      // task as soon as a chat has more than one — cancelling the second used to
      // echo the first one's subject and due time into it, overwriting them.
      const known =
        _knownReminder(targetEventId) ||
        (meta.reminderEventId === targetEventId
          ? {
              subject: (meta.reminderSubject as string) || 'Reminder',
              performAt: meta.reminderTime as number | undefined,
              createdTime: meta.reminderCreatedTime as number | undefined,
              contactId,
            }
          : null);
      if (!known) {
        console.error(
          '[ConvStore] Cannot %s reminder %s — the stored task could not be read and nothing local describes it',
          status, targetEventId
        );
        return false;
      }
      const uid = useAuthStore.getState().user?.uid || getEffectiveUid() || '';
      scheduledEventData = {
        eventId: targetEventId,
        conversationId,
        appCode: extensionName,
        uid,
        performAt: known.performAt,
        scheduleTime: known.performAt,
        eventType: 'chat-reminder',
        type: 'chat-reminder',
        createdTime: known.createdTime,
        subject: known.subject || 'Reminder',
        eventBody: { conversationId, contactId: known.contactId || contactId },
      };
    }
    scheduledEventData.status = status;
    scheduledEventData.read = true;

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_SET_SCHEDULED_EVENT',
        data: { conversationID: conversationId, contactId, scheduledEventData },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });

      const clearedMeta = {
        hasReminder: false,
        reminderEventId: undefined,
        reminderTime: undefined,
        reminderSubject: undefined,
        reminderCreatedTime: undefined,
      };
      set({
        conversations: get().conversations.map((c) =>
          c.id === conversationId ? { ...c, metadata: { ...c.metadata, ...clearedMeta } } : c
        ),
      });
      const active = get().activeConversation;
      if (active?.id === conversationId) {
        set({ activeConversation: { ...active, metadata: { ...active.metadata, ...clearedMeta } } });
      }
      // Move it from Active to Past in place. Clearing pastReminders and waiting
      // for a refetch isn't enough: the Firestore wrapper answers from cache
      // first, so a refetch moments after the write hands back the pre-write
      // snapshot and the reminder is missing from Past until the next visit.
      // `_knownReminder`, not the drawer alone: the chat panel reads its own list
      // and the drawer is empty until the bell is opened, so on the ordinary
      // close path this was undefined and the reminder left Active without ever
      // arriving in Past.
      const closed = _knownReminder(targetEventId);
      const past = get().pastReminders;
      if (closed) _rememberClosed({ ...closed, status, read: true });
      set({
        pastReminders: closed
          ? [{ ...closed, status, read: true }, ...past.filter((r) => r.eventId !== targetEventId)]
              .sort((a, b) => b.performAt - a.performAt)
          : past.filter((r) => r.eventId !== targetEventId),
        activeReminders: get().activeReminders.filter((r) => r.eventId !== targetEventId),
      });
      _syncChatRow(null, targetEventId, true);
      return true;
    } catch (error) {
      console.error('[ConvStore] Failed to cancel/complete reminder:', error);
      return false;
    }
  },

  handleReminderEvent: (event) => {
    const meta = event.b as Record<string, unknown> | undefined;
    const task = event.z;
    if (!meta || typeof meta !== 'object' || meta.type !== 'chat-reminder') return;

    // Reminders are per-agent. On a shared/admin inbox every org member
    // receives the same push, and this uid check keeps each client to its own
    // reminders — legacy does the identical comparison (firebase-things.js:18313).
    const myUid = useAuthStore.getState().user?.uid;
    if (!task || !myUid || task.uid !== myUid) return;

    const eventId = (task.eventId as string) || '';
    const status = (meta.status as string) || (task.status as string) || '';
    // performAt is part of the key: a reschedule reuses the eventId, so keying
    // on id+status alone would swallow the SECOND fire of a rescheduled
    // reminder (same id, same 'completed').
    const dedupKey = `${eventId}:${status}:${task.performAt ?? ''}`;
    if (!_rememberReminderEvent(dedupKey)) return;

    const eventBody = task.eventBody as Record<string, unknown> | undefined;
    const conversationId =
      (eventBody?.conversationId as string) || (task.conversationId as string) || '';

    /** Clearing is only safe when the badge still points at THIS reminder — a
     *  replayed old push must not wipe the badge of a newer one set since. */
    const badgeIsThisReminder = (convId: string) => {
      const c = get().conversations.find((x) => x.id === convId) ||
        (get().activeConversation?.id === convId ? get().activeConversation : null);
      const current = c?.metadata?.reminderEventId as string | undefined;
      return !current || current === eventId;
    };
    const applyMeta = (patch: Record<string, unknown>) => {
      set({
        conversations: get().conversations.map((c) =>
          c.id === conversationId ? { ...c, metadata: { ...c.metadata, ...patch } } : c
        ),
      });
      const active = get().activeConversation;
      if (active?.id === conversationId) {
        set({ activeConversation: { ...active, metadata: { ...active.metadata, ...patch } } });
      }
    };
    const clearedMeta = {
      hasReminder: false,
      reminderEventId: undefined,
      reminderTime: undefined,
      reminderSubject: undefined,
      reminderCreatedTime: undefined,
    };

    if (status === 'completed') {
      // A push the user has ALREADY acknowledged must not ring again. The RTDB
      // listener attaches with onChildAdded(limitToLast(1)), so every page load
      // replays the last event, and schedule-task is exempt from the 10-second
      // staleness filter (use-realtime-listeners.ts) precisely so a reminder
      // that fired while the tab slept still surfaces. Without this check a
      // dismissed reminder chimed on every refresh. `read` is flipped by
      // markReminderAsRead, so it is the acknowledgement flag.
      const alreadySeen = task.read === true;
      if (!alreadySeen) playReminderChime();
      const subject = (task.subject as string) || 'Reminder';
      if (!alreadySeen) toast('Reminder due', {
        description: subject,
        // Persistent until acted on — legacy's popup never auto-dismissed
        // either. A reminder that evaporates in 30s defeats its own purpose
        // (live testing kept missing the window to click "Mark as read").
        duration: Infinity,
        ...(conversationId && {
          action: {
            label: 'Open',
            onClick: () => {
              useUIStore.getState().openChatDialog(conversationId);
              // Opening counts as seeing it — legacy marks read on open too
              // (updateChatReminderButton, firebase-things.js:21940).
              get().markReminderAsRead(task);
            },
          },
        }),
        cancel: {
          label: 'Mark as read',
          onClick: () => {
            get().markReminderAsRead(task);
          },
        },
      });
      if (badgeIsThisReminder(conversationId)) applyMeta(clearedMeta);
      // Move it Active → Past in place. Blanking `pastReminders` to force a
      // refetch does not work: the dialog fetches once per visit to the tab
      // (pastFetchedRef), so a fire while the Past tab is open emptied the list
      // and nothing refilled it until the user switched tabs.
      _moveReminderToPast(eventId, task, 'completed');
    } else if (status === 'scheduled') {
      // A push older than the reminder it describes is a REPLAY: the RTDB
      // listener re-delivers its last event on every attach, and schedule-task is
      // exempt from the staleness filter. Re-stamping a long-past reminder would
      // resurrect a badge for something already dealt with elsewhere.
      // A push we have no matching task for is not proof the reminder still
      // exists: the RTDB listener replays its last event on every attach, so a
      // reminder cancelled elsewhere keeps replaying as `scheduled`. Time cannot
      // separate the two — one due next week is never "old" — so an unknown push
      // is confirmed against the stored task before it is allowed to create
      // anything. A push for a reminder we already hold just updates it.
      const known = _knownReminder(eventId);
      if (!known) {
        void (async () => {
          const tasksPath = getRemindersTasksPath();
          if (!tasksPath || !eventId) return;
          try {
            const snap = await getDocFromServer(doc(db, `${tasksPath}/${eventId}`));
            if (!snap.exists()) return;
            if ((snap.data() as Record<string, unknown>).status !== 'scheduled') return;
          } catch {
            return; // cannot confirm → do not invent a reminder
          }
          _applyScheduledPush(eventId, task, conversationId, applyMeta);
        })();
        return;
      }

      _applyScheduledPush(eventId, task, conversationId, applyMeta);
    } else if (status === 'cancelled') {
      if (badgeIsThisReminder(conversationId)) applyMeta(clearedMeta);
      _moveReminderToPast(eventId, task, 'cancelled');
    }
  },

  markReminderAsRead: async (scheduledEvent) => {
    const extensionName = useAppStore.getState().extensionName;
    const eventBody = scheduledEvent.eventBody as Record<string, unknown> | undefined;
    const conversationId =
      (eventBody?.conversationId as string) || (scheduledEvent.conversationId as string) || '';
    // Same fallback every other read of this field uses — the reconstruction path
    // in cancelOrCompleteReminder writes documents with no eventBody.contactId,
    // and without it "Mark as read" returned silently, leaving `read` false so the
    // next page-load replay rang the reminder all over again.
    const contactId = (eventBody?.contactId as string) || (scheduledEvent.contactId as string) || '';
    if (!extensionName || !conversationId || !contactId) {
      console.error('[ConvStore] Cannot mark reminder as read — missing conversation or contact id');
      return;
    }

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_UPDATE_REMINDER_WITH_READ',
        data: {
          conversationID: conversationId,
          contactId,
          // Full document with read flipped, the way legacy sends it
          // (updatingReminderWithAsRead → setScheduledEventsInConversation).
          scheduledEventData: { ...scheduledEvent, read: true },
        },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to mark reminder as read:', error);
    }
  },

  fetchActiveReminders: async () => {
    const tasksPath = getRemindersTasksPath();
    if (!tasksPath) {
      // Not "no reminders" — we could not even ask (the app config is not ready
      // yet). Publishing a clean empty list here makes a chat that HAS a reminder
      // look free, so the panel offers "Set reminder" and the user files a
      // duplicate. Say the read failed so the badge is trusted instead.
      set({ activeRemindersLoading: false, activeRemindersFailed: true });
      return;
    }

    // One read at a time, and not more often than the TTL: ChatThread remounts on
    // every conversation click (it is keyed by conversation id), which otherwise
    // fired a fresh uncached page query per click.
    if (_activeRemindersInFlight) return _activeRemindersInFlight;
    // Keyed on "when did a read last succeed", not on whether the list has rows —
    // an account with no pending reminders would otherwise refetch on every single
    // conversation click, which is the case this guard exists for.
    if (_activeRemindersFetchedAt && Date.now() - _activeRemindersFetchedAt < ACTIVE_REMINDERS_TTL_MS) return;

    set({ activeRemindersLoading: true });
    const work = (async () => {
      try {
        // First page, from the server: the cache-first wrapper answers a
        // never-cached query with an EMPTY snapshot, which the drawer then paints
        // as "No active reminders" until the refresh bus fills it in.
        // No generation guard: `_activeRemindersInFlight` above means only one
        // run exists at a time, so there is no older result to discard.
        const page = await _fetchReminderPage(tasksPath, 'active', null);
        _activeRemindersCursor = page.cursor;
        _activeRemindersOrdered = page.ordered;
        _activeRemindersFetchedAt = Date.now();
        set({
          activeReminders: page.rows.slice().sort((a, b) => a.performAt - b.performAt),
          activeRemindersLoading: false,
          activeRemindersFailed: false,
          activeRemindersHasMore: page.hasMore,
        });
      } catch (error) {
        // Leave whatever we already had and say the read failed. Silently
        // publishing an empty list makes a chat with a reminder look reminder-free,
        // so the UI offers "Set reminder" and the user files a duplicate they can
        // never see or cancel (shared agents blocked by Firestore rules hit this).
        console.error('[ConvStore] Failed to fetch active reminders:', error);
        set({ activeRemindersLoading: false, activeRemindersFailed: true, activeRemindersHasMore: false });
      }
    })();
    _activeRemindersInFlight = work.finally(() => { _activeRemindersInFlight = null; });
    return _activeRemindersInFlight;
  },

  fetchChatReminders: async (conversationId) => {
    const tasksPath = getRemindersTasksPath();
    if (!conversationId) {
      // Returning without clearing the flag left the panel in its "still
      // answering" state for good, and that state means "trust the badge".
      set({ chatRemindersLoading: false });
      return;
    }
    if (!tasksPath) {
      set({ chatReminderConvId: conversationId, chatRemindersLoading: false, chatRemindersFailed: true });
      return;
    }
    // Reopened within the TTL: show what we already read. Writes and pushes keep
    // the cached rows current, so this cannot serve something the live list would
    // contradict.
    const cached = _chatRowsCache.get(conversationId);
    if (cached && Date.now() - cached.at < CHAT_REMINDERS_TTL_MS) {
      set({
        chatReminderConvId: conversationId,
        chatReminderRows: cached.rows,
        chatRemindersLoading: false,
        chatRemindersFailed: false,
      });
      return;
    }

    // Switching chats: clear the old rows immediately so the panel can never show
    // the previous conversation's reminders while this one loads.
    set({
      chatReminderConvId: conversationId,
      chatReminderRows: get().chatReminderConvId === conversationId ? get().chatReminderRows : [],
      chatRemindersLoading: true,
      chatRemindersFailed: false,
    });
    try {
      // TWO reads, because the field is not written consistently: the inbox and
      // legacy both stamp a top-level `conversationId`
      // (firebase-things.js:22252), but reminders created by an automation FLOW
      // carry it only inside `eventBody` — 8 of 65 documents on the test account.
      // Querying one field alone silently hides the other kind.
      // Each is a single equality filter, so neither needs a composite index;
      // `status` is narrowed below because a chat holds a handful of tasks.
      const ref = collection(db, tasksPath);
      // `status` is filtered SERVER-side, not below: reading a chat's documents
      // and keeping the scheduled ones means a chat with a long history returns a
      // page of closed tasks and an empty panel — which reads as "no reminder"
      // and produces a duplicate. Two equality filters need no composite index
      // (Firestore merges the single-field ones), verified against the live
      // collection.
      const [byTop, byBody] = await Promise.all([
        getDocsFromServer(query(ref,
          where('conversationId', '==', conversationId), where('status', '==', 'scheduled'), limit(CHAT_REMINDERS_LIMIT))),
        getDocsFromServer(query(ref,
          where('eventBody.conversationId', '==', conversationId), where('status', '==', 'scheduled'), limit(CHAT_REMINDERS_LIMIT))),
      ]);
      const seen = new Set<string>();
      const rows: PastReminder[] = [];
      const collect = (snapshot: typeof byTop) => snapshot.forEach((d) => {
        const data = d.data() as Record<string, unknown>;
        if (data.status !== 'scheduled' || seen.has(d.id)) return; // belt and braces
        seen.add(d.id);
        const eventBody = data.eventBody as Record<string, unknown> | undefined;
        rows.push({
          eventId: d.id,
          subject: (data.subject as string) || (data.text as string) || 'Reminder',
          performAt: (data.performAt as number) || 0,
          createdTime: (data.createdTime as number) || 0,
          conversationId: (data.conversationId as string) || (eventBody?.conversationId as string) || conversationId,
          contactId: (eventBody?.contactId as string) || (data.contactId as string) || '',
          status: 'scheduled',
          read: (data.read as boolean) ?? true,
        });
      });
      collect(byTop);
      collect(byBody);
      rows.sort((a, b) => a.performAt - b.performAt);
      _cacheChatRows(conversationId, rows);
      if (get().chatReminderConvId !== conversationId) return; // the user moved on
      set({ chatReminderRows: rows, chatRemindersLoading: false, chatRemindersFailed: false });
    } catch (error) {
      console.error('[ConvStore] Failed to fetch this chat\'s reminders:', error);
      if (get().chatReminderConvId !== conversationId) return;
      set({ chatRemindersLoading: false, chatRemindersFailed: true });
    }
  },

  loadMoreActiveReminders: async () => {
    const tasksPath = getRemindersTasksPath();
    if (!tasksPath || !_activeRemindersCursor || get().activeRemindersLoadingMore) return;
    set({ activeRemindersLoadingMore: true });
    try {
      const before = get().activeReminders.length;
      const page = await _fetchReminderPage(tasksPath, 'active', _activeRemindersCursor);
      if (_activeRemindersOrdered !== null && page.ordered !== _activeRemindersOrdered) {
        // The ordering flipped under us; this page cannot be trusted to continue
        // where the last one stopped. Stop paging rather than skip a range.
        set({ activeRemindersHasMore: false, activeRemindersLoadingMore: false });
        return;
      }
      _activeRemindersCursor = page.cursor ?? _activeRemindersCursor;
      // Merge by eventId — a reminder can arrive from the refresh bus while the
      // next page is in flight, and the same row must not appear twice.
      const byId = new Map(get().activeReminders.map((r) => [r.eventId, r] as const));
      for (const r of page.rows) byId.set(r.eventId, r);
      // A page that adds nothing new means the cursor is going in circles —
      // leaving the button armed would look broken. Also keep the freshness
      // stamp alive so the next TTL-guarded fetch does not discard these pages.
      const grew = byId.size > before;
      _activeRemindersFetchedAt = Date.now();
      set({
        activeReminders: [...byId.values()].sort((a, b) => a.performAt - b.performAt),
        activeRemindersHasMore: page.hasMore && grew,
        activeRemindersLoadingMore: false,
      });
    } catch (error) {
      console.error('[ConvStore] Failed to load more active reminders:', error);
      set({ activeRemindersLoadingMore: false });
    }
  },

  fetchPastReminders: async () => {
    const tasksPath = getRemindersTasksPath();
    if (!tasksPath) return;

    set({ pastRemindersLoading: true });
    try {
      // Filtered and ordered on the SERVER. Reading unfiltered docs and filtering
      // here meant Firestore returned the lowest ids — the oldest-created tasks —
      // so an account whose oldest page was all still scheduled saw "No past
      // reminders" however much history it had.
      const page = await _fetchReminderPage(tasksPath, 'past', null);
      _pastRemindersCursor = page.cursor;
      _pastRemindersOrdered = page.ordered;
      set({
        pastReminders: _withClosedThisSession(page.rows),
        pastRemindersLoading: false,
        pastRemindersHasMore: page.hasMore,
      });
    } catch (error) {
      console.error('[ConvStore] Failed to fetch past reminders:', error);
      set({ pastRemindersLoading: false, pastRemindersHasMore: false });
    }
  },

  loadMorePastReminders: async () => {
    const tasksPath = getRemindersTasksPath();
    if (!tasksPath || !_pastRemindersCursor || get().pastRemindersLoadingMore) return;
    set({ pastRemindersLoadingMore: true });
    try {
      const before = get().pastReminders.length;
      const page = await _fetchReminderPage(tasksPath, 'past', _pastRemindersCursor);
      if (_pastRemindersOrdered !== null && page.ordered !== _pastRemindersOrdered) {
        set({ pastRemindersHasMore: false, pastRemindersLoadingMore: false });
        return;
      }
      _pastRemindersCursor = page.cursor ?? _pastRemindersCursor;
      const byId = new Map(get().pastReminders.map((r) => [r.eventId, r] as const));
      for (const r of page.rows) byId.set(r.eventId, r);
      set({
        pastReminders: _withClosedThisSession([...byId.values()]),
        pastRemindersHasMore: page.hasMore && byId.size > before,
        pastRemindersLoadingMore: false,
      });
    } catch (error) {
      console.error('[ConvStore] Failed to load more past reminders:', error);
      set({ pastRemindersLoadingMore: false });
    }
  },

  fetchMentions: async () => {
    try {
      const mentionsPath = getInboxMentionsCollectionPath();
      const currentUserEmail = useAuthStore.getState().user?.email;
      if (!mentionsPath || !currentUserEmail) return;

      const mentionedMsgsRef = collection(db, mentionsPath, currentUserEmail, 'MENTIONED_MESSAGES');
      const mentionsSnap = await getDocs(query(mentionedMsgsRef, limit(100)));
      const mentionDocs: MentionDocument[] = [];
      mentionsSnap.forEach((d) => {
        const data = d.data();
        if (data.conversationId) {
          mentionDocs.push({
            id: d.id,
            conversationId: data.conversationId,
            noteId: data.noteId || '',
            noteBody: data.noteBody || '',
            mentionedBy: data.mentionedBy || '',
            mentionedByName: data.mentionedByName || '',
            createdTime: data.createdTime || 0,
            read: data.read ?? false,
          });
        }
      });

      // Mark conversations that have mentions
      const mentionedConvIds = new Set(mentionDocs.map((m) => m.conversationId));
      const updatedConvs = get().conversations.map((c) =>
        mentionedConvIds.has(c.id)
          ? { ...c, metadata: { ...c.metadata, isMentioned: true } }
          : c
      );
      set({ mentions: mentionDocs, conversations: updatedConvs });
    } catch {
      // Mentions fetch failed silently
    }
  },

  dismissMention: (mentionId) => {
    // Find the mention to get its data
    const mention = get().mentions.find((m) => m.id === mentionId);
    if (!mention || mention.read) return; // already read

    // Mark as read locally
    const updated = get().mentions.map((m) =>
      m.id === mentionId ? { ...m, read: true } : m
    );
    set({ mentions: updated });

    // Mark as read on server (matches old app: updateUserMentionedMessageWithAsRead)
    try {
      const mentionsPath = getInboxMentionsCollectionPath();
      const currentUserEmail = useAuthStore.getState().user?.email;
      if (mentionsPath && currentUserEmail) {
        // Update read flag in Firestore
        const docRef = doc(db, mentionsPath, currentUserEmail, 'MENTIONED_MESSAGES', mentionId);
        setDoc(docRef, { read: true }, { merge: true }).catch((err) =>
          console.error('[ConvStore] Failed to mark mention as read in Firestore:', err)
        );

        // Call cloud function to decrement unread count (same as old app UA_INBOX_UPDATE_MENTIONS_READ)
        const appStore = useAppStore.getState();
        const extensionName = appStore.extensionName;
        if (extensionName) {
          const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
          const { selectedAdminUserId } = useAuthStore.getState();
          callServiceAPI({
            appCode: extensionName,
            TPAService: extensionName.split('for')[0],
            mentionedEmail: currentUserEmail,
            action: 'UA_INBOX_UPDATE_MENTIONS_READ',
            data: { ...mention, read: true },
            ...(selectedAdminUserId && { _ua_lic_adminUserID: selectedAdminUserId }),
            headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
          }).catch((err) =>
            console.error('[ConvStore] Failed to update mention read status on server:', err)
          );
        }
      }
    } catch (err) {
      console.error('[ConvStore] dismissMention error:', err);
    }
  },

  removeNeedReplyLabel: async (conversationId) => {
    if (!conversationId) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_REMOVE_LABEL_NEED_REPLY',
        data: { conversationID: conversationId },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to remove need-reply label:', error);
    }
  },

  updateMessageDetails: async (conversationId, contactId, messageId, updateData) => {
    if (!conversationId) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_UPDATE_MESSAGE',
        data: { conversationID: conversationId, contactId, messageId, updateData },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to update message details:', error);
    }
  },

  storeSentMessageMeta: async (conversationId, contactId, messageId, resolvedText) => {
    // Skip WhatsApp — the WhatsApp webhook pipeline already writes the full
    // message doc to Firestore with author info. Only REST TPAs (Twilio,
    // MessageBird, Vonage, etc.) need this fallback write.
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName || extensionName.startsWith('whatsappfor')) return;
    if (!conversationId || !contactId || !messageId) return;

    // Build the source object — matches old app's getCurrentUserMessageSourceObj
    const authStore = useAuthStore.getState();
    const currentUser = authStore.user;
    const effectiveUid = getEffectiveUid();
    const orgId = appStore.appConfig?.orgId;
    const saasUserId = (appStore.appConfig as { saasUserId?: string } | null)?.saasUserId;
    const source = {
      itsMsgSource: true,
      type: 'uaapp',
      email: currentUser?.email || '',
      name: currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
      id: currentUser?.uid || effectiveUid || '',
      pic: currentUser?.photoURL || '',
      uaApp: extensionName,
      ...(orgId && { uaAppSaaSOrgID: orgId }),
      ...(saasUserId && { uaAppSaaSUserId: saasUserId }),
    };

    const updateData: Record<string, unknown> = {
      source,
      _uaCreatedTime: Date.now() - 5000,
    };

    // Only include resolved text for specific admin UIDs (matches old app gate).
    // This is normally skipped for privacy reasons unless explicitly enabled.
    const PRIVILEGED_UIDS = new Set([
      'BaxP3oZnEKQXCQXOUiEIXXNc4G03',
      '3AeobzEZETUqYDbkan9cCajyVQl1',
      'ssVmXGHlZEgppRIfMcoENJC36ja2',
      'RfJMr0ppRygQ4nQuESSNp7mhVRq1',
      '5n8PbBloYYTv9Ytnki3laI1OUz92',
      '8sXgXK5dqya0ECdIMc53OSUrpJO2',
    ]);
    if (resolvedText && currentUser?.uid && PRIVILEGED_UIDS.has(currentUser.uid)) {
      updateData.msgResolvedText = resolvedText;
    }

    try {
      await get().updateMessageDetails(conversationId, contactId, messageId, updateData);
    } catch (error) {
      console.error('[ConvStore] Failed to store sent message meta:', error);
    }
  },

  requestMessageAuthor: (messageId) => {
    // Skip WhatsApp — author info already baked into the message doc
    const tpaId = useModuleStore.getState().tpaModule?.id;
    if (tpaId === 'whatsapp') return;

    // Cache hit: apply immediately and bail
    const cached = messageAuthorCache.get(messageId);
    if (cached !== undefined) {
      if (cached === null) return; // known-no-author, nothing to do
      const current = get().messages;
      const msg = current.find((m) => m.id === messageId);
      if (!msg) return;
      const mWithAuthor = msg as Message & { author?: MessageAuthor };
      if (mWithAuthor.author) return; // already applied
      const updated = current.map((m) =>
        m.id === messageId ? ({ ...m, author: cached } as Message) : m
      );
      set({ messages: updated });
      return;
    }

    // Resolve the conv doc path for the currently-open conversation
    const activeConv = get().activeConversation;
    if (!activeConv) return;
    const convDocPath = getInboxConvDocPath(activeConv.id);
    if (!convDocPath) return;

    // Enqueue for batched fetch
    let pending = pendingAuthorFetches.get(convDocPath);
    if (!pending) {
      pending = { ids: new Set(), timer: null };
      pendingAuthorFetches.set(convDocPath, pending);
    }
    pending.ids.add(messageId);

    // Debounce: wait 100ms for more requests to accumulate
    if (pending.timer) clearTimeout(pending.timer);
    pending.timer = setTimeout(() => {
      flushAuthorFetches(convDocPath, set, get).catch(() => {});
    }, 100);
  },

  updateWABAMediaUrl: async (conversationId, contactId, messageId, mediaFileID) => {
    if (!conversationId || !contactId || !messageId || !mediaFileID) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_WABA_UPDATE_MEDIA_URL_IF_MISSED',
        data: { conversationId, contactId, messageId, mediaFileID },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to update WABA media URL:', error);
    }
  },

  updateInboxDetailsInAssigneePath: async (conversationId, contactId, updateData) => {
    if (!conversationId || !updateData) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_DETAILS_UPDATE_ASSIGNEE_PATH',
        data: { conversationID: conversationId, contactId, data: updateData },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to update assignee path:', error);
    }
  },

  setServiceEntityMapsForTPA: async (orgId, saasUserId, msgId, historyData) => {
    if (!orgId || !saasUserId || !msgId) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'SET_SERVICE_ENTRY_MAPS_TPA',
        data: { orgId, msgId, appCode: extensionName, historyData, saasUserId },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to set service entity maps:', error);
    }
  },

  updateExistingRecordInConversation: async (conversationId, module, recordId, recordName) => {
    if (!conversationId || !module || !recordId) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    try {
      const inboxData: Record<string, unknown> = {
        saas_record: { appCode: extensionName, entityId: recordId, module, ...(recordName ? { recordName } : {}) },
      };
      if (recordName) {
        inboxData.latestExistingRecordName = recordName;
      }

      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_UPDATE_CONV_EXISTING_RECORD',
        conversationID: conversationId,
        data: inboxData,
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to update existing record:', error);
    }
  },

  updateExistingContactInConversation: async (conversationId, module, recordId, recordName) => {
    if (!conversationId || !module || !recordId) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const { selectedAdminUserId } = useAuthStore.getState();
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'UA_INBOX_UPDATE_CONV_SAAS_CONTACT',
        conversationID: conversationId,
        data: {
          saas_contact: {
            appCode: extensionName,
            entityId: recordId,
            module,
            ...(recordName ? { recordName } : {}),
          },
        },
        _ua_lic_adminUserID: selectedAdminUserId,
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (error) {
      console.error('[ConvStore] Failed to update saas_contact:', error);
    }
  },

  resyncConversationEnrichment: async () => {
    // 1. Wipe IDB enrichment cache so every phone group re-queries
    //    the CRM (otherwise cached hits / empty entries persist for
    //    up to 7 days / 24h respectively).
    const { CONV_ENRICHMENT_KEY_PREFIX } = await import('@/lib/cache/cache-keys');
    await idbCache.clearByKeyPattern('org_data', CONV_ENRICHMENT_KEY_PREFIX).catch(() => {});

    // 2. Clear in-memory crmRecord/crmRecordName on every conversation
    //    so the candidate filter (`!c.crmRecord`) actually picks them
    //    up. The inbox-detail re-read below will then re-populate from
    //    `saas_contact` for ones that still have it stamped in
    //    Firestore.
    const stripped = get().conversations.map((c) => {
      const { crmRecord: _cr, crmRecordName: _crn, ...rest } = c;
      return rest as typeof c;
    });
    set({ conversations: stripped });

    // 3. Re-run the inbox-detail read for the current list. This
    //    function already triggers `enrichConversationsWithCRMRecord`
    //    internally for conversations missing a record, so we get the
    //    full re-resolve in one call.
    await fetchInboxDetailsForConversations(stripped, set, get);
  },

  // ── Draft management ──────────────────────────────────────────────────

  saveDraft: (conversationId: string, text: string) => {
    // Update local state immediately
    set((s) => ({ drafts: { ...s.drafts, [conversationId]: text } }));

    // Debounced Firestore write — uses per-conversation timer
    clearTimeout(_draftTimers[conversationId]);
    _draftTimers[conversationId] = window.setTimeout(() => {
      _persistDraftToFirestore(conversationId, text);
      delete _draftTimers[conversationId];
    }, text.trim().length === 0 ? 0 : 1000);
  },

  clearDraft: (conversationId: string) => {
    set((s) => {
      const updated = { ...s.drafts };
      delete updated[conversationId];
      return { drafts: updated };
    });
    clearTimeout(_draftTimers[conversationId]);
    delete _draftTimers[conversationId];
    _persistDraftToFirestore(conversationId, '');
  },

  getDraft: (conversationId: string) => {
    return get().drafts[conversationId] || '';
  },
}));

/** Listen for cross-tab broadcasts and apply them to this tab's store */
crossTabChannel?.addEventListener('message', (event) => {
  const data = event.data;
  if (!data || typeof data !== 'object') return;
  if (data.type === 'mark-as-read' && data.conversationId) {
    useConversationStore.getState().markAsRead(data.conversationId, true);
  }
  if (data.type === 'mark-as-unread' && data.conversationId) {
    useConversationStore.getState().markAsUnread(data.conversationId, true);
  }
});

/** Timers for debounced draft saves — keyed by conversation ID */
const _draftTimers: Record<string, number> = {};

/** Persist draft to Firestore on the conversation doc: messageDraft_{uid} */
async function _persistDraftToFirestore(conversationId: string, text: string) {
  try {
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    const currentUserUid = useAuthStore.getState().user?.uid;
    if (!currentUserUid) return;

    const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
    const { selectedAdminUserId } = useAuthStore.getState();

    // Extract contactId from conversationId (format: senderNum::contactNum)
    const parts = conversationId.split('::');
    const contactId = parts.length >= 2 ? toContactId(parts[1]) : '';

    await callServiceAPI({
      appCode: extensionName,
      TPAService: extensionName.split('for')[0],
      action: 'UA_INBOX_UPDATE_MESSAGE_DRAFT',
      data: {
        conversationID: conversationId,
        messageDraft: text,
        contactId,
      },
      _ua_lic_adminUserID: selectedAdminUserId,
      headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
    });
  } catch (error) {
    console.error('[ConvStore] Failed to save draft:', error);
  }
}

// ── SaaS sync callbacks (ported from UA_INBOX_UTILITY.handleAfter*) ──

/** After label is updated, sync to SaaS CRM field */
async function _handleAfterLabelUpdated(conversationId: string, contactId: string | null, labelVal: string) {
  try {
    const { useModuleStore } = await import('./module-store');
    const saasModule = useModuleStore.getState().saasModule;
    if (saasModule && typeof (saasModule as unknown as { updateInboxFieldIntoSAASField: unknown }).updateInboxFieldIntoSAASField === 'function') {
      await (saasModule as unknown as { updateInboxFieldIntoSAASField(cid: string, f: string, v: string, p?: string | null): Promise<void> })
        .updateInboxFieldIntoSAASField(conversationId, 'label', labelVal, contactId);
    }
  } catch (err) {
    console.error('[ConvStore] handleAfterLabelUpdated error:', err);
  }
}

/** After assignee is updated, sync to SaaS CRM field */
async function _handleAfterAssigneeUpdated(conversationId: string, contactId: string | null, assigneeEmail: string) {
  try {
    const { useModuleStore } = await import('./module-store');
    const saasModule = useModuleStore.getState().saasModule;
    if (saasModule && typeof (saasModule as unknown as { updateInboxFieldIntoSAASField: unknown }).updateInboxFieldIntoSAASField === 'function') {
      await (saasModule as unknown as { updateInboxFieldIntoSAASField(cid: string, f: string, v: string, p?: string | null): Promise<void> })
        .updateInboxFieldIntoSAASField(conversationId, 'owner', assigneeEmail, contactId);
    }
  } catch (err) {
    console.error('[ConvStore] handleAfterAssigneeUpdated error:', err);
  }
}

// Batch buffer for message refresh — collects messages from multiple synchronous
// emitRefresh calls and applies them in a single setState via setTimeout(0).
let pendingRefreshMessages: import('@/types/messaging').Message[] = [];
let refreshMessageTimer: ReturnType<typeof setTimeout> | null = null;

// Batch buffer for conversation refresh — collects all conv updates from a single
// server sync and applies them in one setState.
let pendingRefreshConvs: Array<{ rawConvId: string; data: Record<string, unknown> }> = [];
let refreshConvTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Queue a refreshed message for the active thread and flush the buffer after a
 * macrotask, so several emitRefresh calls from one server sync land in a single
 * setState. Upserts by id; new messages are spliced in timestamp order.
 */
function queueRefreshMessage(msg: Message, activeId: string) {
  pendingRefreshMessages.push(msg);
  if (refreshMessageTimer) return;
  refreshMessageTimer = setTimeout(() => {
    const toApply = pendingRefreshMessages.splice(0);
    refreshMessageTimer = null;
    if (toApply.length === 0) return;
    const current = useConversationStore.getState().messages;
    const updated = [...current];
    for (const m of toApply) {
      const existingIdx = updated.findIndex((x) => x.id === m.id);
      if (existingIdx >= 0) {
        updated[existingIdx] = m;
      } else {
        // Insert in timestamp order
        const insertIdx = updated.findIndex((x) => x.timestamp > m.timestamp);
        if (insertIdx >= 0) {
          updated.splice(insertIdx, 0, m);
        } else {
          updated.push(m);
        }
      }
    }
    useConversationStore.setState({ messages: updated });
    console.log(`[ConvStore] Firestore refresh: ${toApply.length} messages applied to ${activeId}`);
  }, 0);
}

// Handle Firestore cache refresh — apply fresh server data directly to store
onFirestoreRefresh(async (path, data) => {
  if (!data) return;

  // Reminder task doc: .../UA_INBOX_TASKS/{uid}/REMINDERS_TASKS/{eventId}
  //
  // getDocs is cache-first: a never-cached query resolves EMPTY and the real
  // server rows arrive here a beat later. Without this handler the reminder
  // lists showed only the single badge-fallback row on first open (the
  // "I have 2 reminders but only 1 shows" bug) and healed only on reopen.
  const taskMatch = path.match(/\/REMINDERS_TASKS\/([^/]+)$/);
  if (taskMatch) {
    // Only MY folder, in the app that is open. The suffix alone would also match
    // another agent's tasks (`UA_INBOX_TASKS/{otherUid}/…`) and docs from the
    // previously selected TPA arriving just after an app switch, either of which
    // would be spliced into this user's drawer.
    const myTasksPath = getRemindersTasksPath();
    if (!myTasksPath || !path.startsWith(`${myTasksPath}/`)) return;
    const eventId = taskMatch[1];
    const store = useConversationStore.getState();
    const eventBody = data.eventBody as Record<string, unknown> | undefined;
    const withoutThis = store.activeReminders.filter((r) => r.eventId !== eventId);
    if (data.status === 'scheduled') {
      const upserted: PastReminder = {
        eventId,
        subject: (data.subject as string) || (data.text as string) || 'Reminder',
        performAt: (data.performAt as number) || 0,
        createdTime: (data.createdTime as number) || 0,
        conversationId: (eventBody?.conversationId as string) || (data.conversationId as string) || '',
        contactId: (eventBody?.contactId as string) || (data.contactId as string) || '',
        status: data.status as string,
        read: (data.read as boolean) ?? true,
      };
      const next = [...withoutThis, upserted].sort((a, b) => a.performAt - b.performAt);
      useConversationStore.setState({ activeReminders: next });
      _syncChatRow(upserted, eventId);
    } else {
      // No longer scheduled (completed/cancelled elsewhere) — drop it.
      if (withoutThis.length !== store.activeReminders.length) {
        useConversationStore.setState({ activeReminders: withoutThis });
      }
      _syncChatRow(null, eventId, true);
    }
    return;
  }

  // Message doc: .../UA_INBOX_CONVERSATIONS/{convId}/messages/{msgId}
  const msgMatch = path.match(/UA_INBOX_CONVERSATIONS\/([^/]+)\/messages\/([^/]+)$/);
  if (msgMatch) {
    const rawConvId = msgMatch[1];
    const convIdDash = rawConvId.replace('::', '--');
    const convIdColon = rawConvId.replace('--', '::');
    const store = useConversationStore.getState();
    const activeId = store.activeConversation?.id;
    if (!activeId || (activeId !== convIdDash && activeId !== convIdColon && activeId !== rawConvId)) return;

    const tpaModule = useModuleStore.getState().tpaModule;
    const isWhatsAppTPA = !!tpaModule && 'buildConvertContext' in tpaModule;

    if (!isWhatsAppTPA) {
      // Non-WhatsApp TPAs (Twilio, etc.) — the WhatsApp converter below can't be
      // used here (its context is WhatsApp-only, and a REST TPA's message doc is
      // just a `source` + `_uaCreatedTime` stub that would convert into an empty
      // bubble), so this handler used to drop the doc outright.
      //
      // UA-authored docs are the exception: notes, pinned notes and system
      // messages are written by UA itself in a TPA-independent shape, so they
      // parse with no converter at all. Applying them here is what makes the
      // fresh server copy — the one `getDocs` fetches moments after resolving
      // from its local cache — actually reach the thread, instead of surfacing
      // only on the NEXT open of the chat.
      if (isUaAuthoredMessageDoc(data)) {
        const uaMsg = parseInboxNoteDoc(msgMatch[2], data, activeId);
        if (uaMsg) queueRefreshMessage(uaMsg, activeId);
      }
      return;
    }

    try {
      const { convertWhatsAppMessage } = await import('@/modules/tpa/whatsapp/whatsapp-message-converter');
      const convertCtx = (tpaModule as unknown as { buildConvertContext(): unknown }).buildConvertContext();
      const rawMsg = { id: msgMatch[2], ...data };
      const converted = convertWhatsAppMessage(rawMsg, convertCtx);
      if (converted) {
        converted.conversationId = activeId;
        queueRefreshMessage(converted, activeId);
      }
    } catch { /* conversion failed — skip */ }
    return;
  }

  // Conversation doc: .../UA_INBOX_CONVERSATIONS/{convId}
  const convMatch = path.match(/UA_INBOX_CONVERSATIONS\/([^/]+)$/);
  if (convMatch && data) {
    const tpaModule = useModuleStore.getState().tpaModule;
    const isWhatsAppTPA = tpaModule && 'buildConvertContext' in tpaModule;

    if (!isWhatsAppTPA) {
      // Non-WhatsApp TPAs (Twilio, etc.) — apply inbox fields directly
      const rawConvId = convMatch[1];
      const convIdDash = rawConvId.replace('::', '--');
      const convIdColon = rawConvId.replace('--', '::');
      const conversations = useConversationStore.getState().conversations;
      const idx = conversations.findIndex(c => c.id === convIdDash || c.id === convIdColon || c.id === rawConvId);
      if (idx >= 0) {
        const existing = conversations[idx];
        const updated = [...conversations];

        // Recompute crmRecord/crmRecordName from saas_contact (primary)
        // or saas_record salvage (only when its module is in the current
        // enrichment-allowed set). Mirrors fetchInboxDetailsForConversations
        // so a Firestore push lands the same shape as a full fetch.
        const appStore = useAppStore.getState();
        const extensionName = appStore.extensionName;
        const _saasModule = useModuleStore.getState().saasModule;
        const _userPins = useSettingsStore.getState().preferences.inboxRecordEnrichmentModules;
        const currentEnrichmentModules: string[] | null = _saasModule
          ? getCurrentEnrichmentModules(_saasModule, _userPins)
          : null;

        let crmRecord: { appCode: string; entityId: string; module: string; recordName?: string } | undefined;
        let crmRecordName: string | undefined;

        const saasContact = data.saas_contact;
        if (saasContact && typeof saasContact === 'object') {
          const sc = saasContact as Record<string, unknown>;
          if (sc.entityId) {
            crmRecord = {
              appCode: (typeof sc.appCode === 'string' && sc.appCode) ? sc.appCode : (extensionName || ''),
              entityId: sc.entityId as string,
              module: (sc.module as string) || '',
              recordName: sc.recordName as string | undefined,
            };
            if (typeof sc.recordName === 'string') crmRecordName = sc.recordName;
          }
        }
        if (!crmRecord) {
          const sr = (extensionName ? data[`saas_record_${extensionName}`] : undefined) || data.saas_record;
          if (sr && typeof sr === 'object') {
            const srObj = sr as Record<string, unknown>;
            const srModule = typeof srObj.module === 'string' ? srObj.module : '';
            if (srObj.entityId && srModule && currentEnrichmentModules && moduleInList(srModule, currentEnrichmentModules)) {
              crmRecord = {
                appCode: (typeof srObj.appCode === 'string' && srObj.appCode) ? srObj.appCode : (extensionName || ''),
                entityId: srObj.entityId as string,
                module: srModule,
                recordName: srObj.recordName as string | undefined,
              };
              if (typeof srObj.recordName === 'string') crmRecordName = srObj.recordName;
            }
          }
        }
        // Filter phone-shaped names
        if (crmRecordName) {
          const cleaned = crmRecordName.replace(/[\s\-+()]/g, '');
          if (/^\d+$/.test(cleaned)) crmRecordName = undefined;
        }

        // '_activeFlow' present → flow paused (lock); explicit null → cleared (unlock).
        const _flowMarker = '_activeFlow' in data ? (parseActiveFlow(data._activeFlow) ?? undefined) : existing.activeFlow;
        // KAN-155 opt-out flag — same rule: present → apply, absent → keep.
        const _optOut = 'optOut' in data ? (parseOptOut(data.optOut) ?? undefined) : existing.optOut;
        // The load-time getDoc resolves cache-first, so the unread derived in
        // fetchInboxDetailsForConversations can come from a stale IndexedDB
        // doc (badge missing / previous count until a second reload). This
        // event carries the fresh server doc — recompute and patch the badge.
        // Raise-only, same guards as the load path: zeroing stays with
        // markAsRead, and skip convs the user is viewing / just read.
        const _authUid = useAuthStore.getState().user?.uid || getEffectiveUid();
        const _docUnread = computeInboxDocUnreadCount(data, _authUid || undefined);
        const _activeId = useConversationStore.getState().activeConversation?.id;
        const _recentlyRead = Date.now() - (_lastReadCallAt.get(existing.id) ?? 0) < 15000;
        const _applyUnread =
          _docUnread !== undefined && _docUnread > 0 &&
          existing.id !== _activeId && !_recentlyRead;
        // The one case this raise-only path may lower the badge: the row is
        // sitting on a manual "Mark as unread" dot (placeholder count of 1)
        // and the server doc says the flag is gone — someone opened the chat,
        // or this user did before a reload and the cache-first load brought
        // the stale flag back. Guarded by the local settle window so a mark
        // whose CF hasn't landed yet isn't undone by its own stale doc.
        const _recentlyMarkedUnread = Date.now() - (_lastUnreadMarkAt.get(existing.id) ?? 0) < READ_STATE_SETTLE_MS;
        const _clearManualUnread =
          !!existing.markedUnread && data.__markedUnread !== true && !_recentlyMarkedUnread;
        updated[idx] = {
          ...existing,
          ...(_clearManualUnread && {
            markedUnread: false,
            ...((existing.unreadCount || 0) <= 1 && { unreadCount: 0 }),
          }),
          ...(_applyUnread && { unreadCount: Math.max(existing.unreadCount || 0, _docUnread!) }),
          // The badge just raised may be the "Mark as unread" one — carry
          // the flag so it renders as a dot, not a count.
          ...(_applyUnread && isInboxDocMarkedUnread(data, _authUid || undefined) && { markedUnread: true }),
          label: (data.label as string) || existing.label,
          assigneeEmail: (data.assigneeEmail as string) || existing.assigneeEmail,
          crmRecordName: crmRecordName ?? existing.crmRecordName,
          ...(crmRecord ? { crmRecord } : {}),
          activeFlow: _flowMarker,
          optOut: _optOut,
          metadata: {
            ...existing.metadata,
            ...(data.callPermission && { callPermission: data.callPermission }),
          },
        };
        useConversationStore.setState({ conversations: updated });
        // Live-sync the open conversation so the banner + composer lock react instantly.
        const _liveActive = useConversationStore.getState().activeConversation;
        if (_liveActive?.id === existing.id) {
          useConversationStore.setState({ activeConversation: { ..._liveActive, activeFlow: _flowMarker, optOut: _optOut } });
        }
        console.log(`[ConvStore] Firestore refresh: inbox details updated for ${existing.id}`);
      }
      return;
    }

    pendingRefreshConvs.push({ rawConvId: convMatch[1], data });
    if (!refreshConvTimer) {
      refreshConvTimer = setTimeout(async () => {
        const batch = pendingRefreshConvs.splice(0);
        refreshConvTimer = null;
        if (batch.length === 0) return;

        try {
          const { convertConversationItem } = await import('@/modules/tpa/whatsapp/whatsapp-message-converter');
          const convertCtx = (tpaModule as unknown as { buildConvertContext(): unknown }).buildConvertContext();
          const currentUserId = useAuthStore.getState().user?.uid || '';
          const { conversations, filterAgent: filterAgentNow } = useConversationStore.getState();
          const updated = [...conversations];
          let changed = false;

          for (const { rawConvId, data: convData } of batch) {
            const convIdDash = rawConvId.replace('::', '--');
            const convIdColon = rawConvId.replace('--', '::');
            const idx = updated.findIndex(c => c.id === convIdDash || c.id === convIdColon || c.id === rawConvId);

            const rawDoc = { id: rawConvId, ...convData } as import('@/modules/tpa/whatsapp/whatsapp-types').WhatsAppFirestoreConversation;
            const freshConv = convertConversationItem(rawDoc, currentUserId, convertCtx);

            if (idx >= 0) {
              // Existing conversation — update fields, only take newer message data
              const existing = updated[idx];
              const existingMs = existing.lastMessageTime instanceof Date ? existing.lastMessageTime.getTime() : Number(existing.lastMessageTime) || 0;
              const freshMs = freshConv.lastMessageTime instanceof Date ? freshConv.lastMessageTime.getTime() : Number(freshConv.lastMessageTime) || 0;

              updated[idx] = {
                ...existing,
                contactName: freshConv.contactName || existing.contactName,
                contactPhone: freshConv.contactPhone || existing.contactPhone,
                crmRecordName: freshConv.crmRecordName || existing.crmRecordName,
                crmRecord: freshConv.crmRecord || existing.crmRecord,
                // Server-authoritative fields that can change without
                // `lastMessageTime` moving — read on another device zeroes
                // `unreadCount`, a teammate reassigns, someone pins/unpins,
                // or a label edit lands. `freshConv` was just re-converted
                // from the server doc so it reflects the latest state.
                //
                // unreadCount caveat: when the local user just clicked a conv,
                // `markAsRead` zeroed it locally and `markMessageAsReadInConversation`
                // fired a CF that hasn't yet written `__userLastReadCount_{uid}`
                // to the doc. A refresh landing in that window would otherwise
                // bring the stale > 0 count back. So only accept a server count
                // when it's 0 (cross-device read sync) or when a new message
                // actually arrived (freshMs > existingMs).
                //
                // "Mark as unread" is the mirror image: a local mark sets the
                // badge to 1 before its CF lands, so a 0 arriving inside the
                // settle window is stale and must not clear it. And a doc
                // flagged unread by a teammate (or this user elsewhere) raises
                // the badge here — unless this device just read it and its own
                // CF is still in flight (the flag on the doc is then the stale
                // side).
                ...(() => {
                  // Both fields decided together from one clock read so the
                  // count and the dot can never disagree.
                  const nowMs = Date.now();
                  const holdUnread = nowMs - (_lastUnreadMarkAt.get(existing.id) ?? 0) < READ_STATE_SETTLE_MS;
                  const holdRead = nowMs - (_lastReadCallAt.get(existing.id) ?? 0) < READ_STATE_SETTLE_MS;
                  const newInbound = freshMs > existingMs;
                  // A newer message: take the server pair as-is. The converter
                  // already drops the dot when a real inbound sits on top of
                  // the flag, and keeps it when the newer message was ours.
                  if (newInbound) {
                    return { unreadCount: freshConv.unreadCount, markedUnread: freshConv.markedUnread };
                  }
                  if (freshConv.unreadCount === 0) {
                    return holdUnread
                      ? { unreadCount: existing.unreadCount, markedUnread: existing.markedUnread }
                      : { unreadCount: 0, markedUnread: false };
                  }
                  if (holdUnread || holdRead) {
                    return { unreadCount: existing.unreadCount, markedUnread: existing.markedUnread };
                  }
                  if ((convData as Record<string, unknown>).__markedUnread === true) {
                    return {
                      unreadCount: Math.max(existing.unreadCount || 0, freshConv.unreadCount),
                      markedUnread: freshConv.markedUnread,
                    };
                  }
                  return { unreadCount: existing.unreadCount, markedUnread: freshConv.markedUnread };
                })(),
                label: freshConv.label ?? existing.label,
                assigneeEmail: freshConv.assigneeEmail ?? existing.assigneeEmail,
                activeFlow: '_activeFlow' in convData ? (parseActiveFlow(convData._activeFlow) ?? undefined) : existing.activeFlow,
                optOut: 'optOut' in convData ? (parseOptOut(convData.optOut) ?? undefined) : existing.optOut,
                pinnedMessageId: freshConv.pinnedMessageId ?? existing.pinnedMessageId,
                ...(freshMs > existingMs && {
                  lastMessage: freshConv.lastMessage,
                  lastMessageTime: freshConv.lastMessageTime,
                  metadata: { ...existing.metadata, ...freshConv.metadata },
                }),
              };
            } else if (isAgentEmailFilter(filterAgentNow) && freshConv.assigneeEmail !== filterAgentNow) {
              // List is narrowed to a specific agent and this conversation is
              // someone else's — don't inject it past the filter.
              continue;
            } else {
              // New conversation from server — prepend to list
              updated.unshift(freshConv);
            }
            changed = true;
          }

          if (changed) {
            // Sort by lastMessageTime descending — conversations with newer messages go to top
            updated.sort((a, b) => {
              const aMs = a.lastMessageTime instanceof Date ? a.lastMessageTime.getTime() : Number(a.lastMessageTime) || 0;
              const bMs = b.lastMessageTime instanceof Date ? b.lastMessageTime.getTime() : Number(b.lastMessageTime) || 0;
              return bMs - aMs;
            });
            useConversationStore.setState({ conversations: updated });
            // Keep the open conversation's flow-lock + opt-out lock in sync (banner + composer).
            const _waActive = useConversationStore.getState().activeConversation;
            if (_waActive) {
              const _waFresh = updated.find((c) => c.id === _waActive.id);
              if (_waFresh && (_waFresh.activeFlow !== _waActive.activeFlow || _waFresh.optOut !== _waActive.optOut)) {
                useConversationStore.setState({ activeConversation: { ..._waActive, activeFlow: _waFresh.activeFlow, optOut: _waFresh.optOut } });
              }
            }
            console.log(`[ConvStore] Firestore refresh: ${batch.length} conversations updated`);
          }
        } catch { /* conversion failed — skip */ }
      }, 500);
    }
  }
});

/** After chat name/subject is updated, sync to SaaS CRM field */
async function _handleAfterChatNameUpdated(conversationId: string, contactId: string | null, chatName: string) {
  try {
    const { useModuleStore } = await import('./module-store');
    const saasModule = useModuleStore.getState().saasModule;
    if (saasModule && typeof (saasModule as unknown as { updateInboxFieldIntoSAASField: unknown }).updateInboxFieldIntoSAASField === 'function') {
      await (saasModule as unknown as { updateInboxFieldIntoSAASField(cid: string, f: string, v: string, p?: string | null): Promise<void> })
        .updateInboxFieldIntoSAASField(conversationId, 'name', chatName, contactId);
    }
  } catch (err) {
    console.error('[ConvStore] handleAfterChatNameUpdated error:', err);
  }
}

export async function openConversationInInbox(id: string): Promise<void> {
  try {
    const ui = useUIStore.getState();
    if (ui.activeView !== 'CONVERSATIONS') {
      ui.setView('CONVERSATIONS');
      await new Promise((r) => setTimeout(r, 300));
    }
    const conv = await useConversationStore.getState().resolveConversationById(id);
    const latest = useConversationStore.getState();
    if (!latest.conversations.some((c) => c.id === conv.id)) {
      useConversationStore.setState({ conversations: [conv, ...latest.conversations] });
    }

    useConversationStore.getState().setActiveConversation(conv);
    if (conv.unreadCount > 0) {
      useConversationStore.getState().markAsRead(conv.id);
    }
  }
  catch (err) {
    console.log(`[ConvStore] openConversationInInbox failed: `, err);
  }
}
